import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  Alert,
  Anchor,
  Avatar,
  Badge,
  Button,
  Checkbox,
  Collapse,
  Card,
  Container,
  Group,
  List,
  Loader,
  Menu,
  Modal,
  MultiSelect,
  Paper,
  Select,
  SegmentedControl,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';

import {
  clearSearchHistory,
  createFavorite,
  deleteFavorite,
  deleteNote,
  deleteSearchHistoryItem,
  fetchHighlightTerms,
  getBrowseQaQuestions,
  getFavoriteStatuses,
  getFavorites,
  getNoteBySource,
  getNotes,
  getNoteStatuses,
  getPipelineStatus,
  getQaQuestionSegments,
  getRecentSearchHistory,
  getSearchHistory,
  getSessions,
  getSessionSegments,
  getSessionSummary,
  getSessionTypes,
  getTagStatuses,
  getSessionTagStatuses,
  saveNoteBySource,
  updateNote,
  searchAiRankedChunks,
  searchAnswer,
  searchCombined,
  searchIntent,
  searchSmart,
} from './api';
import {
  MyNotesModal,
  NoteButton,
  NoteEditorModal,
  canOpenSavedNote,
  getNoteSourceForQuestion,
  getNoteSourceForSession,
  getNoteSourceFromSavedNote,
  makeNoteSourceKey,
} from './Notes';
import AdminUploadSessions from './AdminUploadSessions';
import AdminTranscriptDownloads from './AdminTranscriptDownloads';
import AccountingReport from './AccountingReport';
import { MyTagsModal, SessionTagSummary, TagButton } from './Tags';

function renderBoldSnippet(text) {
  const source = text || '';

  const parts = source.split(/(\*\*[^*]+\*\*)/g);

  return parts.map((part, index) => {
    const match = part.match(/^\*\*(.+)\*\*$/);

	if (match) {
	  return (
	    <mark className="context-highlight" key={index}>
	      {match[1]}
	    </mark>
	  );
	}
	
    return <span key={index}>{part}</span>;
  });
}

const HIGHLIGHT_STOP_WORDS = new Set([
  'a', 'an', 'and', 'are', 'as', 'at', 'be', 'by', 'can', 'could',
  'did', 'do', 'does', 'for', 'from', 'he', 'how', 'i', 'if', 'in',
  'is', 'it', 'me', 'not', 'of', 'on', 'or', 'should', 'that',
  'the', 'their', 'them', 'there', 'these', 'they', 'this', 'to',
  'was', 'we', 'what', 'when', 'where', 'which', 'who', 'why',
  'with', 'would', 'you', 'your',
]);

const HIGHLIGHT_EXPANSIONS = {
  fvg: ['fair value gap'],
  fvgs: ['fair value gaps', 'fair value gap'],
  ifvg: ['inverse fair value gap'],
  ifvgs: ['inverse fair value gaps', 'inverse fair value gap'],
  ib: ['initial balance'],
  smt: ['SMT'],
};

function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function termToHighlightPattern(term) {
  const cleanedTerm = String(term || '').trim();

  if (!cleanedTerm) {
    return '';
  }

  const escapedTerm = escapeRegExp(cleanedTerm).replace(/\s+/g, '\\s+');

  // Single-token terms, especially short acronyms like IB/FVG/SMT,
  // must match as whole tokens. This prevents IB from highlighting
  // inside words like possible, fib, vibe, or library.
  if (/^[A-Za-z0-9]+$/.test(cleanedTerm)) {
    return `(?<![A-Za-z0-9])${escapedTerm}(?![A-Za-z0-9])`;
  }

  // Multi-word phrases should also start/end on token boundaries,
  // while still allowing normal whitespace between words.
  return `(?<![A-Za-z0-9])${escapedTerm}(?![A-Za-z0-9])`;
}

function getHighlightTerms(query, extraTerms = [], exactPhraseOnly = false) {
  const cleanedQuery = String(query || '').trim().replace(/^["'“”‘’]+|["'“”‘’]+$/g, '');

  if (!cleanedQuery && (!extraTerms || extraTerms.length === 0)) {
    return [];
  }

  const terms = [];

  if (cleanedQuery.length >= 3) {
    terms.push(cleanedQuery);
  }

  // Exact mode: ranking is now driven purely by the phrase/alias match, not
  // by individual word overlap -- so highlighting individual words (e.g.
  // "price" out of "consumer price index") would show a reason the chunk
  // was NOT actually ranked on. Show only the phrase itself and whatever
  // alias resolved it (via extraTerms below), nothing split out.
  if (!exactPhraseOnly) {
    const rawWords = cleanedQuery.match(/[A-Za-z0-9]+/g) || [];

    for (const rawWord of rawWords) {
      const word = rawWord.toLowerCase();

      if (word.length <= 1 || HIGHLIGHT_STOP_WORDS.has(word)) {
        continue;
      }

      terms.push(rawWord);

      for (const expanded of HIGHLIGHT_EXPANSIONS[word] || []) {
        terms.push(expanded);
      }
    }
  }

  // Backend-resolved alias terms (e.g. "cpi" for a query of "consumer price
  // index") -- these are what actually explain why a chunk matched under
  // Exact mode's alias resolution, which the client-side word-splitting
  // above has no way to know about on its own.
  for (const extraTerm of extraTerms || []) {
    if (extraTerm) {
      terms.push(extraTerm);
    }
  }

  return [...new Set(terms)]
    .filter((term) => String(term).trim().length >= 2)
    .sort((a, b) => b.length - a.length);
}

function highlightQueryTerms(text, query, extraTerms = [], exactPhraseOnly = false) {
  const source = String(text || '');
  const terms = getHighlightTerms(query, extraTerms, exactPhraseOnly);
  const termPatterns = terms
    .map(termToHighlightPattern)
    .filter(Boolean);

  if (!source || termPatterns.length === 0) {
    return source;
  }

  const pattern = termPatterns.join('|');
  const splitter = new RegExp(`(${pattern})`, 'gi');
  const matcher = new RegExp(`^(?:${pattern})$`, 'i');

  return source.split(splitter).map((part, index) => {
	  
	  if (matcher.test(part)) {
	    return (
	      <mark className="context-highlight" key={index}>
	        {part}
	      </mark>
	    );
	  }

    return <span key={index}>{part}</span>;
  });
}

function formatDuration(seconds) {
  const totalSeconds = Number(seconds || 0);

  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) {
    return 'Duration unknown';
  }

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.round((totalSeconds % 3600) / 60);

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }

  return `${minutes}m`;
}


function formatSecondsAsTimestamp(seconds) {
  const totalSeconds = Number(seconds || 0);

  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) {
    return '';
  }

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }

  return `${minutes}:${String(secs).padStart(2, '0')}`;
}


function decorateTranscriptSegments(segments, sourceItem = null, minimumGapSeconds = 30) {
  const targetStart =
    sourceItem?.start_seconds ??
    sourceItem?.startSeconds ??
    null;

  const targetEnd =
    sourceItem?.end_seconds ??
    sourceItem?.endSeconds ??
    targetStart;

  let lastDisplayedTimestampSeconds = null;
  let hasDisplayedTargetTimestamp = false;
  let previousSpeaker = '';

  return (segments || []).map((segment, index) => {
    const segmentStart = Number(segment.start_seconds ?? 0);
    const segmentEnd = Number(segment.end_seconds ?? segmentStart);

    const hasTarget =
      targetStart !== null &&
      targetStart !== undefined &&
      Number.isFinite(Number(targetStart));

    const isTargetSegment =
      hasTarget &&
      segmentStart <= Number(targetEnd ?? targetStart) &&
      segmentEnd >= Number(targetStart);

    const isFirstTargetSegment =
      isTargetSegment && !hasDisplayedTargetTimestamp;

    const showTimestamp =
      index === 0 ||
      isFirstTargetSegment ||
      lastDisplayedTimestampSeconds === null ||
      Math.abs(segmentStart - lastDisplayedTimestampSeconds) >= minimumGapSeconds;

    if (showTimestamp) {
      lastDisplayedTimestampSeconds = segmentStart;
    }

    if (isFirstTargetSegment) {
      hasDisplayedTargetTimestamp = true;
    }

    const speaker = String(segment?.speaker || '').trim();
    const showSpeaker = Boolean(speaker) && (
      index === 0 ||
      isFirstTargetSegment ||
      showTimestamp ||
      speaker !== previousSpeaker
    );

    previousSpeaker = speaker;

    return {
      segment,
      isTargetSegment,
      showTimestamp,
      speaker,
      showSpeaker,
    };
  });
}


function scrollToHighlightedTranscriptLine(container) {
  if (!container) {
    return;
  }

  const target = container.querySelector('.transcript-line-target');

  if (!target) {
    return;
  }

  const containerRect = container.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();

  const scrollOffset =
    targetRect.top -
    containerRect.top -
    container.clientHeight / 2 +
    target.clientHeight / 2;

  container.scrollTo({
    top: container.scrollTop + scrollOffset,
    behavior: 'smooth',
  });
}

const TITLE_COLLATOR = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base',
});

function compareSessionTypes(a, b) {
  const knownOrder = ['accelerator', 'methodology', 'trade room'];
  const aLower = String(a || '').toLowerCase();
  const bLower = String(b || '').toLowerCase();
  const aIndex = knownOrder.indexOf(aLower);
  const bIndex = knownOrder.indexOf(bLower);

  if (aIndex >= 0 && bIndex >= 0) {
    return aIndex - bIndex;
  }

  if (aIndex >= 0) {
    return -1;
  }

  if (bIndex >= 0) {
    return 1;
  }

  return TITLE_COLLATOR.compare(String(a || ''), String(b || ''));
}

function compareSessionsWithinType(sessionType, a, b) {
  const aDate = a.session_date || '';
  const bDate = b.session_date || '';

  if (aDate !== bDate) {
    return bDate.localeCompare(aDate);
  }

  return TITLE_COLLATOR.compare(a.title || '', b.title || '');
}

function groupSessionsByType(sessions) {
  const grouped = new Map();

  for (const session of sessions || []) {
    const sessionType = session.session_type || 'Other';

    if (!grouped.has(sessionType)) {
      grouped.set(sessionType, []);
    }

    grouped.get(sessionType).push(session);
  }

  return Array.from(grouped.entries())
    .sort(([a], [b]) => compareSessionTypes(a, b))
    .map(([sessionType, items]) => ({
      sessionType,
      sessions: [...items].sort((a, b) => compareSessionsWithinType(sessionType, a, b)),
    }));
}

function normalizeFilterValue(value) {
  return String(value || '').trim().toLowerCase();
}

function getSessionTopicOptions(sessions) {
  const topicMap = new Map();

  for (const session of sessions || []) {
    for (const rawTopic of session.topics || []) {
      const topic = String(rawTopic || '').trim();

      if (!topic) {
        continue;
      }

      const key = topic.toLowerCase();

      if (!topicMap.has(key)) {
        topicMap.set(key, {
          topic,
          count: 0,
        });
      }

      topicMap.get(key).count += 1;
    }
  }

  return Array.from(topicMap.values())
    .sort((a, b) => {
      if (b.count !== a.count) {
        return b.count - a.count;
      }

      return TITLE_COLLATOR.compare(a.topic, b.topic);
    })
    .slice(0, 24);
}

function sessionMatchesFilter(session, filterText, selectedTopics, topicFilterMode = 'or') {
  const normalizedFilter = normalizeFilterValue(filterText);
  const normalizedTopics = (session.topics || []).map((topic) => normalizeFilterValue(topic));
  const selectedTopicValues = (selectedTopics || []).map((topic) => normalizeFilterValue(topic));

  const matchesSelectedTopics =
    selectedTopicValues.length === 0 ||
    (topicFilterMode === 'and'
      ? selectedTopicValues.every((topic) => normalizedTopics.includes(topic))
      : selectedTopicValues.some((topic) => normalizedTopics.includes(topic)));

  if (!matchesSelectedTopics) {
    return false;
  }

  if (!normalizedFilter) {
    return true;
  }

  const searchableText = [
    session.id,
    session.title,
    session.session_type,
    session.session_date,
    session.short_summary,
    ...(session.topics || []),
  ]
    .join(' ')
    .toLowerCase();

  return searchableText.includes(normalizedFilter);
}

function filterSessions(sessions, filterText, selectedTopics, topicFilterMode = 'or') {
  return (sessions || []).filter((session) =>
    sessionMatchesFilter(session, filterText, selectedTopics, topicFilterMode)
  );
}

function formatCategoryKeyLabel(categoryKey) {
  return String(categoryKey || '')
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ') || 'Other';
}

function getCategoryDisplayName(categoryKey, categoriesByKey) {
  return categoriesByKey.get(categoryKey)?.display_name || formatCategoryKeyLabel(categoryKey);
}

function getQuestionTitle(question) {
  return question.title || question.item_text || 'Untitled question';
}

function getQuestionTypes(question) {
  const values = Array.isArray(question.question_types) ? question.question_types : [];

  if (values.length > 0) {
    return values;
  }

  return question.primary_question_type ? [question.primary_question_type] : ['other'];
}

function getQaSessionTypeOptions(questions) {
  const typeMap = new Map();

  for (const question of questions || []) {
    const sessionType = String(question.session_type || 'Other').trim() || 'Other';

    if (!typeMap.has(sessionType)) {
      typeMap.set(sessionType, {
        value: sessionType,
        count: 0,
      });
    }

    typeMap.get(sessionType).count += 1;
  }

  return Array.from(typeMap.values())
    .sort((a, b) => compareSessionTypes(a.value, b.value))
    .map((item) => ({
      value: item.value,
      label: `${item.value} (${item.count})`,
    }));
}

function getQaCategoryOptions(categories) {
  return (categories || [])
    .filter((category) => category.is_active !== false)
    .sort((a, b) => {
      const sortCompare = Number(a.sort_order ?? 9999) - Number(b.sort_order ?? 9999);

      if (sortCompare !== 0) {
        return sortCompare;
      }

      return TITLE_COLLATOR.compare(a.display_name || '', b.display_name || '');
    })
    .map((category) => ({
      value: category.category_key,
      label: category.question_count
        ? `${category.display_name} (${category.question_count})`
        : category.display_name,
    }));
}

function questionMatchesQaFilters(question, filters) {
  const {
    dateFrom,
    dateTo,
    sessionTypes,
    categories,
    categoryFilterMode,
  } = filters;

  const questionDate = String(question.session_date || '');

  if (dateFrom && questionDate && questionDate < dateFrom) {
    return false;
  }

  if (dateTo && questionDate && questionDate > dateTo) {
    return false;
  }

  if (Array.isArray(sessionTypes) && sessionTypes.length > 0) {
    if (!sessionTypes.includes(question.session_type || 'Other')) {
      return false;
    }
  }

  if (Array.isArray(categories) && categories.length > 0) {
    const questionTypes = getQuestionTypes(question);

    if (categoryFilterMode === 'all') {
      return categories.every((categoryKey) => questionTypes.includes(categoryKey));
    }

    return categories.some((categoryKey) => questionTypes.includes(categoryKey));
  }

  return true;
}

function filterQaQuestions(questions, filters) {
  return (questions || []).filter((question) => questionMatchesQaFilters(question, filters));
}

function groupQaQuestions(questions, categories, sortMode) {
  if (sortMode === 'session') {
    const grouped = new Map();

    for (const question of questions || []) {
      const key = String(question.session_id || 'unknown');

      if (!grouped.has(key)) {
        grouped.set(key, {
          key,
          label: question.session_title || `Session ${question.session_id}`,
          description: [
            `Session ${question.session_id}`,
            question.session_type,
            question.session_date,
          ].filter(Boolean).join(' · '),
          questions: [],
        });
      }

      grouped.get(key).questions.push(question);
    }

    return Array.from(grouped.values())
      .sort((a, b) => {
        const aQuestion = a.questions[0] || {};
        const bQuestion = b.questions[0] || {};
        const dateCompare = String(bQuestion.session_date || '').localeCompare(String(aQuestion.session_date || ''));

        if (dateCompare !== 0) {
          return dateCompare;
        }

        return Number(bQuestion.session_id || 0) - Number(aQuestion.session_id || 0);
      })
      .map((group) => ({
        ...group,
        questions: [...group.questions].sort((a, b) =>
          Number(a.start_seconds ?? 99999999) - Number(b.start_seconds ?? 99999999)
        ),
      }));
  }

  const categoryOrder = new Map(
    (categories || []).map((category, index) => [category.category_key, Number(category.sort_order ?? index)])
  );
  const categoryNames = new Map(
    (categories || []).map((category) => [category.category_key, category.display_name])
  );
  const grouped = new Map();

  for (const question of questions || []) {
    for (const categoryKey of getQuestionTypes(question)) {
      if (!grouped.has(categoryKey)) {
        grouped.set(categoryKey, {
          key: categoryKey,
          label: categoryNames.get(categoryKey) || formatCategoryKeyLabel(categoryKey),
          description: '',
          questions: [],
        });
      }

      grouped.get(categoryKey).questions.push(question);
    }
  }

  return Array.from(grouped.values())
    .sort((a, b) => {
      const orderCompare =
        Number(categoryOrder.get(a.key) ?? 9999) - Number(categoryOrder.get(b.key) ?? 9999);

      if (orderCompare !== 0) {
        return orderCompare;
      }

      return TITLE_COLLATOR.compare(a.label, b.label);
    })
    .map((group) => ({
      ...group,
      questions: [...group.questions].sort((a, b) => {
        const dateCompare = String(b.session_date || '').localeCompare(String(a.session_date || ''));

        if (dateCompare !== 0) {
          return dateCompare;
        }

        const sessionCompare = Number(b.session_id || 0) - Number(a.session_id || 0);

        if (sessionCompare !== 0) {
          return sessionCompare;
        }

        return Number(a.start_seconds ?? 99999999) - Number(b.start_seconds ?? 99999999);
      }),
    }));
}

const ITEM_TYPE_LABELS = {
  question: 'Question',
  section: 'Section',
  lesson: 'Lesson',
  definition: 'Definition',
  warning: 'Warning',
  setup_rule: 'Setup rule',
  example: 'Example',
  quote: 'Quote',
};

function formatItemTypeLabel(itemType) {
  const normalized = String(itemType || '').trim().toLowerCase();

  if (ITEM_TYPE_LABELS[normalized]) {
    return ITEM_TYPE_LABELS[normalized];
  }

  return normalized
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ') || 'Item';
}

function getSummaryItemsLabel(session, items) {
  const sessionType = String(session?.session_type || '').toLowerCase();
  const allQuestions =
    Array.isArray(items) &&
    items.length > 0 &&
    items.every((item) => item.item_type === 'question');

  if (sessionType === 'q&a' || allQuestions) {
    return 'Questions answered in this session';
  }

  return 'Lessons and key points';
}


function formatOptionalTimestamp(seconds) {
  if (seconds === null || seconds === undefined) {
    return '';
  }

  return formatSecondsAsTimestamp(seconds);
}

function getTranscriptSourceLabel(contextData) {
  const sourceItem = contextData?.sourceItem;

  if (!sourceItem) {
    return 'Transcript context';
  }

  if (sourceItem.chunk_id || sourceItem.chunk_index !== undefined) {
    return 'Search result';
  }

  if (sourceItem.item_type) {
    return formatItemTypeLabel(sourceItem.item_type);
  }

  return 'Transcript item';
}

function getTranscriptSourceTimeRange(contextData) {
  const sourceItem = contextData?.sourceItem || {};

  const start =
    sourceItem.start_time ||
    formatOptionalTimestamp(sourceItem.start_seconds) ||
    contextData?.window_start_time ||
    '';

  const end =
    sourceItem.end_time ||
    formatOptionalTimestamp(sourceItem.end_seconds) ||
    contextData?.window_end_time ||
    '';

  if (start && end && start !== end) {
    return `${start}–${end}`;
  }

  return start || end || '';
}

function getTranscriptOpenedFromText(contextData) {
  if (!contextData) {
    return '';
  }

  const label = getTranscriptSourceLabel(contextData);
  const timeRange = getTranscriptSourceTimeRange(contextData);
  const sourceTitle = contextData.sourceItem?.title || '';

  return [
    `Opened from: ${label}`,
    timeRange,
    sourceTitle,
  ]
    .filter(Boolean)
    .join(' · ');
}

function buildTranscriptCitation(contextData) {
  if (!contextData) {
    return '';
  }

  const session = contextData.session || {};
  const sourceTitle = contextData.sourceItem?.title || '';
  const timeRange = getTranscriptSourceTimeRange(contextData);

  const sessionLabel = [
    session.title,
    session.session_date,
  ]
    .filter(Boolean)
    .join(' · ');

  return [
    sessionLabel || 'Transcript',
    timeRange,
    sourceTitle,
  ]
    .filter(Boolean)
    .join(' — ');
}

async function copyTextToClipboard(text) {
  if (!text) {
    return;
  }

  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();

  try {
    document.execCommand('copy');
  } finally {
    document.body.removeChild(textarea);
  }
}


function SearchInputLabel() {
  return (
    <Group gap={6} align="center" wrap="nowrap">
      <span>Search</span>
      <Tooltip
        multiline
        withArrow
        w={420}
        position="top-start"
        openDelay={250}
        classNames={{ tooltip: 'search-help-tooltip' }}
        styles={{
          tooltip: {
            backgroundColor: '#f3f4f6',
            color: '#000000',
            border: '1px solid #d1d5db',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.25)',
          },
          arrow: {
            backgroundColor: '#f3f4f6',
            border: '1px solid #d1d5db',
          },
        }}
        label={
          <div className="search-tooltip-content">
            <div className="search-tooltip-title">What can I search for?</div>
            <div>
              Type a topic, question, remembered phrase, or trading concept from David&apos;s classes.
            </div>
            <div>
              Examples: “What is displacement?”, “at the tag SMT”, “last candle by definition”,
              “one market takes liquidity but the other does not”, or “initial balance low probability trade”.
            </div>
          </div>
        }
      >
        <span className="info-icon" aria-label="Search help" tabIndex={0}>i</span>
      </Tooltip>
    </Group>
  );
}


function SearchModeLabel() {
  return (
    <Group gap={6} align="center" wrap="nowrap">
      <span>Search mode</span>
      <Tooltip
        multiline
        withArrow
        w={460}
        position="top-start"
        openDelay={250}
        classNames={{ tooltip: 'search-help-tooltip' }}
        styles={{
          tooltip: {
            backgroundColor: '#f3f4f6',
            color: '#000000',
            border: '1px solid #d1d5db',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.25)',
          },
          arrow: {
            backgroundColor: '#f3f4f6',
            border: '1px solid #d1d5db',
          },
        }}
        label={
          <div className="search-tooltip-content">
            <div className="search-tooltip-title">What does each search mode do?</div>
            <div>
              <strong>Best match:</strong> The normal search. It combines meaning, keywords,
              exact phrase scoring, and TD Classes&apos; trading-term boosts. Use this for most searches.
            </div>
            <div>
              <strong>Exact wording:</strong> Looks for the exact contiguous phrase you typed.
              If that exact phrase is not in the transcripts, no results are returned.
            </div>
            <div>
              <strong>Conceptual:</strong> Favors meaning over exact wording. Use this when you
              remember the idea but not David&apos;s exact words.
            </div>
          </div>
        }
      >
        <span className="info-icon" aria-label="Search mode help" tabIndex={0}>i</span>
      </Tooltip>
    </Group>
  );
}



function getCurrentYearPipelineRange() {
  const year = new Date().getFullYear();

  return {
    start: `${year}-01-01`,
    end: `${year}-12-31`,
  };
}

function normalizeSessionStatusText(value) {
  return String(value || '').trim();
}

function normalizeSessionStatusKey(value) {
  return normalizeSessionStatusText(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function getSessionStatusTitle(session) {
  return normalizeSessionStatusText(
    session?.title ||
    session?.session_title ||
    session?.source_name ||
    session?.mp4_path ||
    session?.transcript_path ||
    (session?.id ? `Session ${session.id}` : 'Unknown session')
  );
}

function getSessionStatusDate(session, fallback = '') {
  return normalizeSessionStatusText(
    session?.session_date ||
    session?.date ||
    session?.source_date ||
    fallback
  );
}

function getSessionStatusType(session, fallback = 'Other') {
  return normalizeSessionStatusText(
    session?.session_type ||
    session?.type ||
    fallback ||
    'Other'
  );
}

function makeSessionStatusKey({ sessionType, sessionDate, title, id }) {
  return [
    normalizeSessionStatusKey(sessionType),
    normalizeSessionStatusKey(sessionDate),
    id ? `id:${id}` : '',
    normalizeSessionStatusKey(title),
  ].join('|');
}

function isImportedSessionComplete(session) {
  if (!session) {
    return false;
  }

  const summaryStatus = normalizeSessionStatusText(
    session.summary_status ||
    session.status ||
    ''
  ).toLowerCase();

  if (['complete', 'completed'].includes(summaryStatus)) {
    return true;
  }

  if (session.summary_id || session.short_summary) {
    return true;
  }

  if (Number(session.item_count || session.items_count || 0) > 0) {
    return true;
  }

  return false;
}

function mergeSessionStatusRow(rowMap, row) {
  const key = makeSessionStatusKey(row);
  const existing = rowMap.get(key);

  if (!existing) {
    rowMap.set(key, row);
    return;
  }

  rowMap.set(key, {
    ...existing,
    ...row,
    title: existing.title || row.title,
    sessionDate: existing.sessionDate || row.sessionDate,
    sessionType: existing.sessionType || row.sessionType,
    // Incomplete wins over complete if two sources describe the same session.
    complete: existing.complete === false || row.complete === false
      ? false
      : Boolean(existing.complete && row.complete),
  });
}

function addPipelineIssueRows(rowMap, report) {
  for (const pipeline of report?.pipelines || []) {
    for (const month of pipeline.months || []) {
      const issues = month.issues || {};
      const fallbackType = pipeline.title || 'Other';
      const fallbackDate = month.month || '';

      for (const item of issues.missing_transcripts || []) {
        const title = getSessionStatusTitle(item);

        mergeSessionStatusRow(rowMap, {
          id: item.session_id || null,
          sessionType: fallbackType,
          sessionDate: getSessionStatusDate(item, fallbackDate),
          title,
          complete: false,
        });
      }

      for (const item of issues.mp4_without_imported || []) {
        const title = getSessionStatusTitle(item);

        mergeSessionStatusRow(rowMap, {
          id: null,
          sessionType: fallbackType,
          sessionDate: getSessionStatusDate(item, fallbackDate),
          title,
          complete: false,
        });
      }

      for (const item of issues.missing_summaries || []) {
        const title = getSessionStatusTitle(item);

        mergeSessionStatusRow(rowMap, {
          id: item.id || null,
          sessionType: getSessionStatusType(item, fallbackType),
          sessionDate: getSessionStatusDate(item, fallbackDate),
          title,
          complete: false,
        });
      }
    }
  }
}

function buildSessionStatusRows(report, sessions) {
  const rowMap = new Map();

  for (const session of sessions || []) {
    const title = getSessionStatusTitle(session);
    const sessionType = getSessionStatusType(session);
    const sessionDate = getSessionStatusDate(session);

    mergeSessionStatusRow(rowMap, {
      id: session.id || null,
      sessionType,
      sessionDate,
      title,
      complete: isImportedSessionComplete(session),
    });
  }

  addPipelineIssueRows(rowMap, report);

  return Array.from(rowMap.values()).sort((a, b) => {
    const typeCompare = compareSessionTypes(a.sessionType, b.sessionType);

    if (typeCompare !== 0) {
      return typeCompare;
    }

    const dateCompare = String(b.sessionDate || '').localeCompare(
      String(a.sessionDate || ''),
      undefined,
      { numeric: true, sensitivity: 'base' },
    );

    if (dateCompare !== 0) {
      return dateCompare;
    }

    return TITLE_COLLATOR.compare(a.title || '', b.title || '');
  });
}

function groupSessionStatusRows(rows) {
  const grouped = new Map();

  for (const row of rows || []) {
    const sessionType = row.sessionType || 'Other';

    if (!grouped.has(sessionType)) {
      grouped.set(sessionType, []);
    }

    grouped.get(sessionType).push(row);
  }

  return Array.from(grouped.entries())
    .sort(([a], [b]) => compareSessionTypes(a, b))
    .map(([sessionType, items]) => ({
      sessionType,
      rows: items,
    }));
}




function getSmartRerankerRecommendation(plan) {
  return plan?.reranker_recommendation || null;
}

function getSmartRerankerType(plan) {
  return getSmartRerankerRecommendation(plan)?.reranker_type || 'none';
}

function getAiSearchButtonLabel(plan, hasSelectedSessions = false) {
  if (hasSelectedSessions) {
    return 'Re-Search with AI';
  }

  const recommendation = getSmartRerankerRecommendation(plan);

  if (!recommendation || !recommendation.button_label || recommendation.reranker_type === 'none') {
    return 'Re-Search with AI';
  }

  return recommendation.button_label;
}

function getAiSearchTooltipTitle(plan, hasSelectedSessions = false) {
  if (hasSelectedSessions) {
    return 'Re-Search with AI';
  }

  const recommendation = getSmartRerankerRecommendation(plan);

  if (!recommendation || recommendation.reranker_type === 'none') {
    return 'Re-Search with AI';
  }

  return recommendation.button_label || 'AI Re-Search';
}

function getAiSearchTooltipDescription(plan, hasSelectedSessions = false) {
  if (hasSelectedSessions) {
    return 'This searches only your selected sessions and uses AI to find the best matches. It will also try to give you a direct, cited answer if your question can be answered from just these sessions.';
  }

  const recommendation = getSmartRerankerRecommendation(plan);

  if (!recommendation || recommendation.reranker_type === 'none') {
    return 'AI Re-Search reruns the query with a larger candidate pool and lets TD Classes choose the useful AI reranking path when one is recommended.';
  }

  return recommendation.reason || 'TD Classes will apply the planner-recommended AI reranker.';
}

function buildSmartDisplayResults(data) {
  const plan = data?.plan || {};
  const appliedReranker = data?.applied_reranker || null;
  const target = plan.target || 'combined';

  if ((appliedReranker === 'session' || target === 'sessions') && Array.isArray(data?.session_results)) {
    return data.session_results.map((session) => ({
      ...session,
      result_kind: 'session',
    }));
  }

  if (Array.isArray(data?.chunk_results)) {
    return data.chunk_results.map((chunk) => ({
      ...chunk,
      result_kind: 'chunk',
    }));
  }

  return [];
}

function buildChunkDisplayResults(chunks) {
  return (chunks || []).map((chunk) => ({
    ...chunk,
    result_kind: 'chunk',
  }));
}

function makeFavoriteSourceKey(favoriteType, sourceId) {
  return `${favoriteType}:${sourceId}`;
}

function getFavoriteSourceForResult(result) {
  if (result?.result_kind === 'session') {
    const sessionId = Number(result.session_id);

    if (Number.isInteger(sessionId) && sessionId > 0) {
      return {
        favoriteType: 'session',
        sourceId: sessionId,
        key: makeFavoriteSourceKey('session', sessionId),
      };
    }

    return null;
  }

  const chunkId = Number(result?.chunk_id);

  if (!Number.isInteger(chunkId) || chunkId <= 0) {
    return null;
  }

  return {
    favoriteType: 'clip',
    sourceId: chunkId,
    key: makeFavoriteSourceKey('clip', chunkId),
  };
}



function getFavoriteSourceForSession(session) {
  const sessionId = Number(session?.id ?? session?.session_id);

  if (!Number.isInteger(sessionId) || sessionId <= 0) {
    return null;
  }

  return {
    favoriteType: 'session',
    sourceId: sessionId,
    key: makeFavoriteSourceKey('session', sessionId),

    // Browse favorites were not reached through a search query.
    sourceQuery: null,
  };
}


function getFavoriteSourceForQuestion(question) {
  const questionId = Number(question?.id);

  if (!Number.isInteger(questionId) || questionId <= 0) {
    return null;
  }

  return {
    favoriteType: 'question',
    sourceId: questionId,
    key: makeFavoriteSourceKey('question', questionId),

    // Browse favorites were not reached through a search query.
    sourceQuery: null,
  };
}



function countFavoriteItems(items, getSource, statusBySource) {
  return (items || []).reduce((count, item) => {
    const source = getSource(item);

    return count + (
      source && statusBySource[source.key]?.isFavorite
        ? 1
        : 0
    );
  }, 0);
}


function getFavoriteSourcesForResultQuestions(result) {
  const rawQuestionIds = Array.isArray(result?.question_ids)
    ? result.question_ids
    : result?.question_id !== null && result?.question_id !== undefined
      ? [result.question_id]
      : [];

  const sources = [];
  const seenQuestionIds = new Set();

  for (const rawQuestionId of rawQuestionIds) {
    const questionId = Number(rawQuestionId);

    if (
      !Number.isInteger(questionId)
      || questionId <= 0
      || seenQuestionIds.has(questionId)
    ) {
      continue;
    }

    seenQuestionIds.add(questionId);

    const source = getFavoriteSourceForQuestion({ id: questionId });

    if (source) {
      sources.push(source);
    }
  }

  return sources;
}


const FAVORITE_STATUS_BATCH_SIZE = 100;
const NOTE_STATUS_BATCH_SIZE = 100;

function BookmarkIcon({ filled = false }) {
  return (
    <svg
      className="favorite-bookmark-icon"
      viewBox="0 0 24 24"
      width="20"
      height="20"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M12 2.75l2.86 5.79 6.39.93-4.62 4.5 1.09 6.36L12 17.32l-5.72 3.01 1.09-6.36-4.62-4.5 6.39-.93L12 2.75Z"
        fill={filled ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function FavoriteToggleButton({
  source,
  status,
  checking,
  actionLoading,
  onToggle,
}) {
  const isFavorite = Boolean(status?.isFavorite);
  const busy = checking || actionLoading;
  const label = actionLoading
    ? (isFavorite ? 'Removing favorite…' : 'Saving favorite…')
    : checking
      ? 'Checking favorite status…'
      : isFavorite
        ? 'Remove from favorites'
        : 'Save to favorites';

  return (
    <Tooltip label={label} withArrow position="top" openDelay={250}>
      <button
        type="button"
        className={`favorite-toggle-button${isFavorite ? ' favorite-toggle-button-saved' : ''}`}
        aria-label={label}
        aria-pressed={isFavorite}
        disabled={busy}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onToggle(source);
        }}
      >
        {busy ? <Loader size={16} /> : <BookmarkIcon filled={isFavorite} />}
      </button>
    </Tooltip>
  );
}

// Matches api.js's searchCombined() default chunkLimit, which plain Search
// never overrides. If a plain search returns this many chunk_results, more
// matches likely exist beyond what's shown.
const PLAIN_SEARCH_CHUNK_LIMIT = 12;

// How many results sit within `tolerance` of the top combined_score.
// Used to detect a tight cluster of near-equally-ranked results in
// Best match / Conceptual mode, where the score is a continuous blend
// and a high raw count doesn't by itself imply a tie (unlike Exact mode).
function countCloselyRankedResults(results, tolerance = 0.15) {
  const scores = (results || [])
    .map((result) => Number(result.combined_score))
    .filter((score) => Number.isFinite(score));

  if (scores.length === 0) {
    return 0;
  }

  const topScore = Math.max(...scores);

  if (topScore <= 0) {
    return 0;
  }

  const threshold = topScore * (1 - tolerance);

  return scores.filter((score) => score >= threshold).length;
}

// Software switch: keep regular Search deterministic.
// Set to true later to route regular Search through the AI planner again.
// The AI button still uses /search/smart separately.
const USE_SMART_PLANNER_FOR_REGULAR_SEARCH = false;

// Renders a grounded, cited answer from /search/answer above the regular
// chunk-card results. Returns null (renders nothing) whenever there's no
// grounded answer to show -- callers are expected to only pass a value
// here when answer.grounded was true.
// Splits answer text on [chunk:ID] markers into renderable parts, and
// assigns each distinct chunk_id a stable [N] number in first-appearance
// order. Shared by both the on-screen render and the copy-to-clipboard
// builder below so the numbering is always identical between the two.
function parseAnswerCitationParts(answerText) {
  const citationOrder = [];
  const citationPattern = /\[chunk:(\d+)\]/g;
  const parts = [];
  let lastIndex = 0;
  let match = citationPattern.exec(answerText);

  while (match !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: 'text', value: answerText.slice(lastIndex, match.index) });
    }

    const chunkId = Number(match[1]);

    if (!citationOrder.includes(chunkId)) {
      citationOrder.push(chunkId);
    }

    parts.push({ type: 'citation', chunkId, label: citationOrder.indexOf(chunkId) + 1 });
    lastIndex = citationPattern.lastIndex;
    match = citationPattern.exec(answerText);
  }

  if (lastIndex < answerText.length) {
    parts.push({ type: 'text', value: answerText.slice(lastIndex) });
  }

  return { parts, citationOrder };
}

// Builds the plain-text version of the answer for copy-to-clipboard: the
// original question, the answer with [N] markers instead of clickable
// pills, and a numbered citation list with source/timestamp and the
// supporting note for each -- none of which changes what's shown on
// screen, only what gets copied.
function buildAnswerClipboardText(answer) {
  const { parts, citationOrder } = parseAnswerCitationParts(answer.answer || '');

  const answerLines = parts
    .map((part) => (part.type === 'text' ? part.value : `[${part.label}]`))
    .join('');

  const chunkById = new Map(
    (answer.source_chunks || []).map((chunk) => [chunk.chunk_id, chunk])
  );

  const citationsByChunkId = new Map(
    (answer.citations || []).map((citation) => [citation.chunk_id, citation])
  );

  const citationLines = citationOrder.map((chunkId, index) => {
    const chunk = chunkById.get(chunkId);
    const citation = citationsByChunkId.get(chunkId);

    const sourceLabel = chunk
      ? [chunk.title, chunk.start_time && chunk.end_time ? `${chunk.start_time}–${chunk.end_time}` : null]
          .filter(Boolean)
          .join(' — ')
      : 'Source unavailable';

    const supportsLine = citation && citation.supports ? `\n    ${citation.supports}` : '';

    return `[${index + 1}] ${sourceLabel}${supportsLine}`;
  });

  return [
    `Q: ${answer.query || ''}`,
    '',
    answerLines,
    '',
    'Citations:',
    citationLines.join('\n'),
  ].join('\n');
}

// Small self-contained SVG icons, so this doesn't depend on an icon library
// that may or may not be installed in this project already.
function CopyIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function buildQaQuestionClipboardText({ question, answerText, summaryText }) {
  const questionText = String(
    question?.item_text || getQuestionTitle(question || {})
  ).trim();
  const cleanedAnswerText = String(answerText || '').trim();
  const cleanedSummaryText = String(summaryText || '').trim();
  const questionTime =
    question?.start_time || formatOptionalTimestamp(question?.start_seconds);
  const citationSource =
    question?.session_title || question?.session_type || 'Q&A session';
  const citationDateAndTime = [
    question?.session_date,
    questionTime ? `at ${questionTime}` : '',
  ]
    .filter(Boolean)
    .join(' ');
  const citation = [citationSource, citationDateAndTime]
    .filter(Boolean)
    .join(' — ');
  const clipboardLines = [
    'Question:',
    questionText || 'Untitled question',
    '',
  ];

  if (cleanedSummaryText && cleanedSummaryText !== cleanedAnswerText) {
    clipboardLines.push(
      'Short AI summary:',
      cleanedSummaryText,
      '',
    );
  }

  clipboardLines.push(
    'Answer:',
    cleanedAnswerText || 'No answer text is available for this question.',
    '',
    `Citation: ${citation || 'Q&A session'}`,
  );

  return clipboardLines.join('\n');
}

function QaQuestionCopyButton({ question, answerText, summaryText, disabled, onCopyError }) {
  const [copied, setCopied] = useState(false);
  const copyTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  async function handleCopy() {
    try {
      await copyTextToClipboard(
        buildQaQuestionClipboardText({ question, answerText, summaryText })
      );

      setCopied(true);

      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }

      copyTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      if (onCopyError) {
        onCopyError(`Could not copy question and answer: ${err.message}`);
      }
    }
  }

  return (
    <Button
      size="xs"
      variant="light"
      color={copied ? 'teal' : undefined}
      leftSection={copied ? <CheckIcon /> : <CopyIcon />}
      disabled={disabled}
      onClick={handleCopy}
    >
      {copied ? 'Copied' : 'Copy question & answer'}
    </Button>
  );
}

function AnswerPanel({ answer, onJumpToChunk, onCopyError }) {
  const [copied, setCopied] = useState(false);
  const copyTimeoutRef = useRef(null);

  async function handleCopy() {
    if (!answer) {
      return;
    }

    try {
      await copyTextToClipboard(buildAnswerClipboardText(answer));

      setCopied(true);

      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }

      // Reverts to the copy icon after a couple seconds, but the button
      // stays clickable the whole time -- a second click just re-copies
      // and restarts this timer, it's never disabled.
      copyTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      if (onCopyError) {
        onCopyError(`Could not copy answer: ${err.message}`);
      }
    }
  }

  if (!answer) {
    return null;
  }

  if (!answer.grounded) {
    return (
      <Paper
        withBorder
        radius="md"
        p="sm"
        style={{
          background: 'rgba(148, 163, 184, 0.08)',
          borderColor: 'rgba(148, 163, 184, 0.3)',
        }}
      >
        <Text size="sm" c="dimmed">
          TD Classes checked the transcripts for a direct answer to this question but didn’t
          find one clearly stated. Try rephrasing, or check the results below.
        </Text>
      </Paper>
    );
  }

  if (!answer.answer) {
    return null;
  }

  const chunkById = new Map(
    (answer.source_chunks || []).map((chunk) => [chunk.chunk_id, chunk])
  );

  const { parts, citationOrder } = parseAnswerCitationParts(answer.answer);

  return (
    <Paper
      withBorder
      radius="md"
      p="md"
      style={{
        background: 'rgba(37, 99, 235, 0.10)',
        borderColor: 'rgba(96, 165, 250, 0.45)',
      }}
    >
      <Stack gap="xs">
        <Group gap={8} align="center" wrap="nowrap" justify="space-between">
          <Group gap={8} align="center" wrap="nowrap">
            <Text size="md" fw={700}>Answer</Text>
            <Text size="xs" c="dimmed">
              Generated from {citationOrder.length} transcript excerpt{citationOrder.length === 1 ? '' : 's'} — click a citation to view the source clip
            </Text>
          </Group>

          <Button
            variant="subtle"
            size="compact-xs"
            color={copied ? 'teal' : undefined}
            leftSection={copied ? <CheckIcon /> : <CopyIcon />}
            onClick={handleCopy}
          >
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </Group>

        <Text size="md" style={{ lineHeight: 1.6 }}>
          {parts.map((part, index) => {
            if (part.type === 'text') {
              return <span key={index}>{part.value}</span>;
            }

            const chunk = chunkById.get(part.chunkId);

            return (
              <Button
                key={index}
                variant="light"
                size="compact-xs"
                radius="xl"
                disabled={!chunk}
                onClick={() => chunk && onJumpToChunk(chunk)}
                style={{ margin: '0 2px', verticalAlign: 'middle' }}
              >
                {part.label}
              </Button>
            );
          })}
        </Text>

        <Text size="xs" c="dimmed">
          This answer is generated by AI from your transcripts and may be incomplete. Verify
          against the cited clips before relying on it.
        </Text>
      </Stack>
    </Paper>
  );
}


function GoogleIcon() {
  return (
    <svg
      aria-hidden="true"
      className="google-sign-in-icon"
      viewBox="0 0 18 18"
      width="18"
      height="18"
    >
      <path
        fill="#4285F4"
        d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.482h4.844a4.14 4.14 0 0 1-1.797 2.716v2.259h2.909c1.702-1.567 2.684-3.875 2.684-6.616Z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.468-.806 5.956-2.179l-2.909-2.259c-.806.54-1.836.859-3.047.859-2.344 0-4.328-1.585-5.037-3.714H.956v2.332A9 9 0 0 0 9 18Z"
      />
      <path
        fill="#FBBC05"
        d="M3.963 10.707A5.42 5.42 0 0 1 3.682 9c0-.593.102-1.17.281-1.707V4.961H.956A9 9 0 0 0 0 9c0 1.452.347 2.827.956 4.039l3.007-2.332Z"
      />
      <path
        fill="#EA4335"
        d="M9 3.579c1.321 0 2.507.454 3.44 1.346l2.581-2.581C13.464.892 11.426 0 9 0A9 9 0 0 0 .956 4.961l3.007 2.332C4.672 5.164 6.656 3.579 9 3.579Z"
      />
    </svg>
  );
}

function getUserDisplayName(user) {
  const fullName = [user?.firstname, user?.lastname]
    .filter(Boolean)
    .join(' ')
    .trim();

  return fullName || user?.emailaddress || 'TD Classes user';
}

function getUserInitials(user) {
  const initials = [user?.firstname, user?.lastname]
    .filter(Boolean)
    .map((value) => String(value).trim().charAt(0).toUpperCase())
    .join('');

  if (initials) {
    return initials.slice(0, 2);
  }

  return String(user?.emailaddress || 'U').charAt(0).toUpperCase();
}

function UserAccountMenu({
  user,
  loggingOut,
  onLogout,
  onOpenSearchHistory,
  onOpenFavorites,
  onOpenNotes,
  onOpenTags,
}) {
  const displayName = getUserDisplayName(user);
  const initials = getUserInitials(user);

  return (
    <Menu position="bottom-end" width={280} shadow="md" withinPortal>
      <Menu.Target>
        <Button
          size="md"
          variant="light"
          className="user-account-button"
          aria-label={`Open account menu for ${displayName}`}
          leftSection={(
            <Avatar
              src={user?.profile_image_url || null}
              alt=""
              size={26}
              radius="xl"
              className="user-account-avatar"
            >
              {initials}
            </Avatar>
          )}
          rightSection={<span className="user-account-chevron" aria-hidden="true">▾</span>}
        >
          {displayName}
        </Button>
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Label>Signed in as</Menu.Label>
        <div className="user-account-summary">
          <Avatar
            src={user?.profile_image_url || null}
            alt=""
            size={42}
            radius="xl"
          >
            {initials}
          </Avatar>

          <div className="user-account-summary-text">
            <Text fw={700} size="sm" lineClamp={1}>
              {displayName}
            </Text>
            <Text size="xs" c="dimmed" lineClamp={1}>
              {user?.emailaddress || ''}
            </Text>
          </div>
        </div>

        <Menu.Divider />

        <Menu.Item onClick={onOpenFavorites}>
          My Favorites
        </Menu.Item>

        <Menu.Item onClick={onOpenNotes}>
          My Notes
        </Menu.Item>

        <Menu.Item onClick={onOpenTags}>
          My Tags
        </Menu.Item>

        <Menu.Item onClick={onOpenSearchHistory}>
          My Searches
        </Menu.Item>

        <Menu.Divider />

        <Menu.Item color="red" disabled={loggingOut} onClick={onLogout}>
          {loggingOut ? 'Signing out…' : 'Sign out'}
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}

const HISTORY_SEARCH_MODES = new Set(['best', 'exact', 'conceptual']);

function normalizeHistoryStringList(value) {
  if (Array.isArray(value)) {
    return [...new Set(value.map((item) => String(item || '').trim()).filter(Boolean))];
  }

  if (typeof value === 'string') {
    return [...new Set(value.split(',').map((item) => item.trim()).filter(Boolean))];
  }

  return [];
}

function getHistorySessionIds(parameters) {
  const rawValues = Array.isArray(parameters?.session_ids)
    ? parameters.session_ids
    : parameters?.session_id !== null && parameters?.session_id !== undefined
      ? [parameters.session_id]
      : [];

  return [...new Set(
    rawValues
      .map((value) => Number(value))
      .filter((value) => Number.isInteger(value) && value > 0)
  )];
}

function RecentSearchHistoryMenu({
  items,
  loading,
  error,
  disabled,
  onOpen,
  onSelect,
}) {
  return (
    <Menu position="bottom-end" width={420} shadow="md" withinPortal>
      <Menu.Target>
        <button
          type="button"
          className="search-history-trigger"
          aria-label="Recall a recent search"
          title="Recall a recent search"
          disabled={disabled}
          onClick={onOpen}
        >
          <span aria-hidden="true">▾</span>
        </button>
      </Menu.Target>

      <Menu.Dropdown className="search-history-dropdown">
        <Menu.Label>Recent searches</Menu.Label>

        {loading && (
          <Menu.Item disabled>
            <Group gap="xs" wrap="nowrap">
              <Loader size="xs" />
              <Text size="sm">Loading…</Text>
            </Group>
          </Menu.Item>
        )}

        {!loading && error && (
          <Menu.Item disabled>
            <Text size="sm" c="red.4">Unable to load recent searches.</Text>
          </Menu.Item>
        )}

        {!loading && !error && items.length === 0 && (
          <Menu.Item disabled>
            <Text size="sm" c="dimmed">No saved searches yet.</Text>
          </Menu.Item>
        )}

        {!loading && !error && items.map((item) => (
          <Menu.Item
            key={item.id}
            className="search-history-item"
            title={item.query_text}
            onClick={() => onSelect(item)}
          >
            <Text size="sm" lineClamp={2}>
              {item.query_text}
            </Text>
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
}

const SEARCH_HISTORY_PAGE_SIZE = 50;

function formatSearchHistoryMode(searchMode) {
  const labels = {
    best: 'Best match',
    exact: 'Exact wording',
    conceptual: 'Conceptual',
  };

  return labels[searchMode] || 'Best match';
}

function formatSearchHistoryDate(value) {
  if (!value) {
    return 'Date unavailable';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function formatSearchHistoryResultSummary(historyItem) {
  const parameters = historyItem?.search_parameters && typeof historyItem.search_parameters === 'object'
    ? historyItem.search_parameters
    : {};
  const chunkCount = Number(parameters.chunk_result_count);
  const sessionCount = Number(parameters.session_result_count);

  if (Number.isFinite(chunkCount) && Number.isFinite(sessionCount)) {
    if (chunkCount > 0 && sessionCount > 0) {
      return `${chunkCount} lesson match${chunkCount === 1 ? '' : 'es'} across ${sessionCount} session${sessionCount === 1 ? '' : 's'}`;
    }

    if (chunkCount > 0) {
      return `${chunkCount} lesson match${chunkCount === 1 ? '' : 'es'}`;
    }

    if (sessionCount > 0) {
      return `${sessionCount} session match${sessionCount === 1 ? '' : 'es'}`;
    }

    return 'No results returned';
  }

  const resultCount = Number(historyItem?.result_count || 0);

  if (!Number.isFinite(resultCount) || resultCount <= 0) {
    return 'No results returned';
  }

  return `${resultCount} result${resultCount === 1 ? '' : 's'} returned`;
}

function formatSearchHistoryFilterSummary(historyItem) {
  const parameters = historyItem?.search_parameters && typeof historyItem.search_parameters === 'object'
    ? historyItem.search_parameters
    : {};
  const parts = [];
  const sessionIds = getHistorySessionIds(parameters);
  const sessionTypes = normalizeHistoryStringList(parameters.session_type);
  const dateFrom = parameters.date_from || parameters.effective_date_from || null;
  const dateTo = parameters.date_to || parameters.effective_date_to || null;

  if (sessionIds.length > 0) {
    parts.push(`${sessionIds.length} selected session${sessionIds.length === 1 ? '' : 's'}`);
  } else if (sessionTypes.length > 0) {
    parts.push(sessionTypes.join(', '));
  } else {
    parts.push('All session types');
  }

  if (dateFrom || dateTo) {
    parts.push(`${dateFrom || 'earliest'} to ${dateTo || 'latest'}`);
  }

  if (parameters.strict_exact) {
    parts.push('Strict exact');
  }

  return parts.join(' · ');
}

function isAiSearchHistoryItem(historyItem) {
  const parameters = historyItem?.search_parameters && typeof historyItem.search_parameters === 'object'
    ? historyItem.search_parameters
    : {};

  return Boolean(
    parameters.ai_reranked ||
    parameters.applied_reranker ||
    parameters.apply_recommended_reranker
  );
}

function MySearchesModal({
  opened,
  onClose,
  items,
  total,
  loading,
  loadingMore,
  error,
  deletingId,
  clearing,
  onLoad,
  onDelete,
  onClear,
  onLoadMore,
}) {
  const hasItems = items.length > 0;
  const hasMore = items.length < total;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="My Searches"
      size="xl"
      centered
      styles={{ body: { maxHeight: 'calc(100vh - 140px)', overflowY: 'auto' } }}
    >
      <Stack gap="md">
        <Group justify="space-between" align="center" wrap="wrap">
          <Text size="sm" c="dimmed">
            {total === 1 ? '1 saved search' : `${total} saved searches`} · newest first
          </Text>

          {hasItems && (
            <Button
              size="xs"
              variant="light"
              color="red"
              disabled={loading || loadingMore || clearing || deletingId !== null}
              onClick={onClear}
            >
              {clearing ? 'Clearing…' : 'Clear history'}
            </Button>
          )}
        </Group>

        {loading && !hasItems && (
          <Group justify="center" py="xl">
            <Loader />
          </Group>
        )}

        {error && (
          <Alert color="red" title="Search history error">
            {error}
          </Alert>
        )}

        {!loading && !error && !hasItems && (
          <Paper withBorder radius="md" p="lg" className="my-search-history-empty">
            <Text fw={700}>No searches saved yet.</Text>
            <Text size="sm" c="dimmed" mt={4}>
              Searches you run while signed in will appear here.
            </Text>
          </Paper>
        )}

        {hasItems && (
          <Stack gap="sm">
            {items.map((item) => {
              const deleting = deletingId === item.id;

              return (
                <Paper
                  key={item.id}
                  withBorder
                  radius="md"
                  p="md"
                  className="my-search-history-card"
                >
                  <Stack gap="xs">
                    <Group justify="space-between" align="flex-start" wrap="nowrap">
                      <div className="my-search-history-query-block">
                        <Text fw={700} className="my-search-history-query">
                          {item.query_text}
                        </Text>
                        <Text size="xs" c="dimmed" mt={3}>
                          {formatSearchHistoryDate(item.searched_at)}
                        </Text>
                      </div>

                      <Group gap="xs" wrap="nowrap">
                        <Button
                          size="xs"
                          variant="light"
                          disabled={deleting || clearing}
                          onClick={() => onLoad(item)}
                        >
                          Load
                        </Button>
                        <Button
                          size="xs"
                          variant="subtle"
                          color="red"
                          disabled={deleting || clearing}
                          onClick={() => onDelete(item)}
                        >
                          {deleting ? 'Deleting…' : 'Delete'}
                        </Button>
                      </Group>
                    </Group>

                    <Group gap="xs" wrap="wrap">
                      <Badge variant="light">
                        {formatSearchHistoryMode(item.search_mode)}
                      </Badge>
                      {isAiSearchHistoryItem(item) && (
                        <Badge color="violet" variant="light">AI</Badge>
                      )}
                      <Text size="sm" c="dimmed">
                        {formatSearchHistoryResultSummary(item)}
                      </Text>
                    </Group>

                    <Text size="xs" c="dimmed">
                      {formatSearchHistoryFilterSummary(item)}
                    </Text>
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        )}

        {hasMore && (
          <Group justify="center">
            <Button
              variant="light"
              disabled={loadingMore || clearing || deletingId !== null}
              onClick={onLoadMore}
            >
              {loadingMore ? 'Loading…' : 'Load more'}
            </Button>
          </Group>
        )}
      </Stack>
    </Modal>
  );
}


const MY_FAVORITES_PAGE_SIZE = 50;
const MY_NOTES_PAGE_SIZE = 100;

const MY_FAVORITE_TYPE_OPTIONS = [
  { value: 'all', label: 'All types' },
  { value: 'session', label: 'Sessions' },
  { value: 'question', label: 'Questions' },
  { value: 'clip', label: 'Transcript excerpts' },
];

function formatFavoriteTypeLabel(favoriteType) {
  const labels = {
    session: 'Session',
    question: 'Question',
    clip: 'Transcript excerpt',
  };

  return labels[favoriteType] || 'Favorite';
}

function getFavoriteTypeColor(favoriteType) {
  if (favoriteType === 'question') {
    return 'teal';
  }

  if (favoriteType === 'clip') {
    return 'violet';
  }

  return 'blue';
}

function formatFavoriteTimeRange(favorite) {
  const start = formatOptionalTimestamp(favorite?.start_seconds);
  const end = formatOptionalTimestamp(favorite?.end_seconds);

  if (start && end && start !== end) {
    return `${start}–${end}`;
  }

  return start || end || '';
}

function canOpenFavoriteSource(favorite) {
  if (!favorite?.live_source_available) {
    return false;
  }

  if (favorite.favorite_type === 'session') {
    return Number(favorite.session_id) > 0;
  }

  return (
    Number(favorite.session_id) > 0
    && favorite.start_seconds !== null
    && favorite.start_seconds !== undefined
  );
}

function MyFavoritesModal({
  opened,
  onClose,
  items,
  total,
  loading,
  loadingMore,
  error,
  deletingId,
  query,
  favoriteType,
  onQueryChange,
  onFavoriteTypeChange,
  onClearFilters,
  onOpen,
  onDelete,
  onLoadMore,
}) {
  const hasItems = items.length > 0;
  const hasMore = items.length < total;
  const hasFilters = Boolean(String(query || '').trim()) || favoriteType !== 'all';

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="My Favorites"
      size="xl"
      centered
      styles={{ body: { maxHeight: 'calc(100vh - 140px)', overflowY: 'auto' } }}
    >
      <Stack gap="md">
        <Paper withBorder radius="md" p="sm" className="my-favorites-filter-panel">
          <Group align="end" wrap="wrap">
            <TextInput
              label="Search favorites"
              placeholder="Search titles, transcript text, sessions, or saved queries..."
              value={query}
              maxLength={500}
              onChange={(event) => onQueryChange(event.currentTarget.value)}
              style={{ flex: '1 1 360px' }}
            />

            <Select
              label="Favorite type"
              data={MY_FAVORITE_TYPE_OPTIONS}
              value={favoriteType}
              allowDeselect={false}
              onChange={(value) => onFavoriteTypeChange(value || 'all')}
              style={{ flex: '0 1 220px' }}
            />

            {hasFilters && (
              <Button
                variant="subtle"
                disabled={loading || loadingMore || deletingId !== null}
                onClick={onClearFilters}
              >
                Clear filters
              </Button>
            )}
          </Group>
        </Paper>

        <Group justify="space-between" align="center" wrap="wrap">
          <Text size="sm" c="dimmed">
            {total === 1 ? '1 favorite' : `${total} favorites`} · newest first
          </Text>

          {loading && hasItems && (
            <Group gap="xs">
              <Loader size="xs" />
              <Text size="xs" c="dimmed">Refreshing…</Text>
            </Group>
          )}
        </Group>

        {loading && !hasItems && (
          <Group justify="center" py="xl">
            <Loader />
          </Group>
        )}

        {error && (
          <Alert color="red" title="Favorites error">
            {error}
          </Alert>
        )}

        {!loading && !error && !hasItems && (
          <Paper withBorder radius="md" p="lg" className="my-favorites-empty">
            <Text fw={700}>
              {hasFilters
                ? 'No favorites match the current filters.'
                : 'No favorites saved yet.'}
            </Text>

            <Text size="sm" c="dimmed" mt={4}>
              {hasFilters
                ? 'Try another search term or favorite type.'
                : 'Use the stars beside sessions, questions, and transcript excerpts to save them here.'}
            </Text>
          </Paper>
        )}

        {hasItems && (
          <Stack gap="sm">
            {items.map((favorite) => {
              const deleting = deletingId === favorite.id;
              const timeRange = formatFavoriteTimeRange(favorite);
              const canOpen = canOpenFavoriteSource(favorite);
              const title = favorite.title_snapshot || 'Untitled favorite';
              const sessionTitle = favorite.session_title_snapshot || '';
              const showSessionTitle = (
                favorite.favorite_type !== 'session'
                && sessionTitle
                && sessionTitle !== title
              );

              return (
                <Paper
                  key={favorite.id}
                  withBorder
                  radius="md"
                  p="md"
                  className="my-favorite-card"
                >
                  <Stack gap="xs">
                    <Group justify="space-between" align="flex-start" wrap="nowrap">
                      <div className="my-favorite-title-block">
                        <Group gap="xs" wrap="wrap">
                          <Badge
                            color={getFavoriteTypeColor(favorite.favorite_type)}
                            variant="light"
                          >
                            {formatFavoriteTypeLabel(favorite.favorite_type)}
                          </Badge>

                          {favorite.session_type_snapshot && (
                            <Badge variant="outline">
                              {favorite.session_type_snapshot}
                            </Badge>
                          )}

                          {favorite.session_date_snapshot && (
                            <Badge variant="outline">
                              {favorite.session_date_snapshot}
                            </Badge>
                          )}

                          {timeRange && (
                            <Badge variant="outline">
                              {timeRange}
                            </Badge>
                          )}
                        </Group>

                        <Text fw={700} size="lg" mt={6} className="my-favorite-title">
                          {title}
                        </Text>

                        {showSessionTitle && (
                          <Text size="sm" c="dimmed" mt={2}>
                            From: {sessionTitle}
                          </Text>
                        )}

                        <Text size="xs" c="dimmed" mt={3}>
                          Saved {formatSearchHistoryDate(favorite.created_at)}
                        </Text>
                      </div>

                      <Group gap="xs" wrap="nowrap">
                        <Button
                          size="xs"
                          variant="light"
                          disabled={!canOpen || deleting}
                          onClick={() => onOpen(favorite)}
                        >
                          {favorite.favorite_type === 'session'
                            ? 'Open session'
                            : 'Open transcript'}
                        </Button>

                        <Button
                          size="xs"
                          variant="subtle"
                          color="red"
                          disabled={deleting}
                          onClick={() => onDelete(favorite)}
                        >
                          {deleting ? 'Removing…' : 'Remove'}
                        </Button>
                      </Group>
                    </Group>

                    {favorite.snippet_snapshot && (
                      <Text size="sm" className="my-favorite-snippet" lineClamp={5}>
                        {favorite.snippet_snapshot}
                      </Text>
                    )}

                    {favorite.source_query_snapshot && (
                      <Paper
                        withBorder
                        radius="sm"
                        p="xs"
                        className="my-favorite-source-query"
                      >
                        <Text size="xs" fw={700} c="dimmed">
                          Saved from search
                        </Text>
                        <Text size="sm" mt={2} lineClamp={2}>
                          {favorite.source_query_snapshot}
                        </Text>
                      </Paper>
                    )}

                    {favorite.note_text && (
                      <Paper withBorder radius="sm" p="xs" className="my-favorite-note">
                        <Text size="xs" fw={700} c="dimmed">Note</Text>
                        <Text size="sm" mt={2}>
                          {favorite.note_text}
                        </Text>
                      </Paper>
                    )}

                    {!canOpen && (
                      <Text size="xs" c="yellow.4">
                        {favorite.live_source_available
                          ? 'The saved snapshot is available, but this source has no transcript timestamp.'
                          : 'The original source is no longer available. The saved snapshot remains available here.'}
                      </Text>
                    )}
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        )}

        {hasMore && (
          <Group justify="center">
            <Button
              variant="light"
              disabled={loadingMore || deletingId !== null}
              onClick={onLoadMore}
            >
              {loadingMore ? 'Loading…' : 'Load more'}
            </Button>
          </Group>
        )}
      </Stack>
    </Modal>
  );
}


function AuthControl({
  authLoading,
  loginEnabled,
  currentUser,
  loggingOut,
  onLogout,
  onOpenSearchHistory,
  onOpenFavorites,
  onOpenNotes,
  onOpenTags,
}) {
  if (authLoading) {
    return (
      <div className="auth-control-placeholder" aria-label="Checking sign-in status">
        <Loader size="sm" />
      </div>
    );
  }

  if (!loginEnabled) {
    return null;
  }

  if (currentUser) {
    return (
      <UserAccountMenu
        user={currentUser}
        loggingOut={loggingOut}
        onLogout={onLogout}
        onOpenSearchHistory={onOpenSearchHistory}
        onOpenFavorites={onOpenFavorites}
        onOpenNotes={onOpenNotes}
        onOpenTags={onOpenTags}
      />
    );
  }

  return (
    <Tooltip
      multiline
      withArrow
      w={300}
      position="top"
      openDelay={250}
      label="Signing in is optional. TD Classes remains fully usable without an account."
    >
      <Button
        component="a"
        href="/auth/google/login"
        size="md"
        variant="outline"
        leftSection={<GoogleIcon />}
        className="google-sign-in-button"
      >
        <Group gap={8} align="center" wrap="nowrap">
          <span>Sign in with Google</span>
          <span className="button-info-icon" aria-hidden="true">i</span>
        </Group>
      </Button>
    </Tooltip>
  );
}


const POLICY_EFFECTIVE_DATE = 'July 23, 2026';

function SiteFooter() {
  return (
    <footer className="site-footer">
      <Group justify="space-between" align="center" wrap="wrap" gap="sm">
        <Text size="sm" c="dimmed">
          TD Classes
        </Text>

        <Group gap="md">
          <Anchor href="/privacy" size="sm" className="site-footer-link">
            Privacy Policy
          </Anchor>
          <Anchor href="/terms" size="sm" className="site-footer-link">
            Terms of Use
          </Anchor>
        </Group>
      </Group>
    </footer>
  );
}

function PolicyPageShell({ title, subtitle, children }) {
  return (
    <Container size="md" py="xl">
      <Stack gap="lg">
        <Group justify="space-between" align="flex-start" wrap="wrap" gap="md">
          <div>
            <Title order={1}>{title}</Title>
            <Text size="sm" c="dimmed" mt={4}>
              Effective {POLICY_EFFECTIVE_DATE}
            </Text>
            {subtitle && (
              <Text size="lg" c="dimmed" mt="xs">
                {subtitle}
              </Text>
            )}
          </div>

          <Button component="a" href="/" variant="light">
            Back to TD Classes
          </Button>
        </Group>

        <Paper withBorder radius="md" p="xl" className="policy-card">
          <Stack gap="xl">
            {children}
          </Stack>
        </Paper>

        <SiteFooter />
      </Stack>
    </Container>
  );
}

function PrivacyPolicyPage() {
  return (
    <PolicyPageShell
      title="Privacy Policy"
      subtitle="How TD Classes handles search, technical, and optional account information."
    >
      <Stack gap="sm" className="policy-section">
        <Title order={2}>Overview</Title>
        <Text>
          TD Classes is an educational search and reference tool for David&apos;s trading class
          content. You may use TD Classes without signing in. When optional Google sign-in is
          available, signing in will provide account-based features but will not be required for
          the core search and browse functions.
        </Text>
      </Stack>

      <Stack gap="sm" className="policy-section">
        <Title order={2}>Information TD Classes collects</Title>
        <Text fw={700}>Search and usage information</Text>
        <List spacing="xs">
          <List.Item>The text of searches you submit and the search endpoint or mode used.</List.Item>
          <List.Item>
            Search-result metadata, such as result counts, top scores, and the session or transcript
            chunk returned as the leading result.
          </List.Item>
          <List.Item>
            Technical request information, which may include your IP address, browser user agent,
            request time, and ordinary server-log information.
          </List.Item>
        </List>

        <Text fw={700} mt="sm">Optional Google account information</Text>
        <Text>
          When you choose to sign in with Google, TD Classes may receive and store your Google
          account&apos;s stable identifier, email address, first and last name, profile-image URL,
          last-login time, and account creation or update timestamps. TD Classes does not receive
          your Google password.
        </Text>
      </Stack>

      <Stack gap="sm" className="policy-section">
        <Title order={2}>How information is used</Title>
        <List spacing="xs">
          <List.Item>To operate search, browse, transcript-context, and account features.</List.Item>
          <List.Item>To maintain login sessions and identify returning users who choose to sign in.</List.Item>
          <List.Item>To diagnose errors, protect the service, and investigate misuse.</List.Item>
          <List.Item>To evaluate and improve search relevance and the overall user experience.</List.Item>
          <List.Item>To support future user-requested features such as saved searches and favorites.</List.Item>
        </List>
      </Stack>

      <Stack gap="sm" className="policy-section">
        <Title order={2}>Service providers and disclosure</Title>
        <Text>
          TD Classes uses third-party services to operate. Depending on the feature you use, search
          text and relevant transcript excerpts may be sent to OpenAI for functions such as semantic
          search embeddings, query planning, result reranking, or answer generation. Google processes
          information used for optional Google sign-in. Hosting, database, and network providers may
          process technical information as necessary to provide the service.
        </Text>
        <Text>
          TD Classes does not sell personal information and does not use personal information for
          third-party advertising. Information may be disclosed when reasonably necessary to comply
          with law, protect the service or its users, or respond to a valid legal request.
        </Text>
      </Stack>

      <Stack gap="sm" className="policy-section">
        <Title order={2}>Cookies and sessions</Title>
        <Text>
          When optional sign-in is enabled, TD Classes uses a necessary session cookie to keep you
          signed in. The cookie is intended for authentication and service security, not advertising.
          You can end the session by signing out or by clearing the cookie in your browser.
        </Text>
      </Stack>

      <Stack gap="sm" className="policy-section">
        <Title order={2}>Retention and security</Title>
        <Text>
          Information is retained for as long as reasonably needed to operate, secure, troubleshoot,
          and improve TD Classes, or to meet legal obligations. Reasonable administrative and
          technical safeguards are used, but no internet service or storage system can be guaranteed
          to be completely secure.
        </Text>
      </Stack>

      <Stack gap="sm" className="policy-section">
        <Title order={2}>Your choices</Title>
        <List spacing="xs">
          <List.Item>You may continue using core TD Classes features without signing in.</List.Item>
          <List.Item>You may sign out at any time after optional sign-in is enabled.</List.Item>
          <List.Item>Avoid entering confidential or highly sensitive information into a search.</List.Item>
          <List.Item>
            You may request access to, correction of, or deletion of account information by contacting
            the TD Classes administrator through the contact information provided with your access or
            shown on the Google sign-in consent screen.
          </List.Item>
        </List>
      </Stack>

      <Stack gap="sm" className="policy-section">
        <Title order={2}>Children&apos;s privacy</Title>
        <Text>
          TD Classes is not directed to children under 13, and the service is not intended to collect
          personal information from children under 13.
        </Text>
      </Stack>

      <Stack gap="sm" className="policy-section">
        <Title order={2}>Changes and contact</Title>
        <Text>
          This policy may be updated as TD Classes adds features or changes how information is handled.
          The effective date at the top of this page will be updated when material changes are made.
          Privacy questions may be directed to the TD Classes administrator through the contact
          information provided with your access or shown on the Google sign-in consent screen.
        </Text>
      </Stack>
    </PolicyPageShell>
  );
}

function TermsOfUsePage() {
  return (
    <PolicyPageShell
      title="Terms of Use"
      subtitle="Rules and limitations that apply when using TD Classes."
    >
      <Stack gap="sm" className="policy-section">
        <Title order={2}>Acceptance</Title>
        <Text>
          By accessing or using TD Classes, you agree to these Terms of Use. If you do not agree,
          do not use the service.
        </Text>
      </Stack>

      <Stack gap="sm" className="policy-section">
        <Title order={2}>Purpose of the service</Title>
        <Text>
          TD Classes is an educational search and reference tool that helps users locate material in
          trading-class transcripts and summaries. It is not a brokerage service, trading signal,
          investment adviser, or substitute for reviewing the original lesson and exercising your
          own judgment.
        </Text>
        <Text>
          Nothing in TD Classes is financial, investment, legal, tax, or other professional advice.
          Trading involves substantial risk, and you are solely responsible for your own decisions.
        </Text>
      </Stack>

      <Stack gap="sm" className="policy-section">
        <Title order={2}>Optional accounts</Title>
        <Text>
          Core search and browse functions may be available without an account. When optional Google
          sign-in is offered, you are responsible for activity performed through your signed-in
          session and for signing out on shared devices. Do not attempt to use another person&apos;s
          account or misrepresent your identity.
        </Text>
      </Stack>

      <Stack gap="sm" className="policy-section">
        <Title order={2}>Acceptable use</Title>
        <Text>You agree not to:</Text>
        <List spacing="xs">
          <List.Item>Attack, overload, disrupt, or attempt to bypass the security of TD Classes.</List.Item>
          <List.Item>Use automated access in a manner that materially burdens the service.</List.Item>
          <List.Item>Upload or submit malicious code or unlawful content.</List.Item>
          <List.Item>Use TD Classes to infringe another person&apos;s rights or violate applicable law.</List.Item>
          <List.Item>
            Copy, scrape, publish, sell, or redistribute class transcripts or other protected content
            except as permitted by the content owner or applicable law.
          </List.Item>
        </List>
      </Stack>

      <Stack gap="sm" className="policy-section">
        <Title order={2}>Content and intellectual property</Title>
        <Text>
          Class videos, transcripts, lesson material, names, and other source content remain the
          property of their respective owners. TD Classes&apos; interface, software, organization, and
          original supporting material are protected by applicable intellectual-property laws.
          Access to TD Classes does not grant a right to redistribute the underlying class content.
        </Text>
      </Stack>

      <Stack gap="sm" className="policy-section">
        <Title order={2}>AI-generated and search results</Title>
        <Text>
          Search rankings, transcript excerpts, summaries, and AI-generated answers may be incomplete,
          inaccurate, or taken out of context. Always consult the cited transcript and original lesson
          before relying on a result. TD Classes does not guarantee that every relevant lesson will be
          found or that every generated answer will be correct.
        </Text>
      </Stack>

      <Stack gap="sm" className="policy-section">
        <Title order={2}>Third-party services</Title>
        <Text>
          TD Classes may rely on third-party services, including Google and OpenAI. Those services are
          governed by their own terms and privacy policies. TD Classes is not responsible for outages,
          changes, or actions of third-party services.
        </Text>
      </Stack>

      <Stack gap="sm" className="policy-section">
        <Title order={2}>Availability and changes</Title>
        <Text>
          TD Classes may be changed, suspended, limited, or discontinued at any time. Features may be
          added or removed, and access may be restricted when needed for maintenance, security, legal,
          or operational reasons.
        </Text>
      </Stack>

      <Stack gap="sm" className="policy-section">
        <Title order={2}>Disclaimer and limitation of liability</Title>
        <Text>
          TD Classes is provided on an &quot;as is&quot; and &quot;as available&quot; basis without warranties of any kind
          to the fullest extent permitted by law. To the fullest extent permitted by law, the TD Classes
          administrator and contributors will not be liable for trading losses, lost profits, lost data,
          service interruption, or indirect, incidental, special, consequential, or punitive damages
          arising from use of or inability to use TD Classes.
        </Text>
      </Stack>

      <Stack gap="sm" className="policy-section">
        <Title order={2}>Changes and contact</Title>
        <Text>
          These terms may be revised as TD Classes changes. Continued use after revised terms are posted
          constitutes acceptance of the revised terms. Questions may be directed to the TD Classes
          administrator through the contact information provided with your access or shown on the Google
          sign-in consent screen.
        </Text>
      </Stack>
    </PolicyPageShell>
  );
}

function DavealyzerSearchApp() {
  const [currentUser, setCurrentUser] = useState(null);
  const [loginEnabled, setLoginEnabled] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [loggingOut, setLoggingOut] = useState(false);
  const [query, setQuery] = useState('');
  const [recentSearchHistory, setRecentSearchHistory] = useState([]);
  const [loadingRecentSearchHistory, setLoadingRecentSearchHistory] = useState(false);
  const [recentSearchHistoryError, setRecentSearchHistoryError] = useState('');
  const recentSearchHistoryRequestIdRef = useRef(0);
  const [searchHistoryOpened, setSearchHistoryOpened] = useState(false);
  const [searchHistoryItems, setSearchHistoryItems] = useState([]);
  const [searchHistoryTotal, setSearchHistoryTotal] = useState(0);
  const [loadingSearchHistory, setLoadingSearchHistory] = useState(false);
  const [loadingMoreSearchHistory, setLoadingMoreSearchHistory] = useState(false);
  const [searchHistoryError, setSearchHistoryError] = useState('');
  const [deletingSearchHistoryId, setDeletingSearchHistoryId] = useState(null);
  const [clearingSearchHistory, setClearingSearchHistory] = useState(false);
  const searchHistoryRequestIdRef = useRef(0);

  const [myFavoritesOpened, setMyFavoritesOpened] = useState(false);
  const [myFavoritesItems, setMyFavoritesItems] = useState([]);
  const [myFavoritesTotal, setMyFavoritesTotal] = useState(0);
  const [loadingMyFavorites, setLoadingMyFavorites] = useState(false);
  const [loadingMoreMyFavorites, setLoadingMoreMyFavorites] = useState(false);
  const [myFavoritesError, setMyFavoritesError] = useState('');
  const [deletingMyFavoriteId, setDeletingMyFavoriteId] = useState(null);
  const [myFavoritesQuery, setMyFavoritesQuery] = useState('');
  const [myFavoritesType, setMyFavoritesType] = useState('all');
  const myFavoritesRequestIdRef = useRef(0);

  const [myNotesOpened, setMyNotesOpened] = useState(false);
  const [myNotesItems, setMyNotesItems] = useState([]);
  const [myNotesTotal, setMyNotesTotal] = useState(0);
  const [loadingMyNotes, setLoadingMyNotes] = useState(false);
  const [loadingMoreMyNotes, setLoadingMoreMyNotes] = useState(false);
  const [myNotesError, setMyNotesError] = useState('');
  const [deletingMyNoteId, setDeletingMyNoteId] = useState(null);
  const [myNotesQuery, setMyNotesQuery] = useState('');
  const [myNotesType, setMyNotesType] = useState('all');
  const myNotesRequestIdRef = useRef(0);

  const [myTagsOpened, setMyTagsOpened] = useState(false);
  const [tagStatusBySource, setTagStatusBySource] = useState({});
  const [loadingTagStatuses, setLoadingTagStatuses] = useState(false);
  const [sessionTagSummaryBySession, setSessionTagSummaryBySession] = useState({});
  const [, setLoadingSessionTagSummaries] = useState(false);
  const [tagStatusRefreshToken, setTagStatusRefreshToken] = useState(0);
  const tagStatusRequestIdRef = useRef(0);
  const sessionTagSummaryRequestIdRef = useRef(0);

  const [noteEditorOpened, setNoteEditorOpened] = useState(false);
  const [activeNoteSource, setActiveNoteSource] = useState(null);
  const [activeNote, setActiveNote] = useState(null);
  const [loadingActiveNote, setLoadingActiveNote] = useState(false);
  const [savingActiveNote, setSavingActiveNote] = useState(false);
  const [deletingActiveNote, setDeletingActiveNote] = useState(false);
  const [activeNoteError, setActiveNoteError] = useState('');
  const [openingNoteSourceKey, setOpeningNoteSourceKey] = useState(null);
  const activeNoteRequestIdRef = useRef(0);

  const [noteStatusBySource, setNoteStatusBySource] = useState({});
  const [loadingNoteStatuses, setLoadingNoteStatuses] = useState(false);
  const noteStatusRequestIdRef = useRef(0);

  const [favoriteStatusBySource, setFavoriteStatusBySource] = useState({});
  const [loadingFavoriteStatuses, setLoadingFavoriteStatuses] = useState(false);
  const [favoriteActionBySource, setFavoriteActionBySource] = useState({});
  const favoriteStatusRequestIdRef = useRef(0);
  const [results, setResults] = useState([]);
  const [context, setContext] = useState(null);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [loadingAiSearch, setLoadingAiSearch] = useState(false);
  const [aiSearchUsed, setAiSearchUsed] = useState(false);
  const [smartSearchPlan, setSmartSearchPlan] = useState(null);
  const [answerResult, setAnswerResult] = useState(null);
  const [openResultKeys, setOpenResultKeys] = useState([]);
  const [loadingAnswer, setLoadingAnswer] = useState(false);
  const answerRequestIdRef = useRef(0);
  const answerAbortControllerRef = useRef(null);
  const [loadingContext, setLoadingContext] = useState(false);
  const [error, setError] = useState('');
  // Shown when /search/intent recognizes the query as a retrieval request for
  // a specific session but can't resolve it cleanly (ambiguous match, or no
  // matching session). Per design: show the message and stop -- no fallback
  // search runs until the user edits and resubmits the query themselves.
  const [retrievalNotice, setRetrievalNotice] = useState('');
  const [sessionTypes, setSessionTypes] = useState([]);
  const [selectedSessionTypes, setSelectedSessionTypes] = useState([]);
  const [searchMode, setSearchMode] = useState('best');
  const [strictExactMatch, setStrictExactMatch] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [lastSearchMode, setLastSearchMode] = useState('best');
  const [lastQuery, setLastQuery] = useState('');
  const [highlightAliasTerms, setHighlightAliasTerms] = useState([]);
  const [lastSearchOptions, setLastSearchOptions] = useState(null);
  const [sessionsOpened, setSessionsOpened] = useState(false);
  const [qaOpened, setQaOpened] = useState(false);
  const [aboutOpened, setAboutOpened] = useState(false);
  const [sessionStatusOpened, setSessionStatusOpened] = useState(false);
  const [sessionStatusRows, setSessionStatusRows] = useState([]);
  const [loadingSessionStatus, setLoadingSessionStatus] = useState(false);
  const [sessionStatusError, setSessionStatusError] = useState('');
  const [sessionStatusGeneratedAt, setSessionStatusGeneratedAt] = useState('');
  const [sessionStatusAccordionValues, setSessionStatusAccordionValues] = useState([]);
  const [qaQuestions, setQaQuestions] = useState([]);
  const [qaCategories, setQaCategories] = useState([]);
  const [qaSortMode, setQaSortMode] = useState('type');
  const [qaFiltersOpened, setQaFiltersOpened] = useState(false);
  const [qaDateFrom, setQaDateFrom] = useState('');
  const [qaDateTo, setQaDateTo] = useState('');
  const [qaSelectedSessionTypes, setQaSelectedSessionTypes] = useState([]);
  const [qaSelectedCategories, setQaSelectedCategories] = useState([]);
  const [qaCategoryFilterMode, setQaCategoryFilterMode] = useState('any');
  const [qaFavoritesOnly, setQaFavoritesOnly] = useState(false);
  const [qaGroupAccordionValues, setQaGroupAccordionValues] = useState([]);
  const [qaAccordionValues, setQaAccordionValues] = useState([]);
  // Cache of on-demand-fetched transcript segments, keyed by question.id:
  // { [id]: { status: 'loading' | 'loaded' | 'error', segments, answerText } }
  // Populated only when a question is actually expanded -- see
  // handleQaAccordionChange below. Never reset on modal close, so
  // re-expanding a previously-viewed question doesn't re-fetch.
  const [qaSegmentsByQuestionId, setQaSegmentsByQuestionId] = useState({});
  const [loadingQaQuestions, setLoadingQaQuestions] = useState(false);
  const [qaError, setQaError] = useState('');
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [sessionsError, setSessionsError] = useState('');
  const [selectedSessions, setSelectedSessions] = useState([]);
  const [expandedSummarySessionId, setExpandedSummarySessionId] = useState(null);
  const [sessionSummaryDetails, setSessionSummaryDetails] = useState({});
  const [loadingSummarySessionId, setLoadingSummarySessionId] = useState(null);
  const [summaryDetailErrors, setSummaryDetailErrors] = useState({});
  const [sessionFiltersOpened, setSessionFiltersOpened] = useState(false);
  const [sessionFilterText, setSessionFilterText] = useState('');
  const [selectedSessionTopics, setSelectedSessionTopics] = useState([]);
  const [topicFilterMode, setTopicFilterMode] = useState('or');
  const [sessionFavoritesOnly, setSessionFavoritesOnly] = useState(false);
  const [summaryAccordionValues, setSummaryAccordionValues] = useState({});
  const [sessionTypeAccordionValues, setSessionTypeAccordionValues] = useState([]);
  const [modalTranscriptContext, setModalTranscriptContext] = useState(null);
  const [loadingModalTranscript, setLoadingModalTranscript] = useState(false);
  const [modalTranscriptError, setModalTranscriptError] = useState('');

  const contextTranscriptBoxRef = useRef(null);
  const modalTranscriptBoxRef = useRef(null);

  const sessionTopicOptions = useMemo(() => getSessionTopicOptions(sessions), [sessions]);

  const filteredSessions = useMemo(() => {
    const matchingSessions = filterSessions(
      sessions,
      sessionFilterText,
      selectedSessionTopics,
      topicFilterMode,
    );

    if (!currentUser || !sessionFavoritesOnly || loadingFavoriteStatuses) {
      return matchingSessions;
    }

    return matchingSessions.filter((session) => {
      const source = getFavoriteSourceForSession(session);

      return Boolean(
        source && favoriteStatusBySource[source.key]?.isFavorite
      );
    });
  }, [
    sessions,
    sessionFilterText,
    selectedSessionTopics,
    topicFilterMode,
    currentUser,
    sessionFavoritesOnly,
    loadingFavoriteStatuses,
    favoriteStatusBySource,
  ]);

  const groupedSessions = useMemo(
    () => groupSessionsByType(filteredSessions),
    [filteredSessions]
  );

  const visibleSessionIdsForTags = useMemo(() => {
    if (!sessionsOpened) {
      return [];
    }

    return filteredSessions
      .map((session) => Number(session?.id))
      .filter((sessionId) => Number.isInteger(sessionId) && sessionId > 0);
  }, [sessionsOpened, filteredSessions]);

  const qaCategoriesByKey = useMemo(
    () => new Map((qaCategories || []).map((category) => [category.category_key, category])),
    [qaCategories]
  );

  const qaSessionTypeOptions = useMemo(
    () => getQaSessionTypeOptions(qaQuestions),
    [qaQuestions]
  );

  const qaCategoryOptions = useMemo(
    () => getQaCategoryOptions(qaCategories),
    [qaCategories]
  );

  const filteredQaQuestions = useMemo(() => {
    const matchingQuestions = filterQaQuestions(qaQuestions, {
      dateFrom: qaDateFrom,
      dateTo: qaDateTo,
      sessionTypes: qaSelectedSessionTypes,
      categories: qaSelectedCategories,
      categoryFilterMode: qaCategoryFilterMode,
    });

    if (!currentUser || !qaFavoritesOnly || loadingFavoriteStatuses) {
      return matchingQuestions;
    }

    return matchingQuestions.filter((question) => {
      const source = getFavoriteSourceForQuestion(question);

      return Boolean(
        source && favoriteStatusBySource[source.key]?.isFavorite
      );
    });
  }, [
    qaQuestions,
    qaDateFrom,
    qaDateTo,
    qaSelectedSessionTypes,
    qaSelectedCategories,
    qaCategoryFilterMode,
    currentUser,
    qaFavoritesOnly,
    loadingFavoriteStatuses,
    favoriteStatusBySource,
  ]);

  const groupedQaQuestions = useMemo(
    () => groupQaQuestions(filteredQaQuestions, qaCategories, qaSortMode),
    [filteredQaQuestions, qaCategories, qaSortMode]
  );

  const qaActiveFilterCount = useMemo(() => {
    let count = 0;

    if (qaDateFrom) {
      count += 1;
    }

    if (qaDateTo) {
      count += 1;
    }

    if (qaSelectedSessionTypes.length > 0) {
      count += 1;
    }

    if (qaSelectedCategories.length > 0) {
      count += 1;
    }

    if (currentUser && qaFavoritesOnly) {
      count += 1;
    }

    return count;
  }, [
    qaDateFrom,
    qaDateTo,
    qaSelectedSessionTypes,
    qaSelectedCategories,
    currentUser,
    qaFavoritesOnly,
  ]);

  const selectedSessionIds = useMemo(
    () => selectedSessions.map((session) => session.id),
    [selectedSessions]
  );

  const currentUserId = currentUser?.userid || null;
  const hasSelectedSessions = selectedSessions.length > 0;

  const visibleFavoriteSources = useMemo(() => {
    const sourceByKey = new Map();

    function addSource(source) {
      if (source) {
        sourceByKey.set(source.key, source);
      }
    }

    for (const result of results) {
      addSource(getFavoriteSourceForResult(result));

      // A transcript clip and its parent session are independent favorites.
      // Load both statuses so the result can show:
      //   - the right-side star for this exact clip, and
      //   - a small title star when the whole session is saved.
      if (result?.result_kind !== 'session') {
        addSource(getFavoriteSourceForSession(result));

        for (const questionSource of getFavoriteSourcesForResultQuestions(result)) {
          addSource(questionSource);
        }
      }
    }

    // Favorites-only filters require status for the full inventory.
    // Loading only already-filtered rows would create a circular filter where
    // hidden rows never have their favorite status checked.
    if (sessionsOpened) {
      for (const session of sessions) {
        addSource(getFavoriteSourceForSession(session));
      }
    }

    if (qaOpened) {
      for (const question of qaQuestions) {
        addSource(getFavoriteSourceForQuestion(question));
      }
    }

    return Array.from(sourceByKey.values());
  }, [
    results,
    sessionsOpened,
    sessions,
    qaOpened,
    qaQuestions,
  ]);


  const visibleNoteSources = useMemo(() => {
    const sourceByKey = new Map();

    function addSource(source) {
      if (source) {
        sourceByKey.set(source.key, source);
      }
    }

    // Search result notes are deliberately tied to the class session rather
    // than to an arbitrary transcript clip. Classified questions keep their
    // own independent notes in Browse Questions and session summary details.
    for (const result of results) {
      addSource(getNoteSourceForSession(result));
    }

    if (sessionsOpened) {
      for (const session of sessions) {
        addSource(getNoteSourceForSession(session));

        const summaryItems = sessionSummaryDetails[session.id]?.items || [];

        for (const item of summaryItems) {
          if (item?.item_type !== 'question') {
            continue;
          }

          addSource(getNoteSourceForQuestion({
            ...item,
            session_id: session.id,
            session_title: session.title,
            session_date: session.session_date,
            session_type: session.session_type,
          }));
        }
      }
    }

    if (qaOpened) {
      for (const question of qaQuestions) {
        addSource(getNoteSourceForQuestion(question));
      }
    }

    return Array.from(sourceByKey.values());
  }, [
    results,
    sessionsOpened,
    sessions,
    sessionSummaryDetails,
    qaOpened,
    qaQuestions,
  ]);

  const visibleTagSources = useMemo(() => {
    const sourceByKey = new Map();

    function addSource(source) {
      if (source) {
        sourceByKey.set(source.key, source);
      }
    }

    // Search-result tags attach to the exact result: a whole session result or
    // the transcript clip that matched the search.
    for (const result of results) {
      addSource(getFavoriteSourceForResult(result));
    }

    if (sessionsOpened) {
      for (const session of sessions) {
        addSource(getFavoriteSourceForSession(session));

        const summaryItems = sessionSummaryDetails[session.id]?.items || [];
        for (const item of summaryItems) {
          if (item?.item_type === 'question') {
            addSource(getFavoriteSourceForQuestion(item));
          }
        }
      }
    }

    if (qaOpened) {
      for (const question of qaQuestions) {
        addSource(getFavoriteSourceForQuestion(question));
      }
    }

    return Array.from(sourceByKey.values());
  }, [
    results,
    sessionsOpened,
    sessions,
    sessionSummaryDetails,
    qaOpened,
    qaQuestions,
  ]);


  // Maps chunk_id -> citation number ([1], [2]...) for chunks that the
  // Answer panel actually cited, using the exact same numbering function it
  // uses to render its own pills -- so a "Cited [1]" badge on a result card
  // always matches the number shown in the Answer text above it. Empty
  // whenever there's no grounded answer to compare against.
  const citedChunkNumbers = useMemo(() => {
    if (!answerResult || !answerResult.grounded || !answerResult.answer) {
      return new Map();
    }

    const { citationOrder } = parseAnswerCitationParts(answerResult.answer);
    const map = new Map();

    citationOrder.forEach((chunkId, index) => {
      map.set(chunkId, index + 1);
    });

    return map;
  }, [answerResult]);

  // Decorates each result with its citation info (reusing citedChunkNumbers
  // so the numbers always match what's shown in the Answer panel), then
  // moves anything cited to the top of the list -- ordered by relevance
  // score, best to worst, NOT by citation number. Citation badges still
  // show the number matching the Answer text (unchanged); only the list
  // position is rank-based now. Everything uncited keeps its original
  // relative order below. A session result counts as cited if any of its
  // top_chunks are cited, ranked by the strongest (highest-scoring) cited
  // chunk within it.
  const orderedResults = useMemo(() => {
    const decorated = results.map((result) => {
      if (result.result_kind === 'session') {
        const topChunks = Array.isArray(result.top_chunks) ? result.top_chunks : [];
        const citedChunks = topChunks.filter((chunk) => citedChunkNumbers.has(chunk.chunk_id));
        const citedNumbersForSession = citedChunks.map((chunk) => citedChunkNumbers.get(chunk.chunk_id));
        const bestCitedScore = citedChunks.length > 0
          ? Math.max(...citedChunks.map((chunk) => Number(chunk.combined_score) || 0))
          : null;

        return {
          result,
          citedNumbersForSession,
          citationNumber: null,
          isCited: citedNumbersForSession.length > 0,
          rankScore: bestCitedScore,
        };
      }

      const citationNumber = citedChunkNumbers.get(result.chunk_id) || null;

      return {
        result,
        citedNumbersForSession: [],
        citationNumber,
        isCited: citationNumber !== null,
        rankScore: citationNumber !== null ? Number(result.combined_score) || 0 : null,
      };
    });

    const cited = decorated
      .filter((item) => item.isCited)
      .sort((a, b) => (b.rankScore ?? 0) - (a.rankScore ?? 0));
    const uncited = decorated.filter((item) => !item.isCited);

    return [...cited, ...uncited];
  }, [results, citedChunkNumbers]);

  const aiSearchButtonLabel = getAiSearchButtonLabel(smartSearchPlan, hasSelectedSessions);
  const aiSearchTooltipTitle = getAiSearchTooltipTitle(smartSearchPlan, hasSelectedSessions);
  const aiSearchTooltipDescription = getAiSearchTooltipDescription(smartSearchPlan, hasSelectedSessions);

  const hasSessionFilters =
    Boolean(sessionFilterText.trim()) ||
    selectedSessionTopics.length > 0 ||
    Boolean(currentUserId && sessionFavoritesOnly);

  const sessionActiveFilterCount =
    (Boolean(sessionFilterText.trim()) ? 1 : 0) +
    (selectedSessionTopics.length > 0 ? 1 : 0) +
    (currentUserId && sessionFavoritesOnly ? 1 : 0);

  useEffect(() => {
    const controller = new AbortController();

    async function loadAuthState() {
      try {
        const response = await fetch('/auth/me', {
          method: 'GET',
          credentials: 'same-origin',
          cache: 'no-store',
          headers: { Accept: 'application/json' },
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Authentication status request failed (${response.status}).`);
        }

        const data = await response.json();
        setLoginEnabled(Boolean(data?.login_enabled));
        setCurrentUser(data?.authenticated ? data.user || null : null);
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Failed to load authentication status:', err);
          setLoginEnabled(false);
          setCurrentUser(null);
        }
      } finally {
        if (!controller.signal.aborted) {
          setAuthLoading(false);
        }
      }
    }

    loadAuthState();

    return () => controller.abort();
  }, []);

  useEffect(() => {
    const requestId = ++favoriteStatusRequestIdRef.current;

    if (!currentUserId || visibleFavoriteSources.length === 0) {
      setFavoriteStatusBySource({});
      setLoadingFavoriteStatuses(false);
      return undefined;
    }

    let active = true;

    setFavoriteStatusBySource({});
    setLoadingFavoriteStatuses(true);

    async function loadVisibleFavoriteStatuses() {
      try {
        const nextStatuses = {};

        for (
          let startIndex = 0;
          startIndex < visibleFavoriteSources.length;
          startIndex += FAVORITE_STATUS_BATCH_SIZE
        ) {
          const batch = visibleFavoriteSources.slice(
            startIndex,
            startIndex + FAVORITE_STATUS_BATCH_SIZE,
          );

          const data = await getFavoriteStatuses({
            items: batch.map((source) => ({
              favoriteType: source.favoriteType,
              sourceId: source.sourceId,
            })),
          });

          if (!active || requestId !== favoriteStatusRequestIdRef.current) {
            return;
          }

          for (const item of data?.items || []) {
            const sourceId = Number(item.source_id);
            const favoriteType = item.favorite_type;

            if (!favoriteType || !Number.isInteger(sourceId) || sourceId <= 0) {
              continue;
            }

            const key = makeFavoriteSourceKey(favoriteType, sourceId);
            const favoriteId = Number(item.favorite_id);

            nextStatuses[key] = {
              isFavorite: Boolean(item.is_favorite),
              favoriteId:
                Number.isInteger(favoriteId) && favoriteId > 0
                  ? favoriteId
                  : null,
            };
          }
        }

        if (active && requestId === favoriteStatusRequestIdRef.current) {
          setFavoriteStatusBySource(nextStatuses);
        }
      } catch (err) {
        // Favorite status is optional display metadata. Search and browsing
        // must remain usable even if this request fails.
        if (active && requestId === favoriteStatusRequestIdRef.current) {
          console.error('Failed to load favorite status:', err);
          setFavoriteStatusBySource({});
        }
      } finally {
        if (active && requestId === favoriteStatusRequestIdRef.current) {
          setLoadingFavoriteStatuses(false);
        }
      }
    }

    loadVisibleFavoriteStatuses();

    return () => {
      active = false;
    };
  }, [currentUserId, visibleFavoriteSources]);

  useEffect(() => {
    const requestId = ++tagStatusRequestIdRef.current;

    if (!currentUserId || visibleTagSources.length === 0) {
      setTagStatusBySource({});
      setLoadingTagStatuses(false);
      return undefined;
    }

    let active = true;
    setLoadingTagStatuses(true);

    async function loadVisibleTagStatuses() {
      try {
        const nextStatuses = {};

        for (
          let startIndex = 0;
          startIndex < visibleTagSources.length;
          startIndex += FAVORITE_STATUS_BATCH_SIZE
        ) {
          const batch = visibleTagSources.slice(
            startIndex,
            startIndex + FAVORITE_STATUS_BATCH_SIZE,
          );

          const data = await getTagStatuses({ items: batch });

          if (!active || requestId !== tagStatusRequestIdRef.current) {
            return;
          }

          for (const item of data?.items || []) {
            const sourceId = Number(item.source_id);
            const targetType = item.target_type;

            if (!targetType || !Number.isInteger(sourceId) || sourceId <= 0) {
              continue;
            }

            const key = `${targetType}:${sourceId}`;
            nextStatuses[key] = {
              tagCount: Number(item.tag_count || 0),
              tags: Array.isArray(item.tags) ? item.tags : [],
            };
          }
        }

        if (active && requestId === tagStatusRequestIdRef.current) {
          setTagStatusBySource(nextStatuses);
        }
      } catch (err) {
        if (active && requestId === tagStatusRequestIdRef.current) {
          console.error('Failed to load tag status:', err);
          setTagStatusBySource({});
        }
      } finally {
        if (active && requestId === tagStatusRequestIdRef.current) {
          setLoadingTagStatuses(false);
        }
      }
    }

    loadVisibleTagStatuses();

    return () => {
      active = false;
    };
  }, [currentUserId, visibleTagSources, tagStatusRefreshToken]);

  useEffect(() => {
    const requestId = ++sessionTagSummaryRequestIdRef.current;

    if (!currentUserId || visibleSessionIdsForTags.length === 0) {
      setSessionTagSummaryBySession({});
      setLoadingSessionTagSummaries(false);
      return undefined;
    }

    let active = true;
    setLoadingSessionTagSummaries(true);

    async function loadSessionTagSummaries() {
      try {
        const nextSummaries = {};

        for (
          let startIndex = 0;
          startIndex < visibleSessionIdsForTags.length;
          startIndex += FAVORITE_STATUS_BATCH_SIZE
        ) {
          const batch = visibleSessionIdsForTags.slice(
            startIndex,
            startIndex + FAVORITE_STATUS_BATCH_SIZE,
          );

          const data = await getSessionTagStatuses({ sessionIds: batch });

          if (!active || requestId !== sessionTagSummaryRequestIdRef.current) {
            return;
          }

          for (const item of data?.items || []) {
            const sessionId = Number(item.session_id);
            if (!Number.isInteger(sessionId) || sessionId <= 0) {
              continue;
            }

            nextSummaries[sessionId] = {
              tagCount: Number(item.tag_count || 0),
              assignmentCount: Number(item.assignment_count || 0),
              tags: Array.isArray(item.tags) ? item.tags : [],
            };
          }
        }

        if (active && requestId === sessionTagSummaryRequestIdRef.current) {
          setSessionTagSummaryBySession(nextSummaries);
        }
      } catch (err) {
        if (active && requestId === sessionTagSummaryRequestIdRef.current) {
          console.error('Failed to load session-wide tag summaries:', err);
          setSessionTagSummaryBySession({});
        }
      } finally {
        if (active && requestId === sessionTagSummaryRequestIdRef.current) {
          setLoadingSessionTagSummaries(false);
        }
      }
    }

    loadSessionTagSummaries();

    return () => {
      active = false;
    };
  }, [currentUserId, visibleSessionIdsForTags, tagStatusRefreshToken]);

  useEffect(() => {
    const requestId = ++noteStatusRequestIdRef.current;

    if (!currentUserId || visibleNoteSources.length === 0) {
      setNoteStatusBySource({});
      setLoadingNoteStatuses(false);
      return undefined;
    }

    let active = true;

    setNoteStatusBySource({});
    setLoadingNoteStatuses(true);

    async function loadVisibleNoteStatuses() {
      try {
        const nextStatuses = {};

        for (
          let startIndex = 0;
          startIndex < visibleNoteSources.length;
          startIndex += NOTE_STATUS_BATCH_SIZE
        ) {
          const batch = visibleNoteSources.slice(
            startIndex,
            startIndex + NOTE_STATUS_BATCH_SIZE,
          );

          const data = await getNoteStatuses({
            items: batch.map((source) => ({
              noteType: source.noteType,
              sourceId: source.sourceId,
            })),
          });

          if (!active || requestId !== noteStatusRequestIdRef.current) {
            return;
          }

          for (const item of data?.items || []) {
            const sourceId = Number(item.source_id);
            const noteType = item.note_type;

            if (!noteType || !Number.isInteger(sourceId) || sourceId <= 0) {
              continue;
            }

            const key = makeNoteSourceKey(noteType, sourceId);
            const noteId = Number(item.note_id);

            nextStatuses[key] = {
              hasNote: Boolean(item.has_note),
              noteId:
                Number.isInteger(noteId) && noteId > 0
                  ? noteId
                  : null,
              updatedAt: item.updated_at || null,
            };
          }
        }

        if (active && requestId === noteStatusRequestIdRef.current) {
          setNoteStatusBySource(nextStatuses);
        }
      } catch (err) {
        // Notes are an authenticated convenience feature. A status lookup
        // failure must never interfere with normal search and browsing.
        if (active && requestId === noteStatusRequestIdRef.current) {
          console.error('Failed to load note status:', err);
          setNoteStatusBySource({});
        }
      } finally {
        if (active && requestId === noteStatusRequestIdRef.current) {
          setLoadingNoteStatuses(false);
        }
      }
    }

    loadVisibleNoteStatuses();

    return () => {
      active = false;
    };
  }, [currentUserId, visibleNoteSources]);

  useEffect(() => {
    if (!myNotesOpened || !currentUserId) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      loadMyNotes({ append: false });
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [
    myNotesOpened,
    currentUserId,
    myNotesQuery,
    myNotesType,
  ]);

  useEffect(() => {
    if (!myFavoritesOpened || !currentUserId) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      loadMyFavorites({ append: false });
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [
    myFavoritesOpened,
    currentUserId,
    myFavoritesQuery,
    myFavoritesType,
  ]);

  useEffect(() => {
    if (!currentUserId) {
      setRecentSearchHistory([]);
      setRecentSearchHistoryError('');
      setLoadingRecentSearchHistory(false);
      return undefined;
    }

    let active = true;
    const requestId = ++recentSearchHistoryRequestIdRef.current;

    setLoadingRecentSearchHistory(true);
    setRecentSearchHistoryError('');

    getRecentSearchHistory({ limit: 20 })
      .then((data) => {
        if (active && requestId === recentSearchHistoryRequestIdRef.current) {
          setRecentSearchHistory(Array.isArray(data?.items) ? data.items : []);
        }
      })
      .catch((err) => {
        if (active && requestId === recentSearchHistoryRequestIdRef.current) {
          console.error('Failed to load recent search history:', err);
          setRecentSearchHistoryError(err.message || 'Unable to load recent searches.');
        }
      })
      .finally(() => {
        if (active && requestId === recentSearchHistoryRequestIdRef.current) {
          setLoadingRecentSearchHistory(false);
        }
      });

    return () => {
      active = false;
    };
  }, [currentUserId]);

  async function handleLogout() {
    if (loggingOut) {
      return;
    }

    setLoggingOut(true);

    try {
      const response = await fetch('/auth/logout', {
        method: 'POST',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) {
        throw new Error(`Sign-out request failed (${response.status}).`);
      }

      const data = await response.json();
      recentSearchHistoryRequestIdRef.current += 1;
      searchHistoryRequestIdRef.current += 1;
      myFavoritesRequestIdRef.current += 1;
      myNotesRequestIdRef.current += 1;
      activeNoteRequestIdRef.current += 1;
      noteStatusRequestIdRef.current += 1;
      favoriteStatusRequestIdRef.current += 1;
      setCurrentUser(null);

      setMyFavoritesOpened(false);
      setMyFavoritesItems([]);
      setMyFavoritesTotal(0);
      setMyFavoritesError('');
      setDeletingMyFavoriteId(null);
      setLoadingMyFavorites(false);
      setLoadingMoreMyFavorites(false);
      setMyFavoritesQuery('');
      setMyFavoritesType('all');
      setMyNotesOpened(false);
      setMyNotesItems([]);
      setMyNotesTotal(0);
      setMyNotesError('');
      setDeletingMyNoteId(null);
      setLoadingMyNotes(false);
      setLoadingMoreMyNotes(false);
      setMyNotesQuery('');
      setMyNotesType('all');
      setMyTagsOpened(false);
      setTagStatusBySource({});
      setLoadingTagStatuses(false);
      setSessionTagSummaryBySession({});
      setLoadingSessionTagSummaries(false);
      tagStatusRequestIdRef.current += 1;
      sessionTagSummaryRequestIdRef.current += 1;
      setNoteEditorOpened(false);
      setActiveNoteSource(null);
      setActiveNote(null);
      setLoadingActiveNote(false);
      setSavingActiveNote(false);
      setDeletingActiveNote(false);
      setActiveNoteError('');
      setOpeningNoteSourceKey(null);
      setNoteStatusBySource({});
      setLoadingNoteStatuses(false);
      setQaFavoritesOnly(false);
      setSessionFavoritesOnly(false);
      setFavoriteStatusBySource({});
      setFavoriteActionBySource({});
      setLoadingFavoriteStatuses(false);
      setRecentSearchHistory([]);
      setRecentSearchHistoryError('');
      setSearchHistoryOpened(false);
      setSearchHistoryItems([]);
      setSearchHistoryTotal(0);
      setSearchHistoryError('');
      setDeletingSearchHistoryId(null);
      setClearingSearchHistory(false);
      setLoginEnabled(Boolean(data?.login_enabled));
    } catch (err) {
      console.error('Failed to sign out:', err);
    } finally {
      setLoggingOut(false);
    }
  }

  async function toggleFavorite(source) {
    if (!currentUserId || !source || favoriteActionBySource[source.key]) {
      return;
    }

    const existingStatus = favoriteStatusBySource[source.key] || null;
    const isRemoving = Boolean(existingStatus?.isFavorite && existingStatus?.favoriteId);

    setFavoriteActionBySource((current) => ({
      ...current,
      [source.key]: true,
    }));

    try {
      if (isRemoving) {
        await deleteFavorite({ favoriteId: existingStatus.favoriteId });

        setFavoriteStatusBySource((current) => ({
          ...current,
          [source.key]: {
            isFavorite: false,
            favoriteId: null,
          },
        }));
      } else {
        const data = await createFavorite({
          favoriteType: source.favoriteType,
          sourceId: source.sourceId,
          sourceQuery:
            source.sourceQuery !== undefined
              ? source.sourceQuery
              : lastQuery || query.trim() || null,
        });
        const favoriteId = Number(data?.favorite?.id);

        setFavoriteStatusBySource((current) => ({
          ...current,
          [source.key]: {
            isFavorite: true,
            favoriteId: Number.isInteger(favoriteId) && favoriteId > 0 ? favoriteId : null,
          },
        }));
      }
    } catch (err) {
      console.error('Failed to update favorite:', err);
      setError(
        isRemoving
          ? 'Unable to remove this favorite. Please try again.'
          : 'Unable to save this favorite. Please try again.'
      );
    } finally {
      setFavoriteActionBySource((current) => {
        const next = { ...current };
        delete next[source.key];
        return next;
      });
    }
  }

  async function loadRecentSearchHistory() {
    if (!currentUserId) {
      recentSearchHistoryRequestIdRef.current += 1;
      setRecentSearchHistory([]);
      setRecentSearchHistoryError('');
      setLoadingRecentSearchHistory(false);
      return;
    }

    const requestId = ++recentSearchHistoryRequestIdRef.current;

    setLoadingRecentSearchHistory(true);
    setRecentSearchHistoryError('');

    try {
      const data = await getRecentSearchHistory({ limit: 20 });

      if (requestId === recentSearchHistoryRequestIdRef.current) {
        setRecentSearchHistory(Array.isArray(data?.items) ? data.items : []);
      }
    } catch (err) {
      // Personal history is optional. A failure here must not interfere with
      // normal searching or anonymous use.
      if (requestId === recentSearchHistoryRequestIdRef.current) {
        console.error('Failed to load recent search history:', err);
        setRecentSearchHistoryError(err.message || 'Unable to load recent searches.');
      }
    } finally {
      if (requestId === recentSearchHistoryRequestIdRef.current) {
        setLoadingRecentSearchHistory(false);
      }
    }
  }

  async function loadFullSearchHistory({ append = false } = {}) {
    if (!currentUserId) {
      searchHistoryRequestIdRef.current += 1;
      setSearchHistoryItems([]);
      setSearchHistoryTotal(0);
      setSearchHistoryError('');
      setLoadingSearchHistory(false);
      setLoadingMoreSearchHistory(false);
      return;
    }

    const requestId = ++searchHistoryRequestIdRef.current;
    const offset = append ? searchHistoryItems.length : 0;

    if (append) {
      setLoadingMoreSearchHistory(true);
    } else {
      setLoadingSearchHistory(true);
      setSearchHistoryError('');
    }

    try {
      const data = await getSearchHistory({
        limit: SEARCH_HISTORY_PAGE_SIZE,
        offset,
      });

      if (requestId !== searchHistoryRequestIdRef.current) {
        return;
      }

      const loadedItems = Array.isArray(data?.items) ? data.items : [];

      if (append) {
        setSearchHistoryItems((previousItems) => {
          const existingIds = new Set(previousItems.map((item) => item.id));
          const newItems = loadedItems.filter((item) => !existingIds.has(item.id));
          return [...previousItems, ...newItems];
        });
      } else {
        setSearchHistoryItems(loadedItems);
      }

      setSearchHistoryTotal(Number(data?.total || 0));
    } catch (err) {
      if (requestId === searchHistoryRequestIdRef.current) {
        console.error('Failed to load full search history:', err);
        setSearchHistoryError(err.message || 'Unable to load search history.');
      }
    } finally {
      if (requestId === searchHistoryRequestIdRef.current) {
        setLoadingSearchHistory(false);
        setLoadingMoreSearchHistory(false);
      }
    }
  }

  function openSearchHistoryDialog() {
    setSearchHistoryOpened(true);
    loadFullSearchHistory();
  }

  async function loadSearchHistoryItem(historyItem) {
    await restoreSearchFromHistory(historyItem);
    setSearchHistoryOpened(false);
  }

  async function removeSearchHistoryItem(historyItem) {
    if (!historyItem?.id || deletingSearchHistoryId !== null || clearingSearchHistory) {
      return;
    }

    const confirmed = window.confirm(
      `Delete this search from your history?\n\n${historyItem.query_text || ''}`
    );

    if (!confirmed) {
      return;
    }

    setDeletingSearchHistoryId(historyItem.id);
    setSearchHistoryError('');

    try {
      await deleteSearchHistoryItem({ historyId: historyItem.id });
      setSearchHistoryItems((items) => items.filter((item) => item.id !== historyItem.id));
      setSearchHistoryTotal((total) => Math.max(0, total - 1));
      loadRecentSearchHistory();
    } catch (err) {
      console.error('Failed to delete search history item:', err);
      setSearchHistoryError(err.message || 'Unable to delete search history item.');
    } finally {
      setDeletingSearchHistoryId(null);
    }
  }

  async function removeAllSearchHistory() {
    if (clearingSearchHistory || deletingSearchHistoryId !== null) {
      return;
    }

    const confirmed = window.confirm(
      'Clear all of your TD Classes search history? This cannot be undone.'
    );

    if (!confirmed) {
      return;
    }

    setClearingSearchHistory(true);
    setSearchHistoryError('');

    try {
      await clearSearchHistory();
      searchHistoryRequestIdRef.current += 1;
      recentSearchHistoryRequestIdRef.current += 1;
      setSearchHistoryItems([]);
      setSearchHistoryTotal(0);
      setRecentSearchHistory([]);
      setRecentSearchHistoryError('');
    } catch (err) {
      console.error('Failed to clear search history:', err);
      setSearchHistoryError(err.message || 'Unable to clear search history.');
    } finally {
      setClearingSearchHistory(false);
    }
  }


  async function loadMyFavorites({ append = false } = {}) {
    if (!currentUserId) {
      myFavoritesRequestIdRef.current += 1;
      setMyFavoritesItems([]);
      setMyFavoritesTotal(0);
      setMyFavoritesError('');
      setLoadingMyFavorites(false);
      setLoadingMoreMyFavorites(false);
      return;
    }

    const requestId = ++myFavoritesRequestIdRef.current;
    const offset = append ? myFavoritesItems.length : 0;

    if (append) {
      setLoadingMoreMyFavorites(true);
    } else {
      setLoadingMyFavorites(true);
      setMyFavoritesItems([]);
      setMyFavoritesError('');
    }

    try {
      const data = await getFavorites({
        limit: MY_FAVORITES_PAGE_SIZE,
        offset,
        favoriteType: myFavoritesType,
        query: myFavoritesQuery,
      });

      if (requestId !== myFavoritesRequestIdRef.current) {
        return;
      }

      const loadedItems = Array.isArray(data?.items) ? data.items : [];

      if (append) {
        setMyFavoritesItems((previousItems) => {
          const existingIds = new Set(previousItems.map((item) => item.id));
          const newItems = loadedItems.filter((item) => !existingIds.has(item.id));

          return [...previousItems, ...newItems];
        });
      } else {
        setMyFavoritesItems(loadedItems);
      }

      setMyFavoritesTotal(Number(data?.total || 0));
    } catch (err) {
      if (requestId === myFavoritesRequestIdRef.current) {
        console.error('Failed to load favorites:', err);
        setMyFavoritesError(err.message || 'Unable to load favorites.');
      }
    } finally {
      if (requestId === myFavoritesRequestIdRef.current) {
        setLoadingMyFavorites(false);
        setLoadingMoreMyFavorites(false);
      }
    }
  }

  function openMyFavoritesDialog() {
    setMyFavoritesOpened(true);
  }

  function clearMyFavoritesFilters() {
    setMyFavoritesQuery('');
    setMyFavoritesType('all');
  }

  async function removeMyFavorite(favorite) {
    if (
      !favorite?.id
      || deletingMyFavoriteId !== null
    ) {
      return;
    }

    const confirmed = window.confirm(
      `Remove this favorite?\n\n${favorite.title_snapshot || 'Untitled favorite'}`
    );

    if (!confirmed) {
      return;
    }

    setDeletingMyFavoriteId(favorite.id);
    setMyFavoritesError('');

    try {
      await deleteFavorite({ favoriteId: favorite.id });

      setMyFavoritesItems((items) =>
        items.filter((item) => item.id !== favorite.id)
      );

      setMyFavoritesTotal((total) => Math.max(0, total - 1));

      if (favorite.source_key) {
        setFavoriteStatusBySource((current) => ({
          ...current,
          [favorite.source_key]: {
            isFavorite: false,
            favoriteId: null,
          },
        }));
      }
    } catch (err) {
      console.error('Failed to remove favorite:', err);
      setMyFavoritesError(err.message || 'Unable to remove favorite.');
    } finally {
      setDeletingMyFavoriteId(null);
    }
  }

  async function openMyFavoriteSource(favorite) {
    if (!canOpenFavoriteSource(favorite)) {
      return;
    }

    if (favorite.favorite_type === 'session') {
      setMyFavoritesOpened(false);

      setSessionFilterText(String(favorite.session_id));
      setSelectedSessionTopics([]);
      setTopicFilterMode('or');
      setSessionFavoritesOnly(false);
      setSessionFiltersOpened(true);
      setExpandedSummarySessionId(null);
      setSummaryAccordionValues({});

      setSessionTypeAccordionValues(
        favorite.session_type_snapshot
          ? [String(favorite.session_type_snapshot)]
          : []
      );

      openSessionsDialog();
      return;
    }

    const sourceItem = {
      session_id: Number(favorite.session_id),
      session_title: favorite.session_title_snapshot,
      session_type: favorite.session_type_snapshot,
      session_date: favorite.session_date_snapshot,
      start_seconds: Number(favorite.start_seconds),
      end_seconds:
        favorite.end_seconds !== null && favorite.end_seconds !== undefined
          ? Number(favorite.end_seconds)
          : null,
      start_time: formatOptionalTimestamp(favorite.start_seconds),
      end_time: formatOptionalTimestamp(favorite.end_seconds),
      title: favorite.title_snapshot,
      snippet: favorite.snippet_snapshot,
    };

    if (favorite.favorite_type === 'clip') {
      sourceItem.chunk_id = Number(favorite.chunk_id);
    } else {
      sourceItem.item_type = 'question';
      sourceItem.summary_item_id = Number(favorite.summary_item_id);
      sourceItem.summary_text = favorite.snippet_snapshot;
    }

    setMyFavoritesOpened(false);

    await openContext(sourceItem, {
      highlightQuery: '',
      highlightTerms: [],
      highlightMode: 'best',
    });
  }

  function refreshTagStatuses() {
    setTagStatusRefreshToken((value) => value + 1);
  }

  async function openMyTagAssignment(assignment) {
    if (!assignment) {
      return;
    }

    setMyTagsOpened(false);

    await openMyFavoriteSource({
      ...assignment,
      favorite_type: assignment.target_type,
    });
  }

  async function openSessionTagAssignment(session, assignment) {
    if (!session || !assignment || !assignment.live_source_available) {
      return;
    }

    if (assignment.target_type === 'session') {
      if (expandedSummarySessionId !== session.id) {
        await toggleSessionDetails(session);
      }
      return;
    }

    if (assignment.start_seconds === null || assignment.start_seconds === undefined) {
      return;
    }

    await openSummaryItemContext(session, {
      id: assignment.summary_item_id || assignment.chunk_id || assignment.id,
      item_type: assignment.target_type,
      start_seconds: assignment.start_seconds,
      end_seconds: assignment.end_seconds,
      title: assignment.title_snapshot,
      summary_text: assignment.snippet_snapshot,
      item_text: assignment.snippet_snapshot,
    });
  }

  async function loadMyNotes({ append = false } = {}) {
    if (!currentUserId) {
      myNotesRequestIdRef.current += 1;
      setMyNotesItems([]);
      setMyNotesTotal(0);
      setMyNotesError('');
      setLoadingMyNotes(false);
      setLoadingMoreMyNotes(false);
      return;
    }

    const requestId = ++myNotesRequestIdRef.current;
    const offset = append ? myNotesItems.length : 0;

    if (append) {
      setLoadingMoreMyNotes(true);
    } else {
      setLoadingMyNotes(true);
      setMyNotesItems([]);
      setMyNotesError('');
    }

    try {
      const data = await getNotes({
        limit: MY_NOTES_PAGE_SIZE,
        offset,
        noteType: myNotesType,
        query: myNotesQuery,
      });

      if (requestId !== myNotesRequestIdRef.current) {
        return;
      }

      const loadedItems = Array.isArray(data?.items) ? data.items : [];

      if (append) {
        setMyNotesItems((previousItems) => {
          const existingIds = new Set(previousItems.map((item) => item.id));
          const newItems = loadedItems.filter((item) => !existingIds.has(item.id));

          return [...previousItems, ...newItems];
        });
      } else {
        setMyNotesItems(loadedItems);
      }

      setMyNotesTotal(Number(data?.total || 0));
    } catch (err) {
      if (requestId === myNotesRequestIdRef.current) {
        console.error('Failed to load notes:', err);
        setMyNotesError(err.message || 'Unable to load notes.');
      }
    } finally {
      if (requestId === myNotesRequestIdRef.current) {
        setLoadingMyNotes(false);
        setLoadingMoreMyNotes(false);
      }
    }
  }

  function openMyNotesDialog() {
    setMyNotesOpened(true);
  }

  function clearMyNotesFilters() {
    setMyNotesQuery('');
    setMyNotesType('all');
  }

  function closeNoteEditor() {
    if (savingActiveNote || deletingActiveNote) {
      return;
    }

    activeNoteRequestIdRef.current += 1;
    setNoteEditorOpened(false);
    setActiveNoteSource(null);
    setActiveNote(null);
    setLoadingActiveNote(false);
    setActiveNoteError('');
    setOpeningNoteSourceKey(null);
  }

  async function openNoteEditor(source, existingNote = null) {
    if (!currentUserId || !source?.key || loadingActiveNote || savingActiveNote || deletingActiveNote) {
      return;
    }

    const requestId = ++activeNoteRequestIdRef.current;

    setActiveNoteSource(source);
    setActiveNote(existingNote);
    setActiveNoteError('');
    setNoteEditorOpened(true);

    if (existingNote) {
      setLoadingActiveNote(false);
      setOpeningNoteSourceKey(null);
      return;
    }

    setLoadingActiveNote(true);
    setOpeningNoteSourceKey(source.key);

    try {
      const data = await getNoteBySource({
        noteType: source.noteType,
        sourceId: source.sourceId,
      });

      if (requestId !== activeNoteRequestIdRef.current) {
        return;
      }

      setActiveNote(data?.exists ? data.note || null : null);
    } catch (err) {
      if (requestId === activeNoteRequestIdRef.current) {
        console.error('Failed to open note:', err);
        setActiveNoteError(err.message || 'Unable to load this note.');
      }
    } finally {
      if (requestId === activeNoteRequestIdRef.current) {
        setLoadingActiveNote(false);
        setOpeningNoteSourceKey(null);
      }
    }
  }

  async function saveActiveNoteContent(noteHtml) {
    if (!activeNoteSource || savingActiveNote || deletingActiveNote) {
      return;
    }

    setSavingActiveNote(true);
    setActiveNoteError('');

    try {
      const sourceQuery = activeNote?.source_query_snapshot
        || activeNoteSource.sourceQuery
        || null;
      const data = activeNote?.id
        ? await updateNote({
            noteId: activeNote.id,
            noteHtml,
            sourceQuery,
          })
        : await saveNoteBySource({
            noteType: activeNoteSource.noteType,
            sourceId: activeNoteSource.sourceId,
            noteHtml,
            sourceQuery,
          });
      const savedNote = data?.note || null;
      const noteId = Number(savedNote?.id);

      setActiveNote(savedNote);
      setNoteStatusBySource((current) => ({
        ...current,
        [activeNoteSource.key]: {
          hasNote: true,
          noteId: Number.isInteger(noteId) && noteId > 0 ? noteId : null,
          updatedAt: savedNote?.updated_at || null,
        },
      }));

      if (savedNote) {
        setMyNotesItems((items) =>
          items.map((item) => item.id === savedNote.id ? savedNote : item)
        );
      }

      if (myNotesOpened) {
        loadMyNotes({ append: false });
      }

      setNoteEditorOpened(false);
      setActiveNoteSource(null);
      setActiveNote(null);
    } catch (err) {
      console.error('Failed to save note:', err);
      setActiveNoteError(err.message || 'Unable to save this note.');
    } finally {
      setSavingActiveNote(false);
    }
  }

  async function deleteActiveNoteContent(note) {
    if (!note?.id || savingActiveNote || deletingActiveNote) {
      return;
    }

    const confirmed = window.confirm(
      `Delete this note?\n\n${note.title_snapshot || activeNoteSource?.title || 'Untitled note'}`
    );

    if (!confirmed) {
      return;
    }

    setDeletingActiveNote(true);
    setActiveNoteError('');

    try {
      await deleteNote({ noteId: note.id });

      const sourceKey = note.source_key || activeNoteSource?.key;

      if (sourceKey) {
        setNoteStatusBySource((current) => ({
          ...current,
          [sourceKey]: {
            hasNote: false,
            noteId: null,
            updatedAt: null,
          },
        }));
      }

      setMyNotesItems((items) => items.filter((item) => item.id !== note.id));
      setMyNotesTotal((total) => Math.max(0, total - 1));
      setNoteEditorOpened(false);
      setActiveNoteSource(null);
      setActiveNote(null);
    } catch (err) {
      console.error('Failed to delete note:', err);
      setActiveNoteError(err.message || 'Unable to delete this note.');
    } finally {
      setDeletingActiveNote(false);
    }
  }

  function editMyNote(note) {
    const source = getNoteSourceFromSavedNote(note);

    if (!source) {
      setMyNotesError('Unable to identify the source for this note.');
      return;
    }

    openNoteEditor(source, note);
  }

  async function removeMyNote(note) {
    if (!note?.id || deletingMyNoteId !== null) {
      return;
    }

    const confirmed = window.confirm(
      `Delete this note?\n\n${note.title_snapshot || 'Untitled note'}`
    );

    if (!confirmed) {
      return;
    }

    setDeletingMyNoteId(note.id);
    setMyNotesError('');

    try {
      await deleteNote({ noteId: note.id });
      setMyNotesItems((items) => items.filter((item) => item.id !== note.id));
      setMyNotesTotal((total) => Math.max(0, total - 1));

      if (note.source_key) {
        setNoteStatusBySource((current) => ({
          ...current,
          [note.source_key]: {
            hasNote: false,
            noteId: null,
            updatedAt: null,
          },
        }));
      }
    } catch (err) {
      console.error('Failed to delete note:', err);
      setMyNotesError(err.message || 'Unable to delete note.');
    } finally {
      setDeletingMyNoteId(null);
    }
  }

  async function openMyNoteSource(note) {
    if (!canOpenSavedNote(note)) {
      return;
    }

    if (note.note_type === 'session') {
      setMyNotesOpened(false);

      setSessionFilterText(String(note.session_id));
      setSelectedSessionTopics([]);
      setTopicFilterMode('or');
      setSessionFavoritesOnly(false);
      setSessionFiltersOpened(true);
      setExpandedSummarySessionId(null);
      setSummaryAccordionValues({});
      setSessionTypeAccordionValues(
        note.session_type_snapshot
          ? [String(note.session_type_snapshot)]
          : []
      );

      openSessionsDialog();
      return;
    }

    const sourceItem = {
      session_id: Number(note.session_id),
      session_title: note.session_title_snapshot,
      session_type: note.session_type_snapshot,
      session_date: note.session_date_snapshot,
      start_seconds: Number(note.start_seconds),
      end_seconds:
        note.end_seconds !== null && note.end_seconds !== undefined
          ? Number(note.end_seconds)
          : null,
      start_time: formatOptionalTimestamp(note.start_seconds),
      end_time: formatOptionalTimestamp(note.end_seconds),
      title: note.title_snapshot,
      snippet: note.snippet_snapshot,
      item_type: 'question',
      summary_item_id: Number(note.summary_item_id),
      summary_text: note.snippet_snapshot,
    };

    setMyNotesOpened(false);

    await openContext(sourceItem, {
      highlightQuery: '',
      highlightTerms: [],
      highlightMode: 'best',
    });
  }

  async function restoreSearchFromHistory(historyItem) {
    const queryText = String(historyItem?.query_text || '').trim();

    if (!queryText) {
      return;
    }

    const parameters = historyItem?.search_parameters && typeof historyItem.search_parameters === 'object'
      ? historyItem.search_parameters
      : {};
    const restoredMode = HISTORY_SEARCH_MODES.has(historyItem?.search_mode)
      ? historyItem.search_mode
      : 'best';
    const restoredSessionTypes = normalizeHistoryStringList(parameters.session_type);
    const restoredSessionIds = getHistorySessionIds(parameters);

    let availableSessions = sessions;

    if (restoredSessionIds.length > 0 && availableSessions.length === 0) {
      try {
        const data = await getSessions();
        availableSessions = Array.isArray(data) ? data : [];
        setSessions(availableSessions);
      } catch (err) {
        // The saved IDs are still sufficient to rerun the search. Use simple
        // placeholders if the session catalog cannot be loaded right now.
        console.error('Failed to load sessions while restoring search history:', err);
      }
    }

    const sessionsById = new Map(
      availableSessions.map((session) => [Number(session.id), session])
    );
    const restoredSessions = restoredSessionIds.map((sessionId) => (
      sessionsById.get(sessionId) || {
        id: sessionId,
        title: `Session ${sessionId}`,
        session_type: '',
      }
    ));

    setQuery(queryText);
    setSearchMode(restoredMode);
    setStrictExactMatch(
      restoredMode === 'exact' && Boolean(parameters.strict_exact)
    );

    if (restoredSessions.length > 0) {
      setSelectedSessions(restoredSessions);
      setSelectedSessionTypes([]);
    } else {
      setSelectedSessions([]);
      setSelectedSessionTypes(restoredSessionTypes);
    }

    setResults([]);
    setContext(null);
    setError('');
    setRetrievalNotice('');
    setOpenResultKeys([]);
    invalidateLastStandardSearch();
  }

  useEffect(() => {
    if (searchMode !== 'exact' && strictExactMatch) {
      setStrictExactMatch(false);
    }
  }, [searchMode, strictExactMatch]);

  useEffect(() => {
    async function loadSessionTypes() {
      try {
        const data = await getSessionTypes();

        const values = Array.isArray(data)
          ? data
              .map((item) => {
                if (typeof item === 'string') {
                  return item;
                }

                return item.session_type || item.value || item.label;
              })
              .filter(Boolean)
          : [];

        setSessionTypes(values);
      } catch (err) {
        console.error('Failed to load session types:', err);
      }
    }

    loadSessionTypes();
  }, []);


  useEffect(() => {
    if (!context || loadingContext) {
      return undefined;
    }

    const animationFrameId = window.requestAnimationFrame(() => {
      scrollToHighlightedTranscriptLine(contextTranscriptBoxRef.current);
    });

    return () => window.cancelAnimationFrame(animationFrameId);
  }, [context, loadingContext]);

  useEffect(() => {
    if (!modalTranscriptContext || loadingModalTranscript) {
      return undefined;
    }

    const animationFrameId = window.requestAnimationFrame(() => {
      scrollToHighlightedTranscriptLine(modalTranscriptBoxRef.current);
    });

    return () => window.cancelAnimationFrame(animationFrameId);
  }, [modalTranscriptContext, loadingModalTranscript]);



  function invalidateLastStandardSearch() {
    setHasSearched(false);
    setLastQuery('');
    setHighlightAliasTerms([]);
    setLastSearchOptions(null);
    setAiSearchUsed(false);
    setSmartSearchPlan(null);
    setAnswerResult(null);
    setLoadingAnswer(false);
  }

  async function refreshHighlightAliasTerms(queryText, strictExact = false) {
    if (!queryText || !queryText.trim()) {
      setHighlightAliasTerms([]);
      return;
    }

    try {
      const data = await fetchHighlightTerms({ query: queryText, strictExact });
      setHighlightAliasTerms(Array.isArray(data.terms) ? data.terms : []);
    } catch (err) {
      // Highlighting is a nice-to-have; a failure here should never
      // interrupt or degrade the actual search results.
      setHighlightAliasTerms([]);
    }
  }

  // /sessions/{id}/segments takes a center timestamp + a window size
  // (backend-enforced 30-7200 seconds), not an explicit start/end pair. All
  // three range shapes /search/intent can return reduce to a center+window,
  // EXCEPT when the true span exceeds 7200s (possible for full_transcript on
  // long sessions, e.g. ~2hr Trade Room sessions) -- in that case this
  // biases the window to start at 0 rather than truncating symmetrically,
  // so at least the beginning of the transcript is shown, but the result
  // will still be capped at 7200s rather than the true full length. Worth
  // revisiting (e.g. a dedicated full-transcript endpoint) if full_transcript
  // retrieval on long sessions becomes common.
  const SEGMENTS_MAX_WINDOW_SECONDS = 7200;
  const SEGMENTS_MIN_WINDOW_SECONDS = 30;

  function computeSegmentWindowFromRange(range) {
    const start = range.start_seconds;
    const end = range.end_seconds;
    const trueSpan = Math.max(0, end - start);

    if (trueSpan > SEGMENTS_MAX_WINDOW_SECONDS) {
      return {
        startSeconds: start + Math.floor(SEGMENTS_MAX_WINDOW_SECONDS / 2),
        windowSeconds: SEGMENTS_MAX_WINDOW_SECONDS,
      };
    }

    const windowSeconds = Math.max(SEGMENTS_MIN_WINDOW_SECONDS, trueSpan);
    const center = Math.round((start + end) / 2);

    return { startSeconds: center, windowSeconds };
  }

  async function openRetrievalSummary(sessionId) {
    setLoadingSessions(true);
    setSessionsError('');
    clearSessionFilters();

    try {
      const data = await getSessions();
      const sessionRows = Array.isArray(data) ? data : [];
      setSessions(sessionRows);
      setSessionsOpened(true);

      // toggleSessionDetails() requires a full session row (it checks
      // session.summary_id), which /search/intent's slimmer session payload
      // doesn't carry -- so look the match up from the freshly-fetched list.
      const matchedSession = sessionRows.find((row) => row.id === sessionId);

      if (matchedSession) {
        // Sessions render inside a separate, collapsed-by-default group-level
        // accordion (one group per session_type). Expanding the individual
        // session's own detail panel does nothing visible if the group
        // containing it is still collapsed -- both need to open.
        setSessionTypeAccordionValues([matchedSession.session_type]);
        await toggleSessionDetails(matchedSession);
      }
    } catch (err) {
      setSessionsError(err.message);
      setSessionsOpened(true);
    } finally {
      setLoadingSessions(false);
    }
  }

  function openRetrievalQuestionList(sessionId, sessionDate, sessionType) {
    clearModalTranscriptContext();
    setQaOpened(true);
    setQaDateFrom(sessionDate || '');
    setQaDateTo(sessionDate || '');
    setQaSelectedSessionTypes(sessionType ? [sessionType] : []);
    // Show the applied filters rather than leaving them collapsed -- the
    // user should be able to see WHY only these questions are showing, not
    // just that the list happens to be short.
    setQaFiltersOpened(true);
    // Group by session (rather than the default "by topic") and pre-expand
    // the specific session's group, so the user lands directly on "here are
    // this session's questions" instead of having to hunt across topic
    // categories for them.
    setQaSortMode('session');
    setQaGroupAccordionValues([String(sessionId)]);

    if (!loadingQaQuestions) {
      loadQaQuestions();
    }
  }

  async function openRetrievalTranscriptRange(sessionId, range) {
    setError('');
    setLoadingContext(true);

    try {
      const { startSeconds, windowSeconds } = computeSegmentWindowFromRange(range);

      const data = await getSessionSegments({
        sessionId,
        startSeconds,
        windowSeconds,
      });

      setContext(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingContext(false);
    }
  }

  // Attempts to handle `trimmedQuery` as a retrieval request for a specific
  // known session. Returns true if it was handled (caller should stop --
  // no fallback search runs, per design: ambiguous/no-match results show a
  // message and require the user to correct and resubmit). Returns false if
  // the query should proceed through the normal search flow -- either
  // because the classifier said "search", or because the intent check
  // itself failed (a failure here should never block the existing search
  // experience).
  async function tryHandleRetrievalIntent(trimmedQuery) {
    let intentData;

    try {
      intentData = await searchIntent({ query: trimmedQuery });
    } catch (err) {
      return false;
    }

    if (!intentData || intentData.status === 'search') {
      return false;
    }

    loadRecentSearchHistory();

    if (intentData.status === 'ambiguous' || intentData.status === 'no_match') {
      setRetrievalNotice(intentData.message || 'Your query could not be resolved to a specific session.');
      return true;
    }

    if (intentData.status === 'resolved') {
      const { retrieval_type: retrievalType, session, transcript_range: transcriptRange } = intentData;

      if (!session) {
        return false;
      }

      if (retrievalType === 'summary') {
        await openRetrievalSummary(session.id);
        return true;
      }

      if (retrievalType === 'question_list') {
        openRetrievalQuestionList(session.id, session.session_date, session.session_type);
        return true;
      }

      if (retrievalType === 'transcript_range' || retrievalType === 'full_transcript') {
        if (!transcriptRange) {
          setRetrievalNotice('The transcript for this session is not available.');
          return true;
        }

        await openRetrievalTranscriptRange(session.id, transcriptRange);
        return true;
      }
    }

    return false;
  }

  async function runSearch(event) {
    event.preventDefault();

    const trimmedQuery = query.trim();

    if (!trimmedQuery) {
      return;
    }

    setError('');
    setRetrievalNotice('');

    // Retrieval-intent routing only applies to the general search bar, not
    // when the user has already manually scoped their search to specific
    // sessions -- that's an unambiguous signal they want a scoped search,
    // not a fetch of some other named session.
    if (!hasSelectedSessions) {
      const handled = await tryHandleRetrievalIntent(trimmedQuery);

      if (handled) {
        return;
      }
    }

    const standardSearchOptions = {
      query: trimmedQuery,
      sessionTypes: hasSelectedSessions ? [] : selectedSessionTypes,
      sessionIds: selectedSessionIds,
      searchMode,
      strictExact: searchMode === 'exact' && strictExactMatch,
    };

    setContext(null);
    setHasSearched(true);
    setLastQuery(trimmedQuery);
    refreshHighlightAliasTerms(trimmedQuery, standardSearchOptions.strictExact);
    setLastSearchMode(searchMode);
    setLastSearchOptions(standardSearchOptions);
    setAiSearchUsed(false);
    setSmartSearchPlan(null);
    setLoadingSearch(true);
    setAnswerResult(null);
    setLoadingAnswer(false);
    setOpenResultKeys([]);

    if (answerAbortControllerRef.current) {
      answerAbortControllerRef.current.abort();
    }

    try {
      if (hasSelectedSessions || !USE_SMART_PLANNER_FOR_REGULAR_SEARCH) {
        const data = await searchCombined(standardSearchOptions);

        setResults(buildChunkDisplayResults(data.chunk_results || []));
        setSmartSearchPlan(null);
      } else {
        const data = await searchSmart({
          query: trimmedQuery,
          sessionLimit: 20,
          chunkLimit: 30,
          sessionTypes: selectedSessionTypes,
          searchMode,
          applyRecommendedReranker: false,
        });

        setSmartSearchPlan(data.plan || null);
        setResults(buildSmartDisplayResults(data));
      }

      loadRecentSearchHistory();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingSearch(false);
    }
  }


  const ANSWER_LIKELY_INTENTS = new Set(['definition', 'how_to', 'lookup']);

  // Decides whether to attempt a synthesized answer, using the REAL
  // AI-classified intent from the planner -- not a client-side guess.
  // This intent is already being computed for free as part of the
  // /search/smart + reranker call in runAiReSearch; this just reads it
  // rather than firing a separate (costly, and still imperfect) classifier
  // call ahead of time. The tradeoff: we can't know this before the AI
  // Re-Search click actually happens, so the tooltip can no longer preview
  // it in advance -- only describe that classification happens as part of
  // the click.
  function isAnswerLikelyHelpful(options, intent, hasResults) {
    // Exact-wording search is for locating a specific phrase, not asking a
    // question -- synthesizing an answer there answers a question the user
    // didn't ask.
    if (!options || (options.searchMode !== 'best' && options.searchMode !== 'conceptual')) {
      return false;
    }

    // Nothing to synthesize from.
    if (!hasResults) {
      return false;
    }

    // Note: this only screens out queries the planner itself doesn't think
    // are direct questions. It cannot predict whether the answer will
    // actually come back grounded -- we've seen high-scoring retrieval
    // (e.g. "inside day") still produce grounded: false, because the chunks
    // merely mentioned the concept rather than defining it. That distinction
    // only /search/answer itself can determine.
    return Boolean(intent) && ANSWER_LIKELY_INTENTS.has(intent);
  }

  async function runAnswerSearch(trimmedQuery, options) {
    const requestId = ++answerRequestIdRef.current;

    if (answerAbortControllerRef.current) {
      answerAbortControllerRef.current.abort();
    }

    const controller = new AbortController();
    answerAbortControllerRef.current = controller;

    setLoadingAnswer(true);

    try {
      const data = await searchAnswer({
        query: trimmedQuery,
        sessionTypes: options.sessionTypes || [],
        sessionIds: options.sessionIds || [],
        signal: controller.signal,
      });

      if (requestId !== answerRequestIdRef.current) {
        return; // A newer search has already started; discard this result.
      }

      // Store the result whenever a check actually completed, grounded or
      // not -- the UI needs to distinguish "checked, found nothing" from
      // "never attempted", not collapse both into silence.
      setAnswerResult(data || null);
    } catch (err) {
      if (err.name === 'AbortError') {
        return;
      }

      if (requestId === answerRequestIdRef.current) {
        // Fail quietly here -- the regular chunk-card results are the
        // primary experience and already have their own error handling.
        // An answer-panel failure shouldn't surface a second error banner.
        setAnswerResult(null);
      }
    } finally {
      if (requestId === answerRequestIdRef.current) {
        setLoadingAnswer(false);
      }
    }
  }

  async function runAiReSearch() {
    const options = lastSearchOptions;

    if (!options || !options.query) {
      return;
    }

    setError('');
    setContext(null);
    setLoadingAiSearch(true);
    setAnswerResult(null);
    setOpenResultKeys([]);
    // Do NOT set loadingAnswer(true) here. Whether an answer will even be
    // attempted isn't known until after reranking resolves and the intent
    // classification comes back (below) -- for queries like session
    // recommendations ("which video is best..."), no answer is ever
    // attempted, and showing a spinner that then silently disappears is
    // more confusing than not showing one at all. runAnswerSearch() sets
    // loadingAnswer(true) itself, only in the branches that actually call it.

    if (answerAbortControllerRef.current) {
      answerAbortControllerRef.current.abort();
    }

    try {
      const selectedSessionSearch =
        Array.isArray(options.sessionIds) && options.sessionIds.length > 0;

      if (selectedSessionSearch) {
        const data = await searchAiRankedChunks({
          ...options,
          candidateLimit: 100,
          limit: 12,
          maxChunksPerSession: 10,
        });

        setResults(buildChunkDisplayResults(Array.isArray(data) ? data : []));

        // This path never calls the AI planner, so there's no intent
        // classification to gate on the way the unscoped branch does below.
        // The user has already signaled clear intent by picking specific
        // sessions and clicking the AI button, so attempt an answer
        // whenever the mode allows it and there's something to work with --
        // no intent check needed for this narrower, already-opted-in case.
        const hasResults = Array.isArray(data) && data.length > 0;

        if (options.searchMode !== 'exact' && hasResults) {
          runAnswerSearch(options.query, options);
        }
      } else {
        const data = await searchSmart({
          query: options.query,
          sessionLimit: 20,
          chunkLimit: 30,
          sessionTypes: options.sessionTypes || [],
          searchMode: options.searchMode || 'best',
          applyRecommendedReranker: true,
          candidateSessionLimit: 50,
          candidateChunkLimit: 100,
        });

        setSmartSearchPlan(data.plan || null);
        setResults(buildSmartDisplayResults(data));

        // Use the real AI-classified intent this call just returned --
        // already paid for as part of this same reranking request -- to
        // decide whether attempting a synthesized answer is worth it.
        const intent = data.plan && data.plan.intent;
        const hasResults =
          (Array.isArray(data.chunk_results) && data.chunk_results.length > 0) ||
          (Array.isArray(data.session_results) && data.session_results.length > 0);

        if (isAnswerLikelyHelpful(options, intent, hasResults)) {
          runAnswerSearch(options.query, options);
        }
      }

      setLastQuery(options.query);
      refreshHighlightAliasTerms(options.query);
      setLastSearchMode(options.searchMode || 'best');
      setAiSearchUsed(true);
      loadRecentSearchHistory();
    } catch (err) {
      setError(err.message);
      setLoadingAnswer(false);
    } finally {
      setLoadingAiSearch(false);
    }
  }

  async function loadSessionStatus() {
    setLoadingSessionStatus(true);
    setSessionStatusError('');

    try {
      const [pipelineStatus, sessionRows] = await Promise.all([
        getPipelineStatus(getCurrentYearPipelineRange()),
        getSessions(),
      ]);

      setSessionStatusRows(
        buildSessionStatusRows(
          pipelineStatus,
          Array.isArray(sessionRows) ? sessionRows : [],
        ),
      );
      setSessionStatusGeneratedAt(pipelineStatus.generated_at || '');
    } catch (err) {
      setSessionStatusError(err.message || 'Failed to load session status.');
    } finally {
      setLoadingSessionStatus(false);
    }
  }

  function openSessionStatusDialog() {
    clearModalTranscriptContext();
    setSessionStatusOpened(true);
    setSessionStatusAccordionValues([]);
    loadSessionStatus();
  }


  async function loadSessions() {
    setLoadingSessions(true);
    setSessionsError('');

    // Summaries may have changed since the last time Browse Sessions was opened.
    // Clear cached detail rows so View details reloads fresh data after /sessions refreshes.
    setExpandedSummarySessionId(null);
    setSessionSummaryDetails({});
    setSummaryDetailErrors({});
    setSummaryAccordionValues({});

    if (typeof setModalTranscriptContext === 'function') {
      setModalTranscriptContext(null);
    }

    if (typeof setModalTranscriptError === 'function') {
      setModalTranscriptError('');
    }

    try {
      const data = await getSessions();
      setSessions(Array.isArray(data) ? data : []);
    } catch (err) {
      setSessionsError(err.message);
    } finally {
      setLoadingSessions(false);
    }
  }

  function openSessionsDialog() {
    setSessionsOpened(true);

    if (!loadingSessions) {
      loadSessions();
    }
  }

  async function loadQaQuestions() {
    setLoadingQaQuestions(true);
    setQaError('');
    setQaGroupAccordionValues([]);
    setQaAccordionValues([]);

    try {
      const data = await getBrowseQaQuestions();
      setQaQuestions(Array.isArray(data.questions) ? data.questions : []);
      setQaCategories(Array.isArray(data.categories) ? data.categories : []);
    } catch (err) {
      setQaError(err.message);
    } finally {
      setLoadingQaQuestions(false);
    }
  }

  function openQaDialog() {
    clearModalTranscriptContext();
    setQaOpened(true);

    if (!loadingQaQuestions) {
      loadQaQuestions();
    }
  }

  function clearQaFilters() {
    setQaDateFrom('');
    setQaDateTo('');
    setQaSelectedSessionTypes([]);
    setQaSelectedCategories([]);
    setQaCategoryFilterMode('any');
    setQaFavoritesOnly(false);
    setQaGroupAccordionValues([]);
    setQaAccordionValues([]);
  }


  async function toggleSessionDetails(session) {
    const sessionId = session.id;

    if (expandedSummarySessionId === sessionId) {
      setExpandedSummarySessionId(null);
      return;
    }

    setExpandedSummarySessionId(sessionId);

    if (!session.summary_id || sessionSummaryDetails[sessionId]) {
      return;
    }

    setLoadingSummarySessionId(sessionId);
    setSummaryDetailErrors((previous) => ({
      ...previous,
      [sessionId]: '',
    }));

    try {
      const data = await getSessionSummary({ sessionId });

      setSessionSummaryDetails((previous) => ({
        ...previous,
        [sessionId]: data,
      }));
    } catch (err) {
      setSummaryDetailErrors((previous) => ({
        ...previous,
        [sessionId]: err.message,
      }));
    } finally {
      setLoadingSummarySessionId(null);
    }
  }

  function toggleSelectedSessionTopic(topic) {
    setSelectedSessionTopics((previous) => {
      if (previous.includes(topic)) {
        return previous.filter((item) => item !== topic);
      }

      return [...previous, topic];
    });
  }

  function clearSessionFilters() {
    setSessionFilterText('');
    setSelectedSessionTopics([]);
    setTopicFilterMode('or');
    setSessionFavoritesOnly(false);
  }

  function expandAllSummaryItems(sessionId) {
    const items = sessionSummaryDetails[sessionId]?.items || [];

    setSummaryAccordionValues((previous) => ({
      ...previous,
      [sessionId]: items.map((item) => String(item.id)),
    }));
  }

  function collapseAllSummaryItems(sessionId) {
    setSummaryAccordionValues((previous) => ({
      ...previous,
      [sessionId]: [],
    }));
  }

  async function copyTranscriptCitation(contextData) {
    try {
      await copyTextToClipboard(buildTranscriptCitation(contextData));
    } catch (err) {
      setError(`Could not copy citation: ${err.message}`);
    }
  }

  async function openSummaryItemContext(session, item) {
    if (item.start_seconds === null || item.start_seconds === undefined) {
      return;
    }

    setModalTranscriptError('');
    setLoadingModalTranscript(true);

    try {
      const data = await getSessionSegments({
        sessionId: session.id,
        startSeconds: item.start_seconds,
        windowSeconds: 600,
      });

      setModalTranscriptContext({
        ...data,
        sourceItem: item,
      });
    } catch (err) {
      setModalTranscriptError(err.message);
    } finally {
      setLoadingModalTranscript(false);
    }
  }

  function clearModalTranscriptContext() {
    setModalTranscriptContext(null);
    setModalTranscriptError('');
    setLoadingModalTranscript(false);
  }

  function openModalTranscriptInMainViewer() {
    if (!modalTranscriptContext) {
      return;
    }

    setContext(modalTranscriptContext);
    setSessionsOpened(false);
    clearModalTranscriptContext();
  }

  function isSessionSelectedForSearch(session) {
    return selectedSessions.some((selected) => selected.id === session.id);
  }

  function selectSessionForSearch(session) {
    setSelectedSessions((current) => {
      const alreadySelected = current.some((selected) => selected.id === session.id);

      if (alreadySelected) {
        return current.filter((selected) => selected.id !== session.id);
      }

      return [...current, session];
    });

    setSelectedSessionTypes([]);
    setResults([]);
    setContext(null);
    setError('');
    setHasSearched(false);
  }

  function removeSelectedSession(sessionId) {
    setSelectedSessions((current) => current.filter((session) => session.id !== sessionId));
    setResults([]);
    setContext(null);
    setError('');
    setHasSearched(false);
  }

  function clearSelectedSession() {
    setSelectedSessions([]);
    setResults([]);
    setContext(null);
    setError('');
    setHasSearched(false);
  }

  function clearMainPage() {
    setQuery('');
    setResults([]);
    setContext(null);
    setLoadingSearch(false);
    setLoadingContext(false);
    setError('');

    setAnswerResult(null);
    setLoadingAnswer(false);
    setOpenResultKeys([]);
    if (answerAbortControllerRef.current) {
      answerAbortControllerRef.current.abort();
    }

    setSelectedSessionTypes([]);
    setSearchMode('best');
    setHasSearched(false);
    setLastSearchMode('best');
    setLastQuery('');
    setHighlightAliasTerms([]);
    setLastSearchOptions(null);
    setSmartSearchPlan(null);

    setSelectedSessions([]);

    setSessionsOpened(false);
    setQaOpened(false);
    setSessionStatusOpened(false);
    setSessionStatusError('');

    setSessionFilterText('');
    setSelectedSessionTopics([]);
    setTopicFilterMode('or');
    setSessionFavoritesOnly(false);
    setSessionFiltersOpened(false);
    setSessionTypeAccordionValues([]);
    setExpandedSummarySessionId(null);
    setSummaryAccordionValues({});
    setSummaryDetailErrors({});

    setQaDateFrom('');
    setQaDateTo('');
    setQaSelectedSessionTypes([]);
    setQaSelectedCategories([]);
    setQaCategoryFilterMode('any');
    setQaFavoritesOnly(false);
    setQaFiltersOpened(false);
    setQaSortMode('type');
    setQaGroupAccordionValues([]);
    setQaAccordionValues([]);
    setQaError('');

    clearModalTranscriptContext();
  }

  async function openContext(
    result,
    {
      highlightQuery = lastQuery,
      highlightTerms = highlightAliasTerms,
      highlightMode = lastSearchMode,
    } = {},
  ) {
    setError('');
    setLoadingContext(true);

    try {
      const data = await getSessionSegments({
        sessionId: result.session_id,
        startSeconds: result.start_seconds,
        windowSeconds: 600,
      });

      setContext({
        ...data,
        sourceItem: result,
        highlightQuery,
        highlightTerms,
        highlightMode,
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingContext(false);
    }
  }

  // Fetches segments for ONE question, only if not already loaded/loading.
  // Only ever called for session_type === 'Q&A' questions (see
  // handleQaAccordionChange) -- other session types never had this data
  // and don't need the request at all.
  async function loadQaQuestionSegmentsIfNeeded(question) {
    const existing = qaSegmentsByQuestionId[question.id];

    if (existing && (existing.status === 'loaded' || existing.status === 'loading')) {
      return;
    }

    setQaSegmentsByQuestionId((prev) => ({
      ...prev,
      [question.id]: { status: 'loading', segments: [], answerText: '' },
    }));

    try {
      const data = await getQaQuestionSegments({ questionId: question.id });

      setQaSegmentsByQuestionId((prev) => ({
        ...prev,
        [question.id]: {
          status: 'loaded',
          segments: data.answer_segments || [],
          answerText: data.answer_text || '',
        },
      }));
    } catch (err) {
      setQaSegmentsByQuestionId((prev) => ({
        ...prev,
        [question.id]: { status: 'error', segments: [], answerText: '' },
      }));
    }
  }

  // The per-question accordion is a single shared controlled component
  // (qaAccordionValues) across every question in the modal. Wrapping its
  // onChange here -- rather than fetching in renderQaQuestion itself --
  // is deliberate: renderQaQuestion is a plain helper invoked inline in a
  // .map(), not a real component, so it can't safely use hooks. Diffing
  // old vs new open values in one place, on the actual toggle event, is
  // both correct and the only place this can safely live.
  function handleQaAccordionChange(newValues) {
    const previousValues = qaAccordionValues;
    setQaAccordionValues(newValues);

    const newlyOpened = newValues.filter((value) => !previousValues.includes(value));

    for (const value of newlyOpened) {
      const questionId = value.substring(value.lastIndexOf(':') + 1);
      const question = qaQuestions.find((q) => String(q.id) === questionId);

      if (question && question.session_type === 'Q&A') {
        loadQaQuestionSegmentsIfNeeded(question);
      }
    }
  }

  async function openQaQuestionInTranscript(question) {
    if (
      !question ||
      question.start_seconds === null ||
      question.start_seconds === undefined ||
      question.session_id === null ||
      question.session_id === undefined
    ) {
      return;
    }

    setModalTranscriptError('');
    setLoadingModalTranscript(true);

    try {
      const data = await getSessionSegments({
        sessionId: question.session_id,
        startSeconds: question.start_seconds,
        windowSeconds: 600,
      });

      setModalTranscriptContext({
        ...data,
        sourceItem: {
          item_type: 'question',
          title: getQuestionTitle(question),
          item_text: question.item_text,
          summary_text: question.summary_text,
          start_seconds: question.start_seconds,
          end_seconds: question.end_seconds,
          start_time: question.start_time,
          end_time: question.end_time,
          session_id: question.session_id,
          session_title: question.session_title,
          session_type: question.session_type,
          session_date: question.session_date,
        },
      });
    } catch (err) {
      setModalTranscriptError(err.message);
    } finally {
      setLoadingModalTranscript(false);
    }
  }

  function parseQaAnswerTimestampToSeconds(timestampText) {
    const parts = String(timestampText || '')
      .split(':')
      .map((part) => Number(part));

    if (parts.some((part) => !Number.isFinite(part))) {
      return null;
    }

    if (parts.length === 2) {
      return parts[0] * 60 + parts[1];
    }

    if (parts.length === 3) {
      return parts[0] * 3600 + parts[1] * 60 + parts[2];
    }

    return null;
  }

  function reduceQaAnswerTimestamps(answerText, minimumGapSeconds = 30) {
    const lines = String(answerText || '').split(/\r?\n/);
    let lastDisplayedTimestampSeconds = null;

    return lines
      .map((line) => {
        const match = line.match(/^\[([0-9]{1,2}:[0-9]{2}(?::[0-9]{2})?)\]\s*(.*)$/);

        if (!match) {
          return line;
        }

        const timestampSeconds = parseQaAnswerTimestampToSeconds(match[1]);

        if (timestampSeconds === null) {
          return line;
        }

        const shouldShowTimestamp =
          lastDisplayedTimestampSeconds === null ||
          timestampSeconds - lastDisplayedTimestampSeconds >= minimumGapSeconds;

        if (shouldShowTimestamp) {
          lastDisplayedTimestampSeconds = timestampSeconds;
          return line;
        }

        return match[2];
      })
      .join('\n')
      .trim();
  }

  function appendQaAnswerText(blocks, text) {
    const cleaned = String(text || '').trim();

    if (!cleaned) {
      return;
    }

    if (blocks.length === 0) {
      blocks.push({
        timestamp: '',
        text: cleaned,
      });
      return;
    }

    const current = blocks[blocks.length - 1];
    current.text = [current.text, cleaned].filter(Boolean).join(' ');
  }

  function buildQaAnswerBlocks(answerText, minimumGapSeconds = 30) {
    const lines = String(answerText || '').split(/\r?\n/);
    const blocks = [];
    let lastDisplayedTimestampSeconds = null;

    for (const line of lines) {
      const trimmedLine = String(line || '').trim();

      if (!trimmedLine) {
        continue;
      }

      const match = trimmedLine.match(/^\[([0-9]{1,2}:[0-9]{2}(?::[0-9]{2})?)\]\s*(.*)$/);

      if (!match) {
        appendQaAnswerText(blocks, trimmedLine);
        continue;
      }

      const timestampText = match[1];
      const timestampSeconds = parseQaAnswerTimestampToSeconds(timestampText);
      const lineText = match[2];

      const shouldShowTimestamp =
        timestampSeconds === null ||
        lastDisplayedTimestampSeconds === null ||
        timestampSeconds - lastDisplayedTimestampSeconds >= minimumGapSeconds;

      if (shouldShowTimestamp) {
        if (timestampSeconds !== null) {
          lastDisplayedTimestampSeconds = timestampSeconds;
        }

        blocks.push({
          timestamp: timestampText,
          text: String(lineText || '').trim(),
        });
      } else {
        appendQaAnswerText(blocks, lineText);
      }
    }

    return blocks.length > 0
      ? blocks
      : [{ timestamp: '', text: String(answerText || '').trim() }];
  }

  // Newer Q&A imports carry structured per-utterance diarization, fetched
  // on demand via GET /qa/questions/{id}/segments once a question is
  // expanded (see qaSegmentsByQuestionId below) -- they're no longer part
  // of the bulk /qa/questions payload. Older imports never have this kind
  // of segment data at all; those must keep rendering via the existing
  // flat-text buildQaAnswerBlocks() path, unchanged. This is also
  // deliberately scoped to session_type === 'Q&A' only: Browse Questions
  // also surfaces questions pulled from other session types (Accelerator,
  // Methodology, etc.) that were never diarized this way.
  function hasDiarizedAnswerTurns(question, segments) {
    if (question.session_type !== 'Q&A') {
      return false;
    }

    if (!Array.isArray(segments) || segments.length === 0) {
      return false;
    }

    return segments.some((segment) => String(segment?.speaker || '').trim());
  }

  // Merges consecutive same-speaker segments into one turn -- this is what
  // removes the redundant repeated "Instructor: ... Instructor: ..."
  // labels (the diarization pipeline splits one continuous stretch of
  // speech into multiple segments, e.g. at pauses, and each one carries its
  // own copy of the speaker label). Only merges ADJACENT same-speaker
  // segments, so a speaker who talks, goes quiet, then talks again later
  // still gets two separate turns, not one merged block.
  function buildQaAnswerTurns(segments) {
    const turns = [];

    for (const segment of segments || []) {
      const text = String(segment?.text || '').trim();

      if (!text) {
        continue;
      }

      const speaker = String(segment?.speaker || '').trim();
      const timestamp = segment.start_time || formatOptionalTimestamp(segment.start_seconds);
      const last = turns[turns.length - 1];

      if (last && last.speaker === speaker) {
        last.text = [last.text, text].filter(Boolean).join(' ');
      } else {
        turns.push({ speaker, text, timestamp });
      }
    }

    return turns;
  }

  function renderQaQuestion(question, groupKey) {
    const questionValue = `${groupKey}:${question.id}`;
    const questionTypes = getQuestionTypes(question);
    const segmentState = qaSegmentsByQuestionId[question.id];
    const fetchedAnswerText = segmentState?.status === 'loaded' ? segmentState.answerText : '';
    const useDiarizedTurns = hasDiarizedAnswerTurns(question, segmentState?.segments);
    const rawAnswerText = fetchedAnswerText || question.summary_text || 'No answer text is available for this question.';
    const answerBlocks = useDiarizedTurns
      ? buildQaAnswerTurns(segmentState.segments)
      : buildQaAnswerBlocks(rawAnswerText, 30);
    const isLoadingSegments = segmentState?.status === 'loading';
    const segmentsFailedToLoad = segmentState?.status === 'error';
    const favoriteSource = getFavoriteSourceForQuestion(question);
    const favoriteStatus = favoriteSource
      ? favoriteStatusBySource[favoriteSource.key]
      : null;
    const favoriteActionLoading = favoriteSource
      ? Boolean(favoriteActionBySource[favoriteSource.key])
      : false;
    const noteSource = getNoteSourceForQuestion(question);
    const noteStatus = noteSource
      ? noteStatusBySource[noteSource.key]
      : null;
    const tagStatus = favoriteSource
      ? tagStatusBySource[favoriteSource.key]
      : null;

    return (
      <Accordion.Item
        key={questionValue}
        value={questionValue}
        className="qa-question-favorite-item"
      >
        {currentUser && favoriteSource && (
          <div className="qa-question-tag-slot">
            <TagButton
              source={favoriteSource}
              status={tagStatus}
              checking={loadingTagStatuses && !tagStatus}
              onChanged={refreshTagStatuses}
            />
          </div>
        )}

        {currentUser && noteSource && (
          <div className="qa-question-note-slot">
            <NoteButton
              source={noteSource}
              status={noteStatus}
              checking={loadingNoteStatuses && !noteStatus}
              loading={openingNoteSourceKey === noteSource.key}
              onOpen={openNoteEditor}
            />
          </div>
        )}

        {currentUser && favoriteSource && (
          <div className="qa-question-favorite-slot">
            <FavoriteToggleButton
              source={favoriteSource}
              status={favoriteStatus}
              checking={loadingFavoriteStatuses && !favoriteStatus}
              actionLoading={favoriteActionLoading}
              onToggle={toggleFavorite}
            />
          </div>
        )}

        <Accordion.Control>
          <Group gap={8} wrap="wrap" align="center">
            <Text size="lg" fw={700}>
              {getQuestionTitle(question)}
            </Text>
            {qaSortMode !== 'session' && question.session_date && (
              <Badge className="qa-question-date-badge" size="md">
                {question.session_date}
              </Badge>
            )}
            {question.start_seconds !== null && question.start_seconds !== undefined && (
              <Badge className="qa-question-time-badge" size="md">
                {question.start_time || formatOptionalTimestamp(question.start_seconds)}
              </Badge>
            )}
          </Group>
        </Accordion.Control>

        <Accordion.Panel>
          <Stack gap="sm">
            <Group gap={6}>
              <Badge variant="light" size="md">Session {question.session_id}</Badge>
              <Badge variant="light" size="md">{question.session_type || 'Q&A'}</Badge>
              {question.session_date && <Badge variant="outline" size="md">{question.session_date}</Badge>}
              {question.start_time && <Badge variant="outline" size="md">{question.start_time}</Badge>}
            </Group>

            <Group gap={6}>
              {questionTypes.map((categoryKey) => (
                <Badge key={categoryKey} size="sm" variant="light">
                  {getCategoryDisplayName(categoryKey, qaCategoriesByKey)}
                </Badge>
              ))}
            </Group>

            <Group justify="flex-end" gap="xs">
              <QaQuestionCopyButton
                question={question}
                answerText={rawAnswerText}
                summaryText={question.summary_text}
                disabled={isLoadingSegments}
                onCopyError={setError}
              />

              {question.start_seconds !== null && question.start_seconds !== undefined && (
                <Button
                  size="xs"
                  variant="light"
                  onClick={() => openQaQuestionInTranscript(question)}
                >
                  Show transcript at {question.start_time || formatOptionalTimestamp(question.start_seconds)}
                </Button>
              )}
            </Group>

            {question.item_text && question.item_text !== question.title && (
              <Stack gap={3}>
                <Text size="sm" fw={700} c="dimmed">
                  Question text
                </Text>
                <Text size="md" c="gray.3">
                  {question.item_text}
                </Text>
              </Stack>
            )}

            <Stack gap={4}>
              <Group gap={8} align="center">
                <Text size="sm" fw={700} c="dimmed">
                  Full answer text
                </Text>
                {isLoadingSegments && <Loader size="xs" />}
                {segmentsFailedToLoad && (
                  <Text size="xs" c="red.4">
                    Could not load the full transcript -- showing the short summary instead.
                  </Text>
                )}
              </Group>
              <Paper withBorder radius="sm" p="sm" className="qa-answer-box">
                <Stack gap={0} className="qa-answer-lines">
                  {answerBlocks.map((block, index) => (
                    <div className="qa-answer-line" key={`${block.timestamp || 'line'}-${index}`}>
                      <div className="qa-answer-timestamp">
                        {block.timestamp ? `[${block.timestamp}]` : ''}
                      </div>
                      <div className="qa-answer-flow-text">
                        {block.speaker && (
                          <span
                            className={`qa-answer-speaker-label ${
                              /instructor/i.test(block.speaker)
                                ? 'qa-answer-speaker-instructor'
                                : 'qa-answer-speaker-student'
                            }`}
                          >
                            {block.speaker}:
                          </span>
                        )}
                        {block.speaker ? ' ' : ''}
                        {block.text}
                      </div>
                    </div>
                  ))}
                </Stack>
              </Paper>
            </Stack>

            {question.summary_text && fetchedAnswerText && question.summary_text !== fetchedAnswerText && (
              <Stack gap={3}>
                <Text size="sm" fw={700} c="dimmed">
                  Short AI summary
                </Text>
                <Text size="sm" c="gray.3">
                  {question.summary_text}
                </Text>
              </Stack>
            )}
          </Stack>
        </Accordion.Panel>
      </Accordion.Item>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <Group justify="space-between" align="flex-start">
          <div>
            <Title order={1}>TD Classes</Title>
            <Text size="lg" c="dimmed">
              Search Trader Dale class transcripts by topic, phrase, and timestamp.
            </Text>
            <Button
              size="compact-sm"
              variant="subtle"
              mt={4}
              px={0}
              onClick={() => setAboutOpened(true)}
            >
              <Group gap={8} align="center" wrap="nowrap">
                <span>What is TD Classes?</span>
                <span className="button-info-icon" aria-hidden="true">i</span>
              </Group>
            </Button>
          </div>

          <Group gap="xs" align="center" className="header-action-row">
            <Tooltip
              multiline
              withArrow
              w={340}
              position="top"
              openDelay={250}
              label="Browse all class sessions by session type. This opens the session catalog with summaries, topics, and timestamped lesson items."
            >
              <Button size="md" variant="light" onClick={openSessionsDialog}>
                <Group gap={8} align="center" wrap="nowrap">
                  <span>Browse Sessions</span>
                  <span className="button-info-icon" aria-hidden="true">i</span>
                </Group>
              </Button>
            </Tooltip>

            <Tooltip
              multiline
              withArrow
              w={360}
              position="top"
              openDelay={250}
              label="Browse extracted question-and-answer items from all session types. Questions can be grouped by question type or by session."
            >
              <Button size="md" variant="light" onClick={openQaDialog}>
                <Group gap={8} align="center" wrap="nowrap">
                  <span>Browse Questions</span>
                  <span className="button-info-icon" aria-hidden="true">i</span>
                </Group>
              </Button>
            </Tooltip>

            <Tooltip
              multiline
              withArrow
              w={360}
              position="top"
              openDelay={250}
              label="Show uploaded or transcribed sessions that still need pipeline work. Incomplete sessions are flagged in red."
            >
              <Button size="md" variant="light" onClick={openSessionStatusDialog}>
                <Group gap={8} align="center" wrap="nowrap">
                  <span>Session Status</span>
                  <span className="button-info-icon" aria-hidden="true">i</span>
                </Group>
              </Button>
            </Tooltip>

            {currentUser?.is_admin && <AdminToolsMenu />}

            <div className="header-auth-slot">
              <AuthControl
                authLoading={authLoading}
                loginEnabled={loginEnabled}
                currentUser={currentUser}
                loggingOut={loggingOut}
                onLogout={handleLogout}
                onOpenSearchHistory={openSearchHistoryDialog}
                onOpenFavorites={openMyFavoritesDialog}
                onOpenNotes={openMyNotesDialog}
                onOpenTags={() => setMyTagsOpened(true)}
              />
            </div>
          </Group>
        </Group>

        <MySearchesModal
          opened={searchHistoryOpened}
          onClose={() => setSearchHistoryOpened(false)}
          items={searchHistoryItems}
          total={searchHistoryTotal}
          loading={loadingSearchHistory}
          loadingMore={loadingMoreSearchHistory}
          error={searchHistoryError}
          deletingId={deletingSearchHistoryId}
          clearing={clearingSearchHistory}
          onLoad={loadSearchHistoryItem}
          onDelete={removeSearchHistoryItem}
          onClear={removeAllSearchHistory}
          onLoadMore={() => loadFullSearchHistory({ append: true })}
        />

        <MyFavoritesModal
          opened={myFavoritesOpened}
          onClose={() => setMyFavoritesOpened(false)}
          items={myFavoritesItems}
          total={myFavoritesTotal}
          loading={loadingMyFavorites}
          loadingMore={loadingMoreMyFavorites}
          error={myFavoritesError}
          deletingId={deletingMyFavoriteId}
          query={myFavoritesQuery}
          favoriteType={myFavoritesType}
          onQueryChange={setMyFavoritesQuery}
          onFavoriteTypeChange={setMyFavoritesType}
          onClearFilters={clearMyFavoritesFilters}
          onOpen={openMyFavoriteSource}
          onDelete={removeMyFavorite}
          onLoadMore={() => loadMyFavorites({ append: true })}
        />

        <MyNotesModal
          opened={myNotesOpened}
          onClose={() => setMyNotesOpened(false)}
          items={myNotesItems}
          total={myNotesTotal}
          loading={loadingMyNotes}
          loadingMore={loadingMoreMyNotes}
          error={myNotesError}
          deletingId={deletingMyNoteId}
          query={myNotesQuery}
          noteType={myNotesType}
          onQueryChange={setMyNotesQuery}
          onNoteTypeChange={setMyNotesType}
          onClearFilters={clearMyNotesFilters}
          onOpen={openMyNoteSource}
          onEdit={editMyNote}
          onDelete={removeMyNote}
          onLoadMore={() => loadMyNotes({ append: true })}
        />

        <MyTagsModal
          opened={myTagsOpened}
          onClose={() => setMyTagsOpened(false)}
          onOpenAssignment={openMyTagAssignment}
          onChanged={refreshTagStatuses}
        />

        <NoteEditorModal
          opened={noteEditorOpened}
          source={activeNoteSource}
          note={activeNote}
          loading={loadingActiveNote}
          saving={savingActiveNote}
          deleting={deletingActiveNote}
          error={activeNoteError}
          onClose={closeNoteEditor}
          onSave={saveActiveNoteContent}
          onDelete={deleteActiveNoteContent}
        />

        <form onSubmit={runSearch} style={{ width: '100%' }}>
          <Stack gap="sm" style={{ width: '100%' }}>
            <TextInput
                        		      size="md"
                        		      label={<SearchInputLabel />}
                        		      placeholder="Example: at the tag SMT"
                        		      value={query}
                        		      onChange={(event) => {
                                        setQuery(event.currentTarget.value);
                                        invalidateLastStandardSearch();
                                      }}
                                      rightSection={currentUser ? (
                                        <RecentSearchHistoryMenu
                                          items={recentSearchHistory}
                                          loading={loadingRecentSearchHistory}
                                          error={recentSearchHistoryError}
                                          disabled={loadingSearch || loadingAiSearch}
                                          onOpen={loadRecentSearchHistory}
                                          onSelect={restoreSearchFromHistory}
                                        />
                                      ) : null}
                                      rightSectionWidth={currentUser ? 44 : undefined}
                                      rightSectionPointerEvents="auto"
                        		      style={{ width: '100%' }}
                        		    />

            <div
              style={{
                display: 'flex',
                alignItems: 'flex-end',
                width: '100%',
                gap: 20,
              }}
            >
              <Group align="end" gap="md" wrap="wrap">
                <MultiSelect
                            		      size="md"
                            		      label="Session type"
                            		      placeholder={hasSelectedSessions ? 'Disabled while searching selected sessions' : 'All types'}
                            		      data={sessionTypes}
                            		      value={hasSelectedSessions ? [] : selectedSessionTypes}
                            		      onChange={(value) => {
                                            setSelectedSessionTypes(value);
                                            invalidateLastStandardSearch();
                                          }}
                            		      clearable
                            		      searchable
                            		      disabled={hasSelectedSessions}
                            		      style={{ width: 280 }}
                            		    />

                <Select
                                          size="md"
                                          label={<SearchModeLabel />}
                                          data={[
                                            { value: 'best', label: 'Best match' },
                                            { value: 'exact', label: 'Exact wording' },
                                            { value: 'conceptual', label: 'Conceptual' },
                                          ]}
                                          value={searchMode}
                                          onChange={(value) => {
                                            setSearchMode(value || 'best');
                                            invalidateLastStandardSearch();
                                          }}
                                          allowDeselect={false}
                                          style={{ width: 180 }}
                                        />

                <Checkbox
                  size="md"
                  label="Strict (no aliases)"
                  checked={strictExactMatch}
                  disabled={searchMode !== 'exact'}
                  onChange={(event) => {
                    setStrictExactMatch(event.currentTarget.checked);
                    invalidateLastStandardSearch();
                  }}
                  style={{ alignSelf: 'flex-end', marginBottom: 8 }}
                />
              </Group>

              <Group align="end" gap="md" wrap="nowrap" style={{ marginLeft: 'auto' }}>
                <Button
                                          size="md"
                                          type="submit"
                                          loading={loadingSearch}
                                          disabled={!query.trim() || loadingAiSearch}
                                        >
                            		      Search
                            		    </Button>

                <Button
                                          size="md"
                                          type="button"
                                          variant="filled"
                                          loading={loadingAiSearch}
                                          disabled={!hasSearched || !lastSearchOptions || loadingSearch || aiSearchUsed}
                                          onClick={runAiReSearch}
                                        >
                                          <Group gap={8} align="center" wrap="nowrap">
                                            <span>{aiSearchButtonLabel}</span>

                                            <Tooltip
                                              multiline
                                              withArrow
                                              w={430}
                                              position="top"
                                              openDelay={250}
                                              classNames={{ tooltip: 'search-help-tooltip' }}
                                              styles={{
                                                tooltip: {
                                                  backgroundColor: '#f3f4f6',
                                                  color: '#000000',
                                                  border: '1px solid #d1d5db',
                                                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.25)',
                                                },
                                                arrow: {
                                                  backgroundColor: '#f3f4f6',
                                                  border: '1px solid #d1d5db',
                                                },
                                              }}
                                              label={
                                                <div className="search-tooltip-content">
                                                  <div className="search-tooltip-title">{aiSearchTooltipTitle}</div>

                                                  <div>
                                                    {aiSearchTooltipDescription}
                                                  </div>

                                                  <div>
                                                    It searches more broadly than a regular search and uses AI to pick the best results.
                                                  </div>

                                                  <div>
                                                    It will also try to write a direct answer to your question, with links back to
                                                    the exact clips it came from. If your transcripts don’t clearly answer the
                                                    question, no answer will be shown — TD Classes won’t guess.
                                                  </div>

                                                  <div>
                                                    Try a regular search first. Use this when regular search doesn’t find what
                                                    you need — it takes a little longer and costs a small amount to run.
                                                  </div>
                                                </div>
                                              }
                                            >
                                              <span
                                                className="button-info-icon"
                                                aria-label="About AI Re-Search"
                                                tabIndex={0}
                                                onClick={(event) => {
                                                  event.preventDefault();
                                                  event.stopPropagation();
                                                }}
                                                onMouseDown={(event) => {
                                                  event.preventDefault();
                                                  event.stopPropagation();
                                                }}
                                              >
                                                i
                                              </span>
                                            </Tooltip>
                                          </Group>
                                        </Button>

                <Button
                                          size="md"
                                          type="button"
                                          onClick={clearMainPage}
                                          disabled={loadingSearch || loadingAiSearch}
                                        >
                                          Clear
                                        </Button>
              </Group>
            </div>
          </Stack>
        </form>

        {hasSelectedSessions && (
          <Paper withBorder radius="md" p="sm">
            <Stack gap="xs">
              <Group justify="space-between" align="center">
                <Text size="sm" fw={700}>
                  Search limited to {selectedSessions.length} session{selectedSessions.length === 1 ? '' : 's'}:
                </Text>

                <Button size="xs" variant="subtle" onClick={clearSelectedSession}>
                  Clear all
                </Button>
              </Group>

              <Group gap="xs">
                {selectedSessions.map((session) => (
                  <span className="selected-session-filter-chip" key={session.id}>
                    <span className="selected-session-filter-chip-label">
                      {session.title}
                    </span>

                    <Badge size="xs" variant="outline">
                      ID {session.id}
                    </Badge>

                    {session.session_type && (
                      <Badge size="xs" variant="light">
                        {session.session_type}
                      </Badge>
                    )}

                    <button
                      type="button"
                      className="selected-session-filter-chip-close"
                      onClick={() => removeSelectedSession(session.id)}
                      aria-label={`Remove ${session.title} from selected sessions`}
                      title="Remove selected session"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </Group>
            </Stack>
          </Paper>
        )}


        <Modal
          opened={sessionStatusOpened}
          onClose={() => setSessionStatusOpened(false)}
          title="Session Status"
          size="80%"
          centered
        >
          <Stack gap="md">
            <Group justify="space-between" align="flex-end">
              <Stack gap={3}>
                <Text size="md" fw={700}>
                  Sessions by type
                </Text>
                <Text size="sm" c="dimmed">
                  Rows shown in red are uploaded or transcribed sessions that are not complete.
                  {sessionStatusGeneratedAt ? ` Generated ${sessionStatusGeneratedAt}.` : ''}
                </Text>
              </Stack>

              <Button
                size="sm"
                variant="light"
                onClick={loadSessionStatus}
                loading={loadingSessionStatus}
              >
                Refresh
              </Button>
            </Group>

            {sessionStatusError && (
              <Alert color="red" title="Could not load session status">
                {sessionStatusError}
              </Alert>
            )}

            {loadingSessionStatus ? (
              <Group justify="center" py="xl">
                <Loader />
              </Group>
            ) : sessionStatusRows.length === 0 ? (
              <Alert color="yellow" title="No sessions found">
                No uploaded, transcribed, or imported sessions were found.
              </Alert>
            ) : (
              <Accordion
                multiple
                variant="separated"
                value={sessionStatusAccordionValues}
                onChange={setSessionStatusAccordionValues}
              >
                {groupSessionStatusRows(sessionStatusRows).map((group) => {
                  const incompleteCount = group.rows.filter((row) => !row.complete).length;

                  return (
                    <Accordion.Item key={group.sessionType} value={group.sessionType}>
                      <Accordion.Control>
                        <Group justify="space-between" wrap="wrap" pr="sm">
                          <Text fw={700}>{group.sessionType}</Text>
                          <Group gap={6}>
                            <Badge variant="light" size="md">
                              {group.rows.length} session{group.rows.length === 1 ? '' : 's'}
                            </Badge>
                            {incompleteCount > 0 && (
                              <Badge color="red" variant="light" size="md">
                                {incompleteCount} incomplete
                              </Badge>
                            )}
                          </Group>
                        </Group>
                      </Accordion.Control>

                      <Accordion.Panel>
                        <Paper withBorder radius="md" style={{ overflow: 'hidden' }}>
                          <Table striped highlightOnHover verticalSpacing="sm">
                            <Table.Thead>
                              <Table.Tr>
                                <Table.Th style={{ width: 140 }}>Date</Table.Th>
                                <Table.Th>Session</Table.Th>
                              </Table.Tr>
                            </Table.Thead>

                            <Table.Tbody>
                              {group.rows.map((row) => (
                                <Table.Tr
                                  key={row.key || makeSessionStatusKey(row)}
                                  style={{
                                    backgroundColor: row.complete ? undefined : 'rgba(250, 82, 82, 0.18)',
                                  }}
                                >
                                  <Table.Td
                                    style={{
                                      color: row.complete ? '#e2e8f0' : '#ffc9c9',
                                      fontWeight: row.complete ? 600 : 700,
                                    }}
                                  >
                                    {row.sessionDate || 'Unknown'}
                                  </Table.Td>

                                  <Table.Td>
                                    <Text
                                      fw={row.complete ? 600 : 800}
                                      c={row.complete ? 'gray.1' : 'red.1'}
                                    >
                                      {row.title}
                                    </Text>
                                  </Table.Td>
                                </Table.Tr>
                              ))}
                            </Table.Tbody>
                          </Table>
                        </Paper>
                      </Accordion.Panel>
                    </Accordion.Item>
                  );
                })}
              </Accordion>
            )}
          </Stack>
        </Modal>


        <Modal
          opened={qaOpened}
          onClose={() => {
            setQaOpened(false);
            clearModalTranscriptContext();
          }}
          title="Browse Questions"
          size="95%"
          centered
        >
          <Stack
            gap="md"
            style={
              modalTranscriptContext || loadingModalTranscript || modalTranscriptError
                ? { paddingRight: '44vw' }
                : undefined
            }
          >
            {(modalTranscriptContext || loadingModalTranscript || modalTranscriptError) && (
              <Paper
                withBorder
                shadow="lg"
                radius="md"
                p="md"
                className="qa-transcript-preview-panel"
                style={{
                  position: 'fixed',
                  top: 92,
                  right: '3vw',
                  bottom: 36,
                  width: '41vw',
                  zIndex: 1000,
                  overflow: 'hidden',
                }}
              >
                <Stack gap="sm" style={{ height: '100%' }}>
                  <Group justify="space-between" align="flex-start" wrap="nowrap">
                    <Stack gap={2} style={{ minWidth: 0 }}>
                      <Text size="lg" fw={700}>
                        Transcript preview
                      </Text>

                      {modalTranscriptContext?.session && (
                        <Text size="sm" c="dimmed" lineClamp={2}>
                          {modalTranscriptContext.session.title}
                        </Text>
                      )}

                      {modalTranscriptContext?.window_start_time && (
                        <Text size="sm" c="dimmed">
                          Displaying: {modalTranscriptContext.window_start_time}–{modalTranscriptContext.window_end_time}
                        </Text>
                      )}

                      {modalTranscriptContext && (
                        <Text size="sm" c="gray.3" lineClamp={2}>
                          {getTranscriptOpenedFromText(modalTranscriptContext)}
                        </Text>
                      )}
                    </Stack>

                    <Group gap="xs" wrap="nowrap">
                      {modalTranscriptContext && (
                        <Button
                          size="xs"
                          variant="subtle"
                          onClick={() => copyTranscriptCitation(modalTranscriptContext)}
                        >
                          Copy citation
                        </Button>
                      )}

                      <Button size="xs" variant="subtle" onClick={clearModalTranscriptContext}>
                        Close
                      </Button>
                    </Group>
                  </Group>

                  {loadingModalTranscript && (
                    <Group>
                      <Loader size="sm" />
                      <Text size="md">Loading transcript...</Text>
                    </Group>
                  )}

                  {modalTranscriptError && (
                    <Alert color="red" title="Could not load transcript">
                      {modalTranscriptError}
                    </Alert>
                  )}

                  {modalTranscriptContext?.segments && (
                    <Paper
                      ref={modalTranscriptBoxRef}
                      className="transcript-box"
                      style={{
                        flex: 1,
                        maxHeight: 'none',
                        overflowY: 'auto',
                      }}
                    >
                      {decorateTranscriptSegments(
                        modalTranscriptContext.segments,
                        modalTranscriptContext.sourceItem,
                      ).map(({ segment, isTargetSegment, showTimestamp, speaker, showSpeaker }) => (
                        <div
                          className={`transcript-line${isTargetSegment ? ' transcript-line-target' : ''}`}
                          key={segment.segment_id}
                        >
                          <Text
                            span
                            size="md"
                            c="gray.4"
                            className={`timestamp${showTimestamp ? '' : ' timestamp-hidden'}`}
                          >
                            {segment.start_time}
                          </Text>
                          {speaker && (
                            <Text
                              span
                              size="md"
                              className={`transcript-speaker-label${showSpeaker ? '' : ' transcript-speaker-label-hidden'}`}
                              aria-hidden={showSpeaker ? undefined : true}
                            >
                              {speaker}:
                            </Text>
                          )}
                          <Text span size="lg" c="gray.1">
                            {segment.text}
                          </Text>
                        </div>
                      ))}
                    </Paper>
                  )}
                </Stack>
              </Paper>
            )}
            <Group justify="space-between" align="flex-end">
              <Stack gap={3}>
                <Text size="lg" fw={700}>
                  Browse classified questions
                </Text>
                <Text size="md" c="dimmed">
                  Showing {filteredQaQuestions.length} of {qaQuestions.length} classified question{qaQuestions.length === 1 ? '' : 's'}.
                </Text>
              </Stack>

              <Group align="end">
                <SegmentedControl
                  value={qaSortMode}
                  onChange={(value) => {
                    setQaSortMode(value || 'type');
                    setQaGroupAccordionValues([]);
                    setQaAccordionValues([]);
                  }}
                  data={[
                    { value: 'type', label: 'Group by Topic' },
                    { value: 'session', label: 'Group by Session' },
                  ]}
                />

                <Button size="sm" variant="light" onClick={loadQaQuestions} loading={loadingQaQuestions}>
                  Refresh
                </Button>
              </Group>
            </Group>

            <Paper withBorder radius="md" className="qa-filter-panel">
              <button
                type="button"
                className="qa-filter-toggle"
                onClick={() => setQaFiltersOpened((opened) => !opened)}
                aria-expanded={qaFiltersOpened}
              >
                <Group justify="space-between" align="center" wrap="nowrap">
                  <Group gap="xs" align="center">
                    <span className="qa-filter-arrow">
                      {qaFiltersOpened ? '▾' : '▸'}
                    </span>
                    <Text fw={700}>Filters</Text>
                    {qaActiveFilterCount > 0 && (
                      <Badge size="sm" variant="light">
                        {qaActiveFilterCount} active
                      </Badge>
                    )}
                  </Group>

                  <Text size="sm" c="dimmed">
                    {qaFiltersOpened ? 'Hide filters' : 'Show filters'}
                  </Text>
                </Group>
              </button>

              <Collapse in={qaFiltersOpened}>
                <Stack gap="sm" p="sm" pt={0}>
                  <Group grow align="end">
                    <TextInput
                      label="Date from"
                      type="date"
                      value={qaDateFrom}
                      onChange={(event) => {
                        setQaDateFrom(event.currentTarget.value);
                        setQaGroupAccordionValues([]);
                        setQaAccordionValues([]);
                      }}
                    />

                    <TextInput
                      label="Date to"
                      type="date"
                      value={qaDateTo}
                      onChange={(event) => {
                        setQaDateTo(event.currentTarget.value);
                        setQaGroupAccordionValues([]);
                        setQaAccordionValues([]);
                      }}
                    />

                    <MultiSelect
                      label="Session type"
                      placeholder="All session types"
                      data={qaSessionTypeOptions}
                      value={qaSelectedSessionTypes}
                      onChange={(value) => {
                        setQaSelectedSessionTypes(value);
                        setQaGroupAccordionValues([]);
                        setQaAccordionValues([]);
                      }}
                      searchable
                      clearable
                    />
                  </Group>

                  <Group align="end">
                    <MultiSelect
                      label="Question categories"
                      placeholder="All categories"
                      data={qaCategoryOptions}
                      value={qaSelectedCategories}
                      onChange={(value) => {
                        setQaSelectedCategories(value);
                        setQaGroupAccordionValues([]);
                        setQaAccordionValues([]);
                      }}
                      searchable
                      clearable
                      style={{ flex: 1 }}
                    />

                    <SegmentedControl
                      value={qaCategoryFilterMode}
                      onChange={(value) => {
                        setQaCategoryFilterMode(value || 'any');
                        setQaGroupAccordionValues([]);
                        setQaAccordionValues([]);
                      }}
                      data={[
                        { value: 'any', label: 'Any category' },
                        { value: 'all', label: 'All categories' },
                      ]}
                    />

                    {currentUser && (
                      <Checkbox
                        label="Favorites only"
                        checked={qaFavoritesOnly}
                        disabled={loadingFavoriteStatuses || loadingQaQuestions}
                        onChange={(event) => {
                          setQaFavoritesOnly(event.currentTarget.checked);
                          setQaGroupAccordionValues([]);
                          setQaAccordionValues([]);
                        }}
                      />
                    )}

                    <Button variant="subtle" onClick={clearQaFilters}>
                      Clear filters
                    </Button>
                  </Group>
                </Stack>
              </Collapse>
            </Paper>

            {loadingQaQuestions && (
              <Group>
                <Loader size="sm" />
                <Text size="sm">Loading Q&A questions...</Text>
              </Group>
            )}

            {qaError && (
              <Alert color="red" title="Could not load Q&A questions">
                <Stack gap="xs">
                  <Text size="sm">{qaError}</Text>
                  <Group justify="flex-end">
                    <Button size="xs" variant="light" onClick={loadQaQuestions}>
                      Retry
                    </Button>
                  </Group>
                </Stack>
              </Alert>
            )}

            {!loadingQaQuestions && !qaError && groupedQaQuestions.length === 0 && (
              <Text size="md" c="dimmed">
                No classified questions match the current filters.
              </Text>
            )}

            {!loadingQaQuestions && !qaError && groupedQaQuestions.length > 0 && (
              <Stack gap="md" mah={650} style={{ overflowY: 'auto', paddingRight: 4 }}>
                <Accordion
                  multiple
                  variant="separated"
                  className="qa-group-accordion"
                  value={qaGroupAccordionValues}
                  onChange={setQaGroupAccordionValues}
                >
                  {groupedQaQuestions.map((group) => (
                    <Accordion.Item key={group.key} value={String(group.key)}>
                      <Accordion.Control>
                        <Group justify="space-between" align="center" wrap="nowrap">
                          <Stack gap={2}>
                            <Text size="lg" fw={700}>{group.label}</Text>
                            {group.description && (
                              <Text size="md" c="dimmed">
                                {group.description}
                              </Text>
                            )}
                          </Stack>
                          <Group gap="xs" wrap="nowrap">
                            <Badge variant="light" size="md">
                              {group.questions.length} question{group.questions.length === 1 ? '' : 's'}
                            </Badge>

                            {currentUser && !loadingFavoriteStatuses && (
                              <Badge color="yellow" variant="light" size="md">
                                Favorites: {countFavoriteItems(
                                  group.questions,
                                  getFavoriteSourceForQuestion,
                                  favoriteStatusBySource,
                                )}
                              </Badge>
                            )}
                          </Group>
                        </Group>
                      </Accordion.Control>

                      <Accordion.Panel>
                        <Accordion
                          multiple
                          variant="separated"
                          className="qa-question-accordion"
                          value={qaAccordionValues}
                          onChange={handleQaAccordionChange}
                        >
                          {group.questions.map((question) => renderQaQuestion(question, group.key))}
                        </Accordion>
                      </Accordion.Panel>
                    </Accordion.Item>
                  ))}
                </Accordion>
              </Stack>
            )}
          </Stack>
        </Modal>

        <Modal
          opened={sessionsOpened}
          onClose={() => {
            setSessionsOpened(false);
            clearModalTranscriptContext();
          }}
          title="Browse Sessions"
          size="95%"
          centered
        >
          <Stack
            gap="md"
            style={
              modalTranscriptContext || loadingModalTranscript || modalTranscriptError
                ? { paddingRight: '44vw' }
                : undefined
            }
          >
            {(modalTranscriptContext || loadingModalTranscript || modalTranscriptError) && (
              <Paper
                withBorder
                shadow="lg"
                radius="md"
                p="md"
                style={{
                  position: 'fixed',
                  top: 92,
                  right: '3vw',
                  bottom: 36,
                  width: '41vw',
                  zIndex: 1000,
                  overflow: 'hidden',
                }}
              >
                <Stack gap="sm" style={{ height: '100%' }}>
                  <Group justify="space-between" align="flex-start" wrap="nowrap">
                    <Stack gap={2} style={{ minWidth: 0 }}>
                      <Text size="lg" fw={700}>
                        Transcript preview
                      </Text>

                      {modalTranscriptContext?.session && (
                        <Text size="sm" c="dimmed" lineClamp={2}>
                          {modalTranscriptContext.session.title}
                        </Text>
                      )}

                      {modalTranscriptContext?.window_start_time && (
                        <Text size="sm" c="dimmed">
                          Displaying: {modalTranscriptContext.window_start_time}–{modalTranscriptContext.window_end_time}
                        </Text>
                      )}

                      {modalTranscriptContext && (
                        <Text size="sm" c="gray.3" lineClamp={2}>
                          {getTranscriptOpenedFromText(modalTranscriptContext)}
                        </Text>
                      )}
                    </Stack>

                    <Group gap="xs" wrap="nowrap">
                      {modalTranscriptContext && (
                        <Button
                          size="xs"
                          variant="subtle"
                          onClick={() => copyTranscriptCitation(modalTranscriptContext)}
                        >
                          Copy citation
                        </Button>
                      )}

                      {modalTranscriptContext && (
                        <Button size="xs" variant="light" onClick={openModalTranscriptInMainViewer}>
                          Open in main viewer
                        </Button>
                      )}

                      <Button size="xs" variant="subtle" onClick={clearModalTranscriptContext}>
                        Close
                      </Button>
                    </Group>
                  </Group>

                  {loadingModalTranscript && (
                    <Group>
                      <Loader size="sm" />
                      <Text size="md">Loading transcript...</Text>
                    </Group>
                  )}

                  {modalTranscriptError && (
                    <Alert color="red" title="Could not load transcript">
                      {modalTranscriptError}
                    </Alert>
                  )}

                  {modalTranscriptContext?.segments && (
                    <Paper
                      ref={modalTranscriptBoxRef}
                      className="transcript-box"
                      style={{
                        flex: 1,
                        maxHeight: 'none',
                        overflowY: 'auto',
                      }}
                    >
                      {decorateTranscriptSegments(
                        modalTranscriptContext.segments,
                        modalTranscriptContext.sourceItem,
                      ).map(({ segment, isTargetSegment, showTimestamp, speaker, showSpeaker }) => (
                        <div
                          className={`transcript-line${isTargetSegment ? ' transcript-line-target' : ''}`}
                          key={segment.segment_id}
                        >
                          <Text
                            span
                            size="md"
                            c="gray.4"
                            className={`timestamp${showTimestamp ? '' : ' timestamp-hidden'}`}
                          >
                            {segment.start_time}
                          </Text>
                          {speaker && (
                            <Text
                              span
                              size="md"
                              className={`transcript-speaker-label${showSpeaker ? '' : ' transcript-speaker-label-hidden'}`}
                              aria-hidden={showSpeaker ? undefined : true}
                            >
                              {speaker}:
                            </Text>
                          )}
                          <Text span size="lg" c="gray.1">
                            {segment.text}
                          </Text>
                        </div>
                      ))}
                    </Paper>
                  )}
                </Stack>
              </Paper>
            )}
            <Text size="sm" c="dimmed">
              Sessions are grouped by type. Accelerator sessions are sorted newest first by date.
              Methodology sessions are sorted by natural title order.
            </Text>

            {hasSelectedSessions && (
              <Paper withBorder radius="md" p="sm" className="selected-sessions-modal-panel">
                <Group justify="space-between" align="center">
                  <Stack gap={3}>
                    <Text size="sm" fw={700}>
                      Selected for search: {selectedSessions.length} session{selectedSessions.length === 1 ? '' : 's'}
                    </Text>
                    <Text size="sm" c="dimmed">
                      These selected sessions override the Session type filter on the main search page.
                    </Text>
                  </Stack>

                  <Button size="xs" variant="subtle" onClick={clearSelectedSession}>
                    Clear selected
                  </Button>
                </Group>

                <Group gap="xs" mt="xs">
                  {selectedSessions.slice(0, 6).map((session) => (
                    <Badge key={session.id} variant="light">
                      {session.title}
                    </Badge>
                  ))}

                  {selectedSessions.length > 6 && (
                    <Badge variant="outline">
                      +{selectedSessions.length - 6} more
                    </Badge>
                  )}
                </Group>
              </Paper>
            )}

            <Paper withBorder radius="md" className="qa-filter-panel">
              <button
                type="button"
                className="qa-filter-toggle"
                onClick={() => setSessionFiltersOpened((opened) => !opened)}
                aria-expanded={sessionFiltersOpened}
              >
                <Group justify="space-between" align="center" wrap="nowrap">
                  <Group gap="xs" align="center">
                    <span className="qa-filter-arrow">
                      {sessionFiltersOpened ? '▾' : '▸'}
                    </span>
                    <Text fw={700}>Filters</Text>
                    {sessionActiveFilterCount > 0 && (
                      <Badge size="sm" variant="light">
                        {sessionActiveFilterCount} active
                      </Badge>
                    )}
                  </Group>

                  <Text size="sm" c="dimmed">
                    {hasSessionFilters
                      ? `Showing ${filteredSessions.length} of ${sessions.length} · ${sessionFiltersOpened ? 'Hide filters' : 'Show filters'}`
                      : sessionFiltersOpened ? 'Hide filters' : 'Show filters'}
                  </Text>
                </Group>
              </button>

              <Collapse in={sessionFiltersOpened}>
                <Stack gap="sm" p="sm" pt={0}>
                  <TextInput
                    size="md"
                    label="Filter sessions"
                    placeholder="Search title, ID, summary, or topic..."
                    value={sessionFilterText}
                    onChange={(event) => setSessionFilterText(event.currentTarget.value)}
                  />

                  {currentUser && (
                    <Checkbox
                      label="Favorites only"
                      checked={sessionFavoritesOnly}
                      disabled={loadingFavoriteStatuses || loadingSessions}
                      onChange={(event) => {
                        setSessionFavoritesOnly(event.currentTarget.checked);
                        setSessionTypeAccordionValues([]);
                      }}
                    />
                  )}

                  {sessionTopicOptions.length > 0 && (
                    <Stack gap={6}>
                      <Group justify="space-between" align="center">
                        <Text size="sm" fw={700} c="dimmed">
                          Topic filters
                        </Text>

                        <Group gap="xs" align="center">
                          <Text size="sm" c="dimmed">
                            Match
                          </Text>

                          <SegmentedControl
                            size="xs"
                            value={topicFilterMode}
                            onChange={setTopicFilterMode}
                            data={[
                              { label: 'OR', value: 'or' },
                              { label: 'AND', value: 'and' },
                            ]}
                          />
                        </Group>
                      </Group>

                      <Group gap={6}>
                        {sessionTopicOptions.map(({ topic, count }) => {
                          const selected = selectedSessionTopics.includes(topic);

                          return (
                            <Button
                              key={topic}
                              size="xs"
                              variant={selected ? 'filled' : 'light'}
                              onClick={() => toggleSelectedSessionTopic(topic)}
                            >
                              {topic} ({count})
                            </Button>
                          );
                        })}
                      </Group>
                    </Stack>
                  )}

                  <Group justify="space-between">
                    <Text size="sm" c="dimmed">
                      Showing {filteredSessions.length} of {sessions.length} sessions
                    </Text>

                    {hasSessionFilters && (
                      <Button size="xs" variant="subtle" onClick={clearSessionFilters}>
                        Clear filters
                      </Button>
                    )}
                  </Group>
                </Stack>
              </Collapse>
            </Paper>

            {loadingSessions && (
              <Group>
                <Loader size="sm" />
                <Text size="sm">Loading sessions...</Text>
              </Group>
            )}

            {sessionsError && (
              <Alert color="red" title="Could not load sessions">
                <Stack gap="xs">
                  <Text size="sm">{sessionsError}</Text>
                  <Group justify="flex-end">
                    <Button size="xs" variant="light" onClick={loadSessions}>
                      Retry
                    </Button>
                  </Group>
                </Stack>
              </Alert>
            )}

            {!loadingSessions && !sessionsError && groupedSessions.length === 0 && (
              <Text size="md" c="dimmed">
                {hasSessionFilters
                  ? 'No sessions match the current filters.'
                  : 'No sessions found.'}
              </Text>
            )}

            {!loadingSessions && !sessionsError && groupedSessions.length > 0 && (
              <Accordion
                multiple
                variant="separated"
                value={sessionTypeAccordionValues}
                onChange={setSessionTypeAccordionValues}
              >
                {groupedSessions.map((group) => (
                  <Accordion.Item key={group.sessionType} value={group.sessionType}>
                    <Accordion.Control>
                      <Group justify="space-between" pr="md">
                        <Text fw={700}>{group.sessionType}</Text>

                        <Group gap="xs" wrap="nowrap">
                          <Badge variant="light">
                            {group.sessions.length} session{group.sessions.length === 1 ? '' : 's'}
                          </Badge>

                          {currentUser && !loadingFavoriteStatuses && (
                            <Badge color="yellow" variant="light">
                              Favorites: {countFavoriteItems(
                                group.sessions,
                                getFavoriteSourceForSession,
                                favoriteStatusBySource,
                              )}
                            </Badge>
                          )}
                        </Group>
                      </Group>
                    </Accordion.Control>

                    <Accordion.Panel>
                      <Stack gap="xs" mah={430} style={{ overflowY: 'auto' }}>
                        {group.sessions.map((session) => {
                          const selectedForSearch = isSessionSelectedForSearch(session);
                          const favoriteSource = getFavoriteSourceForSession(session);
                          const favoriteStatus = favoriteSource
                            ? favoriteStatusBySource[favoriteSource.key]
                            : null;
                          const favoriteActionLoading = favoriteSource
                            ? Boolean(favoriteActionBySource[favoriteSource.key])
                            : false;
                          const noteSource = getNoteSourceForSession(session);
                          const noteStatus = noteSource
                            ? noteStatusBySource[noteSource.key]
                            : null;
                          const tagStatus = favoriteSource
                            ? tagStatusBySource[favoriteSource.key]
                            : null;
                          const sessionTagSummary = sessionTagSummaryBySession[Number(session.id)] || null;

                          return (
                            <Paper
                              key={session.id}
                              withBorder
                              radius="md"
                              p="sm"
                              className={selectedForSearch ? 'session-card-selected' : undefined}
                            >
                            <Group justify="space-between" align="flex-start" gap="md">
                              <Stack gap={6} style={{ flex: 1 }}>
                                <Group gap="xs" align="center">
                                  <Text fw={600}>{session.title}</Text>
                                  <Badge variant="outline">ID {session.id}</Badge>
                                </Group>

                                <Group gap="xs">
                                  <Badge variant="outline">
                                    {session.session_date || 'No date'}
                                  </Badge>
                                  <Badge variant="outline">
                                    {formatDuration(session.duration_seconds)}
                                  </Badge>
                                  {session.summary_status && (
                                    <Badge
                                      variant={session.summary_status === 'complete' ? 'light' : 'outline'}
                                      color={session.summary_status === 'complete' ? 'green' : 'yellow'}
                                    >
                                      Summary {session.summary_status}
                                    </Badge>
                                  )}
                                  {Number(session.question_count || 0) > 0 && (
                                    <Badge variant="light">
                                      {Number(session.question_count)} questions
                                    </Badge>
                                  )}
                                  {Number(session.summary_item_count || 0) > 0 && (
                                    <Badge variant="light">
                                      {Number(session.summary_item_count)} items
                                    </Badge>
                                  )}
                                </Group>

                                {session.short_summary && (
                                  <Text size="md" c="gray.3" lineClamp={3}>
                                    {session.short_summary}
                                  </Text>
                                )}

                                {Array.isArray(session.topics) && session.topics.length > 0 && (
                                  <Group gap={6}>
                                    {session.topics.slice(0, 8).map((topic) => (
                                      <Badge key={topic} size="xs" variant="light">
                                        {topic}
                                      </Badge>
                                    ))}
                                  </Group>
                                )}

                                {currentUser && sessionTagSummary?.tagCount > 0 && (
                                  <SessionTagSummary
                                    summary={sessionTagSummary}
                                    sessionTitle={session.title}
                                    onOpenAssignment={(assignment) =>
                                      openSessionTagAssignment(session, assignment)
                                    }
                                  />
                                )}

                                <Collapse in={expandedSummarySessionId === session.id}>
                                  <Paper withBorder radius="sm" p="sm" mt="xs">
                                    {loadingSummarySessionId === session.id && (
                                      <Group>
                                        <Loader size="xs" />
                                        <Text size="sm">Loading summary details...</Text>
                                      </Group>
                                    )}

                                    {summaryDetailErrors[session.id] && (
                                      <Alert color="red" title="Could not load summary details">
                                        {summaryDetailErrors[session.id]}
                                      </Alert>
                                    )}

                                    {sessionSummaryDetails[session.id]?.summary && (
                                      <Stack gap="sm">
                                        <Stack gap={4}>
                                          <Text size="sm" fw={700} c="dimmed">
                                            Full summary
                                          </Text>
                                          <Text size="md" c="gray.2">
                                            {sessionSummaryDetails[session.id].summary.short_summary || session.short_summary}
                                          </Text>
                                        </Stack>

                                        {Array.isArray(sessionSummaryDetails[session.id].items) &&
                                          sessionSummaryDetails[session.id].items.length > 0 && (
                                            <Stack gap="xs">
                                              <Group justify="space-between" align="center">
                                                <Text size="sm" fw={700} c="dimmed">
                                                  {getSummaryItemsLabel(session, sessionSummaryDetails[session.id].items)}
                                                </Text>

                                                <Group gap="xs">
                                                  <Button
                                                    size="xs"
                                                    variant="subtle"
                                                    onClick={() => expandAllSummaryItems(session.id)}
                                                  >
                                                    Expand all
                                                  </Button>

                                                  <Button
                                                    size="xs"
                                                    variant="subtle"
                                                    onClick={() => collapseAllSummaryItems(session.id)}
                                                  >
                                                    Collapse all
                                                  </Button>
                                                </Group>
                                              </Group>

                                              <Accordion
                                                variant="separated"
                                                multiple
                                                value={summaryAccordionValues[session.id] || []}
                                                onChange={(value) =>
                                                  setSummaryAccordionValues((previous) => ({
                                                    ...previous,
                                                    [session.id]: value,
                                                  }))
                                                }
                                              >
                                                {sessionSummaryDetails[session.id].items.map((item) => {
                                                  const itemNoteSource = item.item_type === 'question'
                                                    ? getNoteSourceForQuestion({
                                                        ...item,
                                                        session_id: session.id,
                                                        session_title: session.title,
                                                        session_date: session.session_date,
                                                        session_type: session.session_type,
                                                      })
                                                    : null;
                                                  const itemNoteStatus = itemNoteSource
                                                    ? noteStatusBySource[itemNoteSource.key]
                                                    : null;
                                                  const itemTagSource = item.item_type === 'question'
                                                    ? getFavoriteSourceForQuestion(item)
                                                    : null;
                                                  const itemTagStatus = itemTagSource
                                                    ? tagStatusBySource[itemTagSource.key]
                                                    : null;

                                                  return (
                                                  <Accordion.Item key={item.id} value={String(item.id)}>
                                                    <Accordion.Control>
                                                      <Stack gap={5}>
                                                        <Group gap="xs">
                                                          <Badge size="xs" variant="light">
                                                            {formatItemTypeLabel(item.item_type)}
                                                          </Badge>

                                                          {item.start_seconds !== null && item.start_seconds !== undefined && (
                                                            <Badge size="xs" variant="outline">
                                                              {formatSecondsAsTimestamp(item.start_seconds)}
                                                              {item.end_seconds !== null && item.end_seconds !== undefined
                                                                ? `–${formatSecondsAsTimestamp(item.end_seconds)}`
                                                                : ''}
                                                            </Badge>
                                                          )}

                                                          {item.confidence !== null && item.confidence !== undefined && (
                                                            <Badge size="xs" variant="outline">
                                                              {item.confidence}% confidence
                                                            </Badge>
                                                          )}
                                                        </Group>

                                                        <Text size="md" fw={700}>
                                                          {item.title}
                                                        </Text>
                                                      </Stack>
                                                    </Accordion.Control>

                                                    <Accordion.Panel>
                                                      <Stack gap="xs">
                                                        {item.summary_text && (
                                                          <Text size="md" c="gray.3">
                                                            {item.summary_text}
                                                          </Text>
                                                        )}

                                                        {item.item_type === 'question' && item.item_text && item.item_text !== item.title && (
                                                          <Stack gap={3}>
                                                            <Text size="sm" fw={700} c="dimmed">
                                                              Transcript question / prompt
                                                            </Text>
                                                            <Text size="md" c="dimmed">
                                                              {item.item_text}
                                                            </Text>
                                                          </Stack>
                                                        )}

                                                        {Array.isArray(item.topics) && item.topics.length > 0 && (
                                                          <Group gap={5}>
                                                            {item.topics.slice(0, 8).map((topic) => (
                                                              <Badge key={topic} size="xs" variant="outline">
                                                                {topic}
                                                              </Badge>
                                                            ))}
                                                          </Group>
                                                        )}

                                                        {(itemNoteSource || itemTagSource || (item.start_seconds !== null && item.start_seconds !== undefined)) && (
                                                          <Group justify="flex-end">
                                                            {currentUser && itemTagSource && (
                                                              <TagButton
                                                                source={itemTagSource}
                                                                status={itemTagStatus}
                                                                checking={loadingTagStatuses && !itemTagStatus}
                                                                onChanged={refreshTagStatuses}
                                                              />
                                                            )}

                                                            {currentUser && itemNoteSource && (
                                                              <NoteButton
                                                                source={itemNoteSource}
                                                                status={itemNoteStatus}
                                                                checking={loadingNoteStatuses && !itemNoteStatus}
                                                                loading={openingNoteSourceKey === itemNoteSource.key}
                                                                onOpen={openNoteEditor}
                                                              />
                                                            )}

                                                            {item.start_seconds !== null && item.start_seconds !== undefined && (
                                                              <Button
                                                                size="xs"
                                                                variant="light"
                                                                onClick={() => openSummaryItemContext(session, item)}
                                                              >
                                                                Show transcript
                                                              </Button>
                                                            )}
                                                          </Group>
                                                        )}
                                                      </Stack>
                                                    </Accordion.Panel>
                                                  </Accordion.Item>
                                                  );
                                                })}
                                              </Accordion>
                                            </Stack>
                                          )}
                                      </Stack>
                                    )}

                                    {session.summary_id &&
                                      !loadingSummarySessionId &&
                                      !summaryDetailErrors[session.id] &&
                                      sessionSummaryDetails[session.id] &&
                                      !sessionSummaryDetails[session.id]?.summary && (
                                        <Text size="sm" c="dimmed">
                                          No summary details were found for this session.
                                        </Text>
                                      )}
                                  </Paper>
                                </Collapse>
                              </Stack>

                              <Stack gap="xs" align="flex-end">
                                {currentUser && favoriteSource && (
                                  <TagButton
                                    source={favoriteSource}
                                    status={tagStatus}
                                    checking={loadingTagStatuses && !tagStatus}
                                    onChanged={refreshTagStatuses}
                                  />
                                )}

                                {currentUser && noteSource && (
                                  <NoteButton
                                    source={noteSource}
                                    status={noteStatus}
                                    checking={loadingNoteStatuses && !noteStatus}
                                    loading={openingNoteSourceKey === noteSource.key}
                                    onOpen={openNoteEditor}
                                  />
                                )}

                                {currentUser && favoriteSource && (
                                  <FavoriteToggleButton
                                    source={favoriteSource}
                                    status={favoriteStatus}
                                    checking={loadingFavoriteStatuses && !favoriteStatus}
                                    actionLoading={favoriteActionLoading}
                                    onToggle={toggleFavorite}
                                  />
                                )}

                                {session.summary_id && session.summary_status === 'complete' ? (
                    <Button
                                    size="xs"
                                    variant="subtle"
                                    onClick={() => toggleSessionDetails(session)}
                                  >
                                    {expandedSummarySessionId === session.id ? 'Hide details' : 'View details'}
                                  </Button>
                    ) : (
                      <Badge variant="light" color="gray">
                        {session.summary_status === 'failed'
                          ? 'Summary failed'
                          : session.summary_status === 'processing' || session.summary_status === 'pending'
                            ? 'Summary pending'
                            : session.summary_status === 'stale'
                              ? 'Summary stale'
                              : 'No summary yet'}
                      </Badge>
                    )}

                                <Button
                                  size="xs"
                                  variant={selectedForSearch ? 'filled' : 'light'}
                                  onClick={() => selectSessionForSearch(session)}
                                >
                                  {selectedForSearch ? 'Remove from search' : 'Add to search'}
                                </Button>
                              </Stack>
                            </Group>
                            </Paper>
                          );
                        })}
                      </Stack>
                    </Accordion.Panel>
                  </Accordion.Item>
                ))}
              </Accordion>
            )}
          </Stack>
        </Modal>

        <Modal
          opened={aboutOpened}
          onClose={() => setAboutOpened(false)}
          title="What is TD Classes?"
          size="70%"
          centered
        >
          <Stack gap="md">
            <Text size="lg" style={{ lineHeight: 1.55, color: '#f8fafc' }}>
              TD Classes is a searchable guide to David&apos;s trading education. Browse
              Accelerator, Methodology, Q&amp;A, and Trade Room sessions, or ask questions in
              plain English. TD Classes can find the lesson you remember <strong>and</strong>
              answer questions about what David teaches, using transcript evidence, citations,
              and links to the relevant moments in the session videos.
            </Text>

            <Stack gap={5}>
              <Text size="lg" fw={700} style={{ color: '#ffffff' }}>
                Search or Research with AI?
              </Text>
              <Text size="md" style={{ lineHeight: 1.5, color: '#f8fafc' }}>
                <strong>Search</strong> — Start here. It already uses AI to understand your
                question, find relevant sessions and clips, and often provide a direct, cited
                answer. You can narrow by session type or selected sessions; use Exact Wording
                when you remember a particular phrase.
              </Text>
              <Text size="md" style={{ lineHeight: 1.5, color: '#f8fafc' }}>
                <strong>Research with AI</strong> (the Re-Search with AI option) — Searches a
                wider set of possible matches and uses additional AI analysis to evaluate
                evidence and produce a cited answer. Try it for difficult or nuanced questions,
                or when Search misses something. It takes longer and is not always better.
              </Text>
            </Stack>

            <Stack gap={5}>
              <Text size="lg" fw={700} style={{ color: '#ffffff' }}>
                Try questions like these
              </Text>
              <List size="md" spacing={3} style={{ lineHeight: 1.5, color: '#f8fafc' }}>
                <List.Item>&quot;In which session did David explain his trade playbook?&quot;</List.Item>
                <List.Item>&quot;What is an inversed fair value gap?&quot;</List.Item>
                <List.Item>&quot;How should I trade the Initial Balance?&quot;</List.Item>
                <List.Item>&quot;What did David teach about SMT at the tag?&quot;</List.Item>
              </List>
            </Stack>

            <Stack gap={5}>
              <Text size="lg" fw={700} style={{ color: '#ffffff' }}>
                Sign in with Google (optional)
              </Text>
              <Text size="md" style={{ lineHeight: 1.5, color: '#f8fafc' }}>
                Use a Gmail or other Google-managed email address to unlock these personal features:
              </Text>
              <List size="md" spacing={4} style={{ lineHeight: 1.5, color: '#f8fafc' }}>
                <List.Item>
                  <strong>My Favorites</strong> — Star useful sessions, Q&amp;A questions, and
                  transcript clips. Find, filter, and reopen them later.
                </List.Item>
                <List.Item>
                  <strong>My Searches</strong> — Automatically keep your signed-in search history.
                  Revisit or rerun earlier searches, delete individual entries, or clear the history.
                </List.Item>
                <List.Item>
                  <strong>Favorites-only browsing</strong> — Filter session and Q&amp;A catalogs
                  to show just the items you&apos;ve starred.
                </List.Item>
              </List>
              <Text size="md" style={{ lineHeight: 1.5, color: '#cbd5e1' }}>
                Find these features in your account menu. Signing in is not required to browse,
                search, or use Research with AI.
              </Text>
            </Stack>
          </Stack>
        </Modal>

        {error && (
          <Alert color="red" title="Error">
            {error}
          </Alert>
        )}

        {retrievalNotice && (
          <Alert color="yellow" title="Couldn't resolve that to a specific session" style={{ whiteSpace: 'pre-line' }}>
            {retrievalNotice}
          </Alert>
        )}

        <Group align="flex-start" grow>
          <Stack gap="sm">
            <Group justify="space-between" align="center">
              <Title order={2}>Results</Title>

              {hasSearched && !loadingSearch && (
                <Badge variant="outline">
                  Showing {results.length} result{results.length === 1 ? '' : 's'}
                </Badge>
              )}
            </Group>

            {(() => {
              // Only for plain Search results (not the AI-reranked view, which
              // already does its own relevance judgment across the candidates).
              if (!hasSearched || loadingSearch || aiSearchUsed || results.length === 0) {
                return null;
              }

              const resultsMayBeTruncated = results.length >= PLAIN_SEARCH_CHUNK_LIMIT;

              // Exact mode results are essentially binary (a chunk either clears
              // the quoted/exact phrase bar or it doesn't), so a high raw count
              // reliably means a tight cluster of equally-valid matches -- no
              // separate score-clustering check is needed for that mode.
              const manyResults =
                lastSearchMode === 'exact'
                  ? resultsMayBeTruncated
                  : countCloselyRankedResults(results) >= 5;

              if (!manyResults) {
                return null;
              }

              const warningText = resultsMayBeTruncated
                ? 'NOTE: This search returned many good results, and not all matches are shown. Consider narrowing the search, either with a Session Type filter or a more specific search phrase.'
                : 'NOTE: This search returned many good results. Consider narrowing the search, either with a Session Type filter or a more specific search phrase.';

              return (
                <Alert color="yellow">
                  {warningText}
                </Alert>
              );
            })()}

            {loadingAnswer && !answerResult && (
              <Paper withBorder radius="md" p="sm" style={{ opacity: 0.7 }}>
                <Group gap={8} align="center">
                  <Loader size="xs" />
                  <Text size="sm" c="dimmed">Checking for a direct answer…</Text>
                </Group>
              </Paper>
            )}

            <AnswerPanel
              answer={answerResult}
              onJumpToChunk={openContext}
              onCopyError={(msg) => setError(msg)}
            />

            {loadingSearch && <Loader />}

			{!loadingSearch && results.length === 0 && (
			  <Paper withBorder radius="md" p="md">
			    {!hasSearched ? (
			      <Text size="md" c="dimmed">
			        Enter a search above to find matching transcript clips.
			      </Text>
			    ) : lastSearchMode === 'exact' ? (
			      <Stack gap="xs">
			        <Text size="md" fw={700}>No exact phrase matches found.</Text>
			        <Text size="md" c="dimmed">
			          TD Classes searched for the exact phrase “{lastQuery}”. Try Best match
			          or Conceptual search if you want related discussion.
			        </Text>
			      </Stack>
			    ) : (
			      <Stack gap="xs">
			        <Text size="md" fw={700}>No results found.</Text>
			        <Text size="md" c="dimmed">
			          Try a broader query, remove a session type filter, or clear the selected session.
			        </Text>
			      </Stack>
			    )}
			  </Paper>
			)}

            <Accordion multiple value={openResultKeys} onChange={setOpenResultKeys} variant="separated">
              {orderedResults.map(({ result, citedNumbersForSession, citationNumber }, index) => {
                const favoriteSource = getFavoriteSourceForResult(result);
                const favoriteStatus = favoriteSource
                  ? favoriteStatusBySource[favoriteSource.key] || null
                  : null;
                const favoriteActionLoading = Boolean(
                  favoriteSource && favoriteActionBySource[favoriteSource.key]
                );
                const tagSource = favoriteSource
                  ? {
                      ...favoriteSource,
                      sourceQuery: lastQuery || query.trim() || null,
                    }
                  : null;
                const tagStatus = tagSource
                  ? tagStatusBySource[tagSource.key] || null
                  : null;

                const parentSessionFavoriteSource =
                  result.result_kind === 'session'
                    ? null
                    : getFavoriteSourceForSession(result);

                const parentSessionFavoriteStatus = parentSessionFavoriteSource
                  ? favoriteStatusBySource[parentSessionFavoriteSource.key] || null
                  : null;

                const parentSessionIsFavorite = Boolean(
                  parentSessionFavoriteStatus?.isFavorite
                );

                const resultQuestionFavoriteSources =
                  result.result_kind === 'session'
                    ? []
                    : getFavoriteSourcesForResultQuestions(result);

                const matchingFavoriteQuestionCount =
                  resultQuestionFavoriteSources.reduce(
                    (count, questionSource) => (
                      favoriteStatusBySource[questionSource.key]?.isFavorite
                        ? count + 1
                        : count
                    ),
                    0,
                  );

                const resultNoteSource = getNoteSourceForSession(
                  result,
                  lastQuery || query.trim() || null,
                );
                const resultNoteStatus = resultNoteSource
                  ? noteStatusBySource[resultNoteSource.key] || null
                  : null;

                if (result.result_kind === 'session') {
                  const topChunks = Array.isArray(result.top_chunks) ? result.top_chunks : [];
                  const itemKey = `session-${result.session_id}-${index}`;

                  return (
                    <Accordion.Item
                      key={itemKey}
                      value={itemKey}
                      className="search-result-accordion-item"
                    >
                      {currentUser && tagSource && (
                        <div className="search-result-tag-slot">
                          <TagButton
                            source={tagSource}
                            status={tagStatus}
                            checking={loadingTagStatuses && !tagStatus}
                            onChanged={refreshTagStatuses}
                          />
                        </div>
                      )}

                      {currentUser && resultNoteSource && (
                        <div className="search-result-note-slot">
                          <NoteButton
                            source={resultNoteSource}
                            status={resultNoteStatus}
                            checking={loadingNoteStatuses && !resultNoteStatus}
                            loading={openingNoteSourceKey === resultNoteSource.key}
                            onOpen={openNoteEditor}
                          />
                        </div>
                      )}

                      {currentUser && favoriteSource && (
                        <div className="search-result-favorite-slot">
                          <FavoriteToggleButton
                            source={favoriteSource}
                            status={favoriteStatus}
                            checking={loadingFavoriteStatuses && !favoriteStatus}
                            actionLoading={favoriteActionLoading}
                            onToggle={toggleFavorite}
                          />
                        </div>
                      )}

                      <Accordion.Control>
                        <Stack gap={4}>
                          {result.ai_session_score !== undefined && result.ai_session_score !== null && (
                            <Group gap={6} justify="flex-end">
                              <Badge color="violet" variant="filled">
                                AI {Number(result.ai_session_score || 0).toFixed(0)}
                              </Badge>
                              <Badge variant="outline">
                                original {Number(result.original_session_score || result.session_score || 0).toFixed(3)}
                              </Badge>
                            </Group>
                          )}

                          <Group justify="space-between" align="flex-start" pr="md">
                            <div>
                              <Text size="lg" fw={700}>{result.title}</Text>
                              <Group gap="xs" mt={4}>
                                <Badge variant="outline">ID {result.session_id}</Badge>
                                <Badge variant="light">{result.session_type}</Badge>
                                <Badge variant="outline">{result.session_date}</Badge>
                                <Badge variant="outline">
                                  {Number(result.matching_chunks || 0)} matching chunks
                                </Badge>
                                {citedNumbersForSession.length > 0 && (
                                  <Badge color="blue" variant="light">
                                    Cited [{citedNumbersForSession.join(', ')}]
                                  </Badge>
                                )}
                              </Group>
                            </div>

                            {(result.ai_session_score === undefined || result.ai_session_score === null) && (
                              <Badge>
                                Score {Number(result.session_score || 0).toFixed(3)}
                              </Badge>
                            )}
                          </Group>
                        </Stack>
                      </Accordion.Control>

                      <Accordion.Panel>
                        <Stack gap="xs">
                          {result.ai_session_reason && (
                            <Text size="sm" c="violet.2">
                              {result.ai_session_reason}
                            </Text>
                          )}

                          {topChunks.length > 0 && (
                            <Group justify="flex-end">
                              <Button
                                size="xs"
                                variant="light"
                                onClick={() => {
                                  const chunk = topChunks[0];
                                  const chunkResult = {
                                    ...chunk,
                                    session_id: result.session_id,
                                    session_date: result.session_date,
                                    session_type: result.session_type,
                                    title: result.title,
                                  };

                                  openContext(chunkResult);
                                }}
                              >
                                Open best matching transcript context
                              </Button>
                            </Group>
                          )}
                        </Stack>
                      </Accordion.Panel>
                    </Accordion.Item>
                  );
                }

                const itemKey = `chunk-${result.chunk_id}-${index}`;

                return (
                  <Accordion.Item
                    key={itemKey}
                    value={itemKey}
                    className="search-result-accordion-item"
                  >
                    {currentUser && tagSource && (
                      <div className="search-result-tag-slot">
                        <TagButton
                          source={tagSource}
                          status={tagStatus}
                          checking={loadingTagStatuses && !tagStatus}
                          onChanged={refreshTagStatuses}
                        />
                      </div>
                    )}

                    {currentUser && resultNoteSource && (
                      <div className="search-result-note-slot">
                        <NoteButton
                          source={resultNoteSource}
                          status={resultNoteStatus}
                          checking={loadingNoteStatuses && !resultNoteStatus}
                          loading={openingNoteSourceKey === resultNoteSource.key}
                          onOpen={openNoteEditor}
                        />
                      </div>
                    )}

                    {currentUser && favoriteSource && (
                      <div className="search-result-favorite-slot">
                        <FavoriteToggleButton
                          source={favoriteSource}
                          status={favoriteStatus}
                          checking={loadingFavoriteStatuses && !favoriteStatus}
                          actionLoading={favoriteActionLoading}
                          onToggle={toggleFavorite}
                        />
                      </div>
                    )}

                    <Accordion.Control>
                      <Stack gap={4}>
                        {result.ai_score !== undefined && result.ai_score !== null && (
                          <Group gap={6} justify="flex-end">
                            <Badge color="violet" variant="filled">
                              AI {Number(result.ai_score || 0).toFixed(0)}
                            </Badge>
                            <Badge variant="outline">
                              original {Number(result.original_combined_score || result.combined_score || 0).toFixed(3)}
                            </Badge>
                          </Group>
                        )}

                        <Group justify="space-between" align="flex-start" pr="md">
                          <div>
                            <Group gap={7} align="center" wrap="nowrap">
                              {parentSessionIsFavorite && (
                                <Tooltip
                                  label="This session is saved in your favorites"
                                  withArrow
                                  position="top"
                                  openDelay={250}
                                >
                                  <span
                                    className="parent-session-favorite-indicator"
                                    aria-label="This session is saved in your favorites"
                                  >
                                    <BookmarkIcon filled />
                                  </span>
                                </Tooltip>
                              )}

                              {matchingFavoriteQuestionCount > 0 && (
                                <Tooltip
                                  label={
                                    matchingFavoriteQuestionCount === 1
                                      ? 'A question in this session is saved in your favorites'
                                      : `${matchingFavoriteQuestionCount} questions in this session are saved in your favorites`
                                  }
                                  withArrow
                                  position="top"
                                  openDelay={250}
                                >
                                  <span
                                    className="question-favorite-indicator"
                                    aria-label="A question in this session is saved in your favorites"
                                  >
                                    <BookmarkIcon filled />
                                    <span
                                      className="question-favorite-indicator-label"
                                      aria-hidden="true"
                                    >
                                      Q
                                    </span>
                                  </span>
                                </Tooltip>
                              )}

                              <Text size="lg" fw={700}>
                                {result.title}
                              </Text>
                            </Group>

                            <Group gap="xs" mt={4}>
                              <Badge variant="outline">ID {result.session_id}</Badge>
                              <Badge variant="light">{result.session_type}</Badge>
                              <Badge variant="outline">{result.session_date}</Badge>
                              <Badge variant="outline">
                                {result.start_time}–{result.end_time}
                              </Badge>
                              {citationNumber && (
                                <Badge color="blue" variant="light">
                                  Cited [{citationNumber}]
                                </Badge>
                              )}
                            </Group>
                          </div>

                          {(result.ai_score === undefined || result.ai_score === null) && (
                            <Badge>
                              Score {Number(result.combined_score || 0).toFixed(3)}
                            </Badge>
                          )}
                        </Group>
                      </Stack>
                    </Accordion.Control>

                    <Accordion.Panel>
                      <Stack gap="xs">
                        <Text size="md" c="gray.2" lineClamp={4}>
                          {highlightQueryTerms(
                            String(result.snippet || result.display_snippet || result.chunk_text || '').replace(/\*\*/g, ''),
                            lastQuery,
                            highlightAliasTerms,
                            lastSearchMode === 'exact'
                          )}
                        </Text>

                        <Group justify="flex-end">
                          <Button
                            size="sm"
                            variant="light"
                            onClick={() => openContext(result)}
                          >
                            Open context
                          </Button>
                        </Group>
                      </Stack>
                    </Accordion.Panel>
                  </Accordion.Item>
                );
              })}
            </Accordion>          </Stack>

          <Stack gap="sm" className="context-column">
            <Title order={2}>Transcript Context</Title>

            {loadingContext && (
              <Paper withBorder radius="md" p="md">
                <Group>
                  <Loader size="sm" />
                  <Text>Loading context...</Text>
                </Group>
              </Paper>
            )}

            {!loadingContext && !context && (
              <Paper withBorder radius="md" p="md">
                <Text size="md" c="dimmed">
                  Select a result to view the surrounding transcript.
                </Text>
              </Paper>
            )}

            {!loadingContext && context && (
              <Paper withBorder radius="md" p="md" className="context-viewer-card">
                <Stack gap="sm" className="context-viewer-stack">
                  <Group justify="space-between" align="flex-start" wrap="nowrap">
                    <Stack gap={2} style={{ minWidth: 0 }}>
                      <Text size="lg" fw={700}>{context.session.title}</Text>
                      <Text size="md" c="dimmed">
                        Displaying: {context.window_start_time}–{context.window_end_time}
                      </Text>
                      <Text size="sm" c="gray.3" lineClamp={2}>
                        {getTranscriptOpenedFromText(context)}
                      </Text>
                    </Stack>

                    <Button
                      size="xs"
                      variant="light"
                      onClick={() => copyTranscriptCitation(context)}
                    >
                      Copy citation
                    </Button>
                  </Group>

                  <div className="transcript-box transcript-box-context" ref={contextTranscriptBoxRef}>
                    {decorateTranscriptSegments(
                      context.segments,
                      context.sourceItem,
                    ).map(({ segment, isTargetSegment, showTimestamp, speaker, showSpeaker }) => (
                      <div
                        key={segment.segment_id}
                        className={`transcript-line${isTargetSegment ? ' transcript-line-target' : ''}`}
                      >
                        <Text
                          span
                          size="md"
                          c="gray.4"
                          className={`timestamp${showTimestamp ? '' : ' timestamp-hidden'}`}
                        >
                          {segment.start_time}
                        </Text>

                        {speaker && (
                          <Text
                            span
                            size="md"
                            className={`transcript-speaker-label${showSpeaker ? '' : ' transcript-speaker-label-hidden'}`}
                            aria-hidden={showSpeaker ? undefined : true}
                          >
                            {speaker}:
                          </Text>
                        )}

                        <Text span size="lg" c="gray.1">
                          {highlightQueryTerms(
                            segment.text,
                            context.highlightQuery ?? lastQuery,
                            context.highlightTerms ?? highlightAliasTerms,
                            (context.highlightMode ?? lastSearchMode) === 'exact',
                          )}
                        </Text>
                      </div>
                    ))}
                  </div>
                </Stack>
              </Paper>
            )}
          </Stack>
        </Group>

        <SiteFooter />
      </Stack>
    </Container>
  );
}

function AdminToolsMenu() {
  const [reportsExpanded, setReportsExpanded] = useState(false);
  return (
    <Menu position="bottom-end" width={260} shadow="md" withinPortal
      onClose={() => setReportsExpanded(false)}>
      <Menu.Target>
        <Button size="md" variant="light" color="green" rightSection={<span aria-hidden="true">▾</span>}>
          Admin Tools
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item component="a" href="/admin/uploads">Upload Sessions</Menu.Item>
        <Menu.Item component="a" href="/admin/transcripts">Download Transcripts</Menu.Item>
        <Menu.Divider />
        <Menu.Item
          closeMenuOnClick={false}
          aria-expanded={reportsExpanded}
          onClick={() => setReportsExpanded((v) => !v)}
          rightSection={<span aria-hidden="true">{reportsExpanded ? '▾' : '▸'}</span>}
        >
          Reports
        </Menu.Item>
        <Collapse in={reportsExpanded}>
          <Menu.Item component="a" href="/admin/reports/accounting" pl="xl">
            Accounting
          </Menu.Item>
        </Collapse>
      </Menu.Dropdown>
    </Menu>
  );
}

function App() {
  const normalizedPath = window.location.pathname.replace(/\/+$/, '') || '/';

  if (normalizedPath === '/privacy') {
    return <PrivacyPolicyPage />;
  }

  if (normalizedPath === '/terms') {
    return <TermsOfUsePage />;
  }

  if (normalizedPath === '/admin/uploads') {
    return <AdminUploadSessions />;
  }

  if (normalizedPath === '/admin/transcripts') {
    return <AdminTranscriptDownloads />;
  }

  if (normalizedPath === '/admin/reports/accounting') {
    return <AccountingReport />;
  }

  return <DavealyzerSearchApp />;
}

export default App;
